/* tslint:disable */
/* eslint-disable */

import { DateLike, NegotiationField } from 'utils';

export interface AliasTypeAndValue {
  type: TradingEntityAliasType | null;
  value: string | null;
}

export interface ApplicationAlert {
  correlationId: string;
  source: string;
  timestamp: DateLike;
  type: AlertType;
  typeSpecificFields: { [index: string]: TypeSpecificFieldUnion };
}

export interface ApplicationAlertUpdate extends EntityUpdate {
  entity: ApplicationAlert;
  entityType: 'APPLICATION_ALERT';
}

export interface AuditEntry<T> {
  auditMarket: AuditMarket | null;
  direction: AuditDirection | null;
  encodedId: number | null;
  id: string;
  message: T | null;
  messageHeaderCreatedOn: DateLike | null;
  timeStamp: DateLike | null;
  type: string | null;
  userId: string | null;
}

export interface AuditEntryUpdate extends EntityUpdate {
  entity: AuditEntry<any>;
  entityType: 'AUDIT_ENTRY';
}

export interface AuthorisedUser {
  algoName: string | null;
  allTradingEntities: boolean;
  application: Application;
  canTrade: boolean;
  featureToggle: { [index: string]: boolean } | null;
  firstName: string;
  gatewayId: number;
  groups: Group[];
  id: string;
  lastGroups: Group[];
  lastLoggedOutInstant: DateLike | null;
  lastLogoutReason: LogoutReason | null;
  lastName: string;
  loggedInInstant: DateLike;
  loginCorrelationId: string;
  loginStatus: LoginStatus;
  notes: string | null;
  oktaDetailsLastUpdated: DateLike | null;
  oktaLastLogin: DateLike | null;
  oktaUserStatus: OktaUserStatus;
  organisationId: string;
  permissions: Permission[];
  phoneNumber: string | null;
  productRights: ProductRight[];
  rateLimiterLogoutInstants: DateLike[];
  sessionAttributes: SessionAttribute[] | null;
  sessionId: number;
  shortId: string | null;
  status: UserStatus;
  tradingControlLimit: TradingControlLimitUser;
  tradingEntityDetails: UserTradingEntityDetails[];
  userPreference: UserPreference;
  username: string;
}

export interface AuthorisedUserUpdate extends EntityUpdate {
  entity: AuthorisedUser;
  entityType: 'AUTHORISED_USER';
}

export interface Axe {
  assetType: AssetType | null;
  axeId: string;
  collateralBasketId: string | null;
  collateralId: string | null;
  collateralIssue: Issue | null;
  currency: Currency | null;
  dateSource: DateSource | null;
  direction: Direction | null;
  endDate: DateLike | null;
  evergreenNoticePeriod: EvergreenNoticePeriod | null;
  externalAxeId: string | null;
  externalCancelAxeId: string | null;
  factor: number | null;
  fee: number | null;
  initiatorOrganisationId: string;
  initiatorTradingEntityId: string;
  market: Market | null;
  marketIdentifierCode: MicCode | null;
  marketTradeDate: DateLike | null;
  marketType: MarketType | null;
  notional: number | null;
  productSectorId: string | null;
  rate: number | null;
  referenceRate: string | null;
  repoType: RepoType | null;
  responderOrganisationIds: string[] | null;
  startConsideration: number | null;
  startDate: DateLike | null;
  term: OrderTerm | null;
  tradingType: TradingType | null;
}

export interface AxeEventCancel extends StateMachineAxeEvent {
  axeId: string;
  eventType: 'AxeEventCancel';
  externalAxeCancelId: string | null;
}

export interface AxeEventClone extends StateMachineAxeEvent {
  axeId: string;
  eventType: 'AxeEventClone';
}

export interface AxeEventCreate extends StateMachineAxeEvent {
  collateralBasketId: string | null;
  collateralId: string | null;
  eventType: 'AxeEventCreate';
  externalAxeId: string | null;
  initiatorTradingEntityId: string;
  updatedValues: { [P in NegotiationFieldMeta]?: any };
}

export interface AxeEventCreateFromClone extends StateMachineAxeEvent {
  eventType: 'AxeEventCreateFromClone';
}

export interface AxeEventSend extends StateMachineAxeEvent {
  axeId: string;
  eventType: 'AxeEventSend';
}

export interface AxeEventStage extends StateMachineAxeEvent {
  axeId: string;
  collateralId: string | null;
  eventType: 'AxeEventStage';
  fundId: string;
}

export interface AxeEventUpdateAttributes extends StateMachineAxeEvent {
  axeId: string;
  eventType: 'AxeEventUpdateAttributes';
  updatedValues: { [P in NegotiationFieldMeta]?: any };
}

export interface AxeUpdate extends EntityUpdate {
  availableEvents: AxeEvent[];
  axePreviousStatus: AxeStatus | null;
  axeStatus: AxeStatus;
  createdAt: DateLike;
  createdBy: string;
  creatingCorrelationId: string;
  entity: Axe;
  entityType: 'AXE';
  updatedAt: DateLike;
  updatedBy: string;
  validationIssues: ValidationIssue[];
  version: number;
}

export interface BasketAllocation {
  amountAllocated: number | null;
  basketId: string;
  collateralAllocations: CollateralAllocation[];
  endTime: DateLike | null;
  endTimeWarning: DateLike | null;
  haircutType: HaircutType | null;
  remainingAllocationRequired: number | null;
  /**
   * @deprecated
   */
  tradingType: TradingType | null;
  trimIndex: number | null;
  userIdWithLock: string | null;
  validationIssues: ValidationIssue[] | null;
  withinWarningPeriod: boolean | null;
}

export interface BatchData {
  batchEntities: BatchEntities | null;
  batchEvent: BatchEvent | null;
  shouldRemoveFromClient: boolean | null;
}

export interface BatchDataUpdate extends EntityUpdate {
  entity: BatchData;
  entityType: 'BATCH_DATA';
}

export interface BatchEntities {
  entitiesInBatch: { [index: string]: string[] } | null;
  transactionId: string | null;
}

export interface BestPrice {
  bestPriceAssociationType: BestPriceAssociationType;
  bestPriceEntityId: string;
  bestPriceId: string;
  excludedNegotiationIds: string[] | null;
  linkedBestPriceEntityId: string | null;
  strategies: { [P in BestPriceStrategy]?: BestPriceStrategyDetails };
}

export interface BestPriceStrategyDetails {
  rankedItems: BestPriceStrategyItem[];
}

export interface BestPriceStrategyItem {
  entityId: string;
  value: number;
}

export interface BestPriceUpdate extends EntityUpdate {
  entity: BestPrice;
  entityType: 'BEST_PRICE';
}

export interface BookPreparedDealEvent extends StateMachineDealEvent, DealIdProvider {
  dealId: string;
  eventType: 'BookPreparedDealEvent';
}

export interface BooleanValue extends TypeSpecificField {
  type: 'boolean';
  value: boolean | null;
}

export interface ClearingDetails {
  actualClearingType: ActualClearingType | null;
  clearingHouseId: string | null;
  clearingType: TradingEntityClearingType | null;
  contingencyReverseMessage: string | null;
  counterpartyClearingType: TradingEntityClearingType | null;
  counterpartySponsorId: string | null;
  dealVersion: number | null;
  reverseMessage: string | null;
  sponsorId: string | null;
}

export interface ClearingHouse {
  bic: string;
  id: string;
  integrationReady: boolean;
  lei: string;
  marketIdentifierCode: string;
  permittedDirectBasketIds: string[];
  permittedDirectCollateralIds: string[];
  permittedSponsoredBasketIds: string[];
  permittedSponsoredCollateralIds: string[];
  publishedName: string;
  status: EntityStatus;
}

export interface ClearingHouseInteraction {
  clearingInstance: ClearingInstance | null;
  clearingInteractionState: ClearingInteractionState | null;
  contingencyMessage: string | null;
  contingencyReverseMessage: string | null;
  createdInstant: DateLike;
  dealId: string;
  dealVersion: number;
  error: string | null;
  errorInstant: DateLike | null;
  errorResolutionEvent: DealEvent | null;
  errorResolutionInstant: DateLike | null;
  id: string;
  message: string | null;
  messageSentInstant: DateLike | null;
  messageType: ClearingMessageType;
  pendingResponse: string | null;
  pendingResponseReceivedInstant: DateLike | null;
  response: string | null;
  responseReceivedInstant: DateLike | null;
  reverseMessage: string | null;
  tradeType: ClearingTradeType;
}

export interface ClearingHouseInteractionUpdate extends EntityUpdate {
  availableEvents: CrudEventEnum[];
  entity: ClearingHouseInteraction;
  entityType: 'CLEARING_HOUSE_INTERACTION';
}

export interface ClearingHouseUpdate extends EntityUpdate {
  availableEvents: CrudEventEnum[];
  entity: ClearingHouse;
  entityType: 'CLEARING_HOUSE';
}

export interface ClearingHouseUpsertEvent extends CrudUpsertEvent {
  entity: ClearingHouse;
  eventType: 'ClearingHouseUpsertEvent';
}

export interface ClientLogConfig {
  config: { [P in ClientLogStream]?: LogSeverity };
}

export interface ClientMessage {
  correlationId: string;
  payload: any;
  type: 'PLATFORM_CHANGE_MESSAGE' | 'ADMIN_LOGS_OUT_USER_MESSAGE' | 'LOGIN_MESSAGE' | 'LOGOUT_MESSAGE' | 'EVENT_MESSAGE' | 'SUBSCRIBE_MESSAGE' | 'DATABASE_REPORT_MESSAGE' | 'SUBSCRIBE_BATCH_MESSAGE' | 'UNSUBSCRIBE_MESSAGE' | 'LOG_MESSAGE' | 'DEV_SIGNAL' | 'PERMISSIONS_REPORT' | 'ALERT' | 'UPDATE_COLLATERALS' | 'UPDATE_ISSUER_ORGANISATION' | 'CRUD_EVENT_MESSAGE' | 'TOGGLE_PLATFORM_FEATURE_MESSAGE' | 'COLLATERAL_UPLOAD' | 'CLIENT_UPDATE_LOG_LEVELS_MESSAGE' | 'CLUSTER_COMMAND';
}

export interface ClientMessageAdminLogsOutUser extends ClientMessage {
  payload: ClientMessageAdminLogsOutUserPayload;
  type: 'ADMIN_LOGS_OUT_USER_MESSAGE';
}

export interface ClientMessageAdminLogsOutUserPayload {
  correlationId: string;
  usernameBeingLoggedOut: string;
}

export interface ClientMessageAlert extends ClientMessage {
  payload: ApplicationAlert;
  type: 'ALERT';
}

export interface ClientMessageClusterRoutedCommand extends ClientMessage {
  payload: ClusterRoutedCommand;
  type: 'CLUSTER_COMMAND';
}

export interface ClientMessageCollateralUpload extends ClientMessage {
  payload: ClientMessageCollateralUploadPayload;
  type: 'COLLATERAL_UPLOAD';
}

export interface ClientMessageCollateralUploadPayload {
  collateralUpload: CollateralUpload;
  correlationId: string;
  filename: string | null;
}

export interface ClientMessageCrudEvent extends ClientMessage {
  payload: CrudEventUnion;
  type: 'CRUD_EVENT_MESSAGE';
}

export interface ClientMessageDatabaseReportRequest extends ClientMessage {
  payload: DatabaseReportRequest;
  type: 'DATABASE_REPORT_MESSAGE';
}

export interface ClientMessageDevSignal extends ClientMessage {
  payload: DevSignal;
  type: 'DEV_SIGNAL';
}

export interface ClientMessageEvent extends ClientMessage {
  payload: ClientMessageEventPayloadUnion;
  type: 'EVENT_MESSAGE';
}

export interface ClientMessageEventPayload {
  correlationId: string;
  eventType: 'DealServicingRespondEventBulk' | 'DealServicingStartEventBulk' | 'DealServicingStartCloseOpenEventBulk' | 'DealServicingStartRerateEventBulk' | 'DealServicingStartRollInPlaceEventBulk' | 'DealServicingStartAdminCloseOpenEventBulk' | 'DealServicingStartAdminRerateEventBulk' | 'DealServicingStartAdminSubstitutionEventBulk' | 'DealServicingStartTerminateEventBulk' | 'DealServicingStartAdminTerminateEventBulk' | 'NegotiationEventAcceptBulk' | 'NegotiationEventCancelBulk' | 'NegotiationEventUpdateAttributesBulk' | 'OrderEventCloneBulk' | 'OrderEventCreateBulk' | 'OrderEventCreateSingle' | 'OrderEventPrepareBulk' | 'OrderEventSubmitBulk' | 'ReferenceDataManagementEventCancelBulk' | 'ReferenceDataManagementEventSubmitBulk' | 'CompositeEvent' | 'AxeEventCancel' | 'AxeEventClone' | 'AxeEventCreate' | 'AxeEventCreateFromClone' | 'AxeEventStage' | 'AxeEventSend' | 'AxeEventUpdateAttributes' | 'BookPreparedDealEvent' | 'DealAllocateEvent' | 'DealAllocateLateWarningEvent' | 'DealAmendEvent' | 'DealAssignFundEvent' | 'DealCancelAllocationsEvent' | 'DealCancelAssignmentEvent' | 'DealCancelEvent' | 'DealClearingCancelResponseReceivedEvent' | 'DealClearingCancelSentEvent' | 'DealClearingCreateResponseReceivedEvent' | 'DealClearingCreateSentEvent' | 'DealClearingFailedEvent' | 'DealClearingFailedManuallyAcceptedEvent' | 'DealClearingFailedManuallyRejectedEvent' | 'DealClearingFailedRetryEvent' | 'DealCollateralAssignmentStartSaveAllocateEvent' | 'DealCompleteEvent' | 'DealExpireAllocationEvent' | 'DealExtendAllocationTimeEvent' | 'DealFundAssignmentStartSaveAssignmentEvent' | 'DealRecalculateEvent' | 'DealRefreshEvent' | 'DealSaveAllocationsEvent' | 'DealSaveFundAssignmentEvent' | 'DealServicingAcceptEvent' | 'DealServicingCancelEvent' | 'DealServicingRejectEvent' | 'DealServicingResponderLockEvent' | 'DealServicingResponderUnlockEvent' | 'DealServicingRevertAttributesEvent' | 'DealServicingSendEvent' | 'DealServicingUpdateAttributesEvent' | 'DealServicingUpdateRegulatoryAttributesEvent' | 'DealServicingWithdrawEvent' | 'DealServicingWorkflowCompleteEvent' | 'DealServicingWorkflowCancelEvent' | 'DealSettledEvent' | 'DealStartAllocationsEvent' | 'DealStartFundAssignmentEvent' | 'DealTradeEvent' | 'DealUpdateAllocationCollateralsEvent' | 'DealUpdateAvailableEventsEvent' | 'DealUpdateFundAssignmentReminderEvent' | 'PrepareDealEvent' | 'DealServicingStartAmendEvent' | 'DealServicingStartCloseOpenEvent' | 'DealServicingStartRerateEvent' | 'DealServicingStartReturnRecallEvent' | 'DealServicingStartRollInPlaceEvent' | 'DealServicingStartSubstituteEvent' | 'DealServicingStartTerminateEvent' | 'DealServicingStartAdminCancelEvent' | 'DealServicingStartAdminCloseOpenEvent' | 'DealServicingStartAdminRerateEvent' | 'DealServicingStartAdminTerminateEvent' | 'DealServicingStartAdminSubstitutionEvent' | 'DealServicingStartTraderAmendEvent' | 'DealServicingStartTraderCancelEvent' | 'DealServicingStartRollEvent' | 'CollectionEventAssignOrders' | 'CollectionEventCreate' | 'CollectionEventDelete' | 'CollectionEventModifyOrders' | 'CollectionEventModifyType' | 'CollectionEventRefresh' | 'CollectionEventSubmit' | 'NegotiationEventAccept' | 'NegotiationEventBackToStaging' | 'NegotiationEventCancel' | 'NegotiationEventCounterOffer' | 'NegotiationEventCounterOfferWithAttributes' | 'NegotiationEventExpire' | 'NegotiationEventInitiatorRevertAttributes' | 'NegotiationEventLock' | 'NegotiationEventLockQuote' | 'NegotiationEventMiss' | 'NegotiationEventOrderCancelled' | 'NegotiationEventQuote' | 'NegotiationEventQuoteWithAttributes' | 'NegotiationEventRefresh' | 'NegotiationEventReject' | 'NegotiationEventRequest' | 'NegotiationEventResponderRevertAttributes' | 'NegotiationEventTagAndInvoke' | 'NegotiationEventTagAndInvokeMany' | 'NegotiationEventUnlock' | 'NegotiationEventUpdateBestPrice' | 'NegotiationEventUpdateAttributes' | 'NegotiationEventUpdatePreAssignmentAttributes' | 'NegotiationEventUpdateRegulatoryAttributes' | 'NegotiationEventUpdateSummary' | 'NegotiationEventUpdateContainerSummary' | 'NegotiationEventUpdateTotalFill' | 'NegotiationEventWithdraw' | 'NegotiationEventValidate' | 'OrderEventAddPreAssignment' | 'OrderEventCancel' | 'OrderEventClone' | 'OrderEventCreate' | 'OrderEventDelete' | 'OrderEventDeletePreAssignment' | 'OrderEventDone' | 'OrderEventEnabledCollateral' | 'OrderEventExpired' | 'OrderEventModify' | 'OrderEventModifyCollection' | 'OrderEventNegotiationAccepted' | 'OrderEventNegotiationCancelled' | 'OrderEventNegotiationRejected' | 'OrderEventPrepare' | 'OrderEventPrepareCollection' | 'OrderEventRefresh' | 'OrderEventSubmit' | 'OrderEventUpdateAttributes' | 'OrderEventUpdatePreAssignmentAttributes' | 'OrderEventUpdateValidateIssues' | 'ReferenceDataManagementEventCancel' | 'ReferenceDataManagementEventCreateAndSubmit' | 'ReferenceDataManagementEventCreateEdit' | 'ReferenceDataManagementEventCreateClone' | 'ReferenceDataManagementEventCreateNew' | 'ReferenceDataManagementEventEntityAdd' | 'ReferenceDataManagementEventEntityDelete' | 'ReferenceDataManagementEventEntityRevert' | 'ReferenceDataManagementEventEntityUpdate' | 'ReferenceDataManagementEventSubmit' | 'ReferenceDataManagementEventDeleteAllOfType' | 'ProductSectorMappingProductStateEventGenerateReport' | 'ProductSectorMappingProductStateEventRollback' | 'ProductSectorMappingProductStateEventRuleChanged' | 'ProductSectorMappingProductStateEventSubmit';
}

export interface ClientMessageLog extends ClientMessage {
  payload: ClientMessageLogPayload;
  type: 'LOG_MESSAGE';
}

export interface ClientMessageLogDebugPayload {
  actionLog: string | null;
  selections: string | null;
  stack: string | null;
}

export interface ClientMessageLogPayload {
  correlationId: string;
  debugInfo: ClientMessageLogDebugPayload | null;
  logSeverity: LogSeverity;
  logStream: ClientLogStream | null;
  message: string;
}

export interface ClientMessageLogin extends ClientMessage {
  payload: ClientMessageLoginPayload;
  type: 'LOGIN_MESSAGE';
}

export interface ClientMessageLoginPayload {
  correlationId: string;
  token: string;
}

export interface ClientMessageLogout extends ClientMessage {
  payload: ClientMessageLogoutPayload;
  type: 'LOGOUT_MESSAGE';
}

export interface ClientMessageLogoutPayload {
  correlationId: string;
}

export interface ClientMessagePermissionsReportRequest extends ClientMessage {
  payload: PermissionReportRequest;
  type: 'PERMISSIONS_REPORT';
}

export interface ClientMessagePlatformChange extends ClientMessage {
  payload: ClientMessagePlatformChangePayload;
  type: 'PLATFORM_CHANGE_MESSAGE';
}

export interface ClientMessagePlatformChangePayload {
  action: ClientPlatformChangeActionEnum;
  correlationId: string;
}

export interface ClientMessageSubscribe extends ClientMessage {
  payload: SubscribeMessage;
  type: 'SUBSCRIBE_MESSAGE';
}

export interface ClientMessageSubscribeBatch extends ClientMessage {
  payload: SubscribeBatchMessage;
  type: 'SUBSCRIBE_BATCH_MESSAGE';
}

export interface ClientMessageTogglePlatformFeature extends ClientMessage {
  payload: ClientMessageTogglePlatformFeaturePayload;
  type: 'TOGGLE_PLATFORM_FEATURE_MESSAGE';
}

export interface ClientMessageTogglePlatformFeaturePayload {
  correlationId: string;
  enabled: boolean;
  platformFeature: PlatformFeature;
}

export interface ClientMessageUnsubscribe extends ClientMessage {
  payload: UnsubscribeMessage;
  type: 'UNSUBSCRIBE_MESSAGE';
}

export interface ClientMessageUpdateCollaterals extends ClientMessage {
  payload: UpdateCollateralsClientRequest;
  type: 'UPDATE_COLLATERALS';
}

export interface ClientMessageUpdateIssuerOrganisation extends ClientMessage {
  payload: UpdateIssuerOrganisationClientRequest;
  type: 'UPDATE_ISSUER_ORGANISATION';
}

export interface ClientMessageUpdateLogLevels extends ClientMessage {
  payload: ClientMessageUpdateLogLevelsPayload;
  type: 'CLIENT_UPDATE_LOG_LEVELS_MESSAGE';
}

export interface ClientMessageUpdateLogLevelsPayload {
  clientLogConfig: ClientLogConfig;
  correlationId: string;
}

export interface CloseOpen extends ServicingTypeDetail {
  recalculateBondPrice: boolean | null;
  type: 'CLOSE_OPEN';
}

export interface ClusterClientState {
  id: string;
  /**
   * @deprecated
   */
  lastDailyScheduledTaskRunDate: { [index: string]: DateLike };
  lastDailyScheduledTaskRunInstant: { [index: string]: DateLike };
}

export interface ClusterClientStateUpdate extends EntityUpdate {
  availableEvents: CrudEventEnum[];
  entity: ClusterClientState;
  entityType: 'CLUSTER_CLIENT_STATE';
}

export interface ClusterPong {
  correlationId: string | null;
  timestamp: DateLike | null;
}

export interface ClusterPongUpdate extends EntityUpdate {
  entity: ClusterPong;
  entityType: 'CLUSTER_PONG';
}

export interface ClusterPublishFilter {
}

export interface ClusterRoutedCommand {
}

export interface ClusterSession {
  created: DateLike;
  id: number;
}

export interface ClusterSessionUpdate extends EntityUpdate {
  entity: ClusterSession;
  entityType: 'CLUSTER_SESSION';
}

export interface Collateral extends Security {
  assetStatus: string | null;
  assetStatusEffectiveDate: DateLike | null;
  assetSubType: string | null;
  assetType: AssetType | null;
  basisConvention: BasisConvention;
  cfi: string;
  collateralIdentifierType: CollateralIdentifierType;
  collateralIndexDetails: CollateralIndexDetails | null;
  collateralType: CollateralType;
  complianceOverride: boolean | null;
  countryCode: string;
  countryOfIncorporation: string | null;
  countryOfIssuance: string | null;
  countryOfRiskAll: string | null;
  countryOfRiskPrimary: string | null;
  countryOfRiskUltimateParentAll: string | null;
  countryOfRiskUltimateParentPrimary: string | null;
  coupon: number;
  couponAccruedInterestDate: DateLike | null;
  couponFirstDate: DateLike | null;
  couponFrequency: CouponFrequency | null;
  couponMaturityDate: DateLike | null;
  couponType: RateType | null;
  currency: Currency;
  cusip: string | null;
  debtType: string | null;
  dirtyPrice: number;
  domicile: string | null;
  eeaVenueEligible: boolean | null;
  euCompliant: boolean;
  factor: number | null;
  factorDate: DateLike | null;
  firstTradeTimestamp: DateLike | null;
  fisin: string;
  governmentBondType: string | null;
  id: string;
  inEsmaFirds: boolean | null;
  indexSpread: number | null;
  isin: string | null;
  issue: Issue | null;
  issueDate: DateLike;
  issuer: string;
  issuerLei: string | null;
  issuerOrgId: string | null;
  lastUpdatedAtByUser: DateLike | null;
  lastUpdatedAtByUserOrSystem: DateLike | null;
  lastUpdatedByUser: string | null;
  lookupFailedReason: string | null;
  mbsPriceDate: DateLike | null;
  micReportDate: DateLike | null;
  minimumTicketSize: number;
  mockStrategy: string | null;
  name: string;
  originalSource: OriginalSource | null;
  productSectorId: string;
  prospectus: boolean | null;
  refinitivAssetType: string | null;
  refinitivBaseIndex: string | null;
  ric: string | null;
  rule144aIndicator: boolean | null;
  seniority: Seniority | null;
  status: CollateralStatus;
  ticketIncrement: number;
  totalIssuedNominalAmount: number | null;
  tradingType: TradingType;
  ukVenueEligible: boolean | null;
}

export interface CollateralAllocation {
  accruedInterest: number | null;
  allInPrice: number | null;
  assetType: AssetType | null;
  bondPrice: number | null;
  calculatedStartConsideration: number | null;
  canTrade: boolean;
  cleanPrice: number | null;
  collAsgnId: string | null;
  collateralId: string;
  dirtyPrice: number | null;
  endConsideration: number | null;
  factor: number | null;
  haircut: number | null;
  haircutType: HaircutType | null;
  indexRatio: number | null;
  notional: number | null;
  selected: boolean;
  startConsideration: number | null;
  startConsiderationOverride: boolean | null;
  validationIssues: ValidationIssue[] | null;
}

export interface CollateralBasket extends Security {
  basketFromDate: DateLike;
  basketToDate: DateLike;
  calculateAccruedInterest: boolean | null;
  cmeBasketReference: string | null;
  collateralBasketCusip: string | null;
  collateralBasketIsin: string | null;
  collateralBasketOwner: string | null;
  collateralIds: string[];
  dbvClass: string | null;
  description: string;
  id: string;
  isDynamicBasket: boolean | null;
  lastUpdatedAt: DateLike;
  lastUpdatedBy: string;
  maxConcentration: number | null;
  minAmount: number | null;
  minConcentration: number | null;
  name: string;
  productSectorId: string;
  status: CollateralBasketStatus;
  substitutionAllowed: boolean | null;
  substitutionsNum: number | null;
  tradingEntityIds: string[];
  tradingType: TradingType;
}

export interface CollateralBasketUpdate extends EntityUpdate {
  availableEvents: CrudEventEnum[];
  entity: CollateralBasket;
  entityType: 'COLLATERAL_BASKET';
}

export interface CollateralBasketUpsertEvent extends CrudUpsertEvent {
  entity: CollateralBasket;
  eventType: 'CollateralBasketUpsertEvent';
}

export interface CollateralDetails {
  bondType: string | null;
  callableFlag: string | null;
  couponRateEffectiveDate: DateLike | null;
  ecbIndicator: string | null;
  exDividendCalendar: string | null;
  exDividendRule: string | null;
  exDividendValue: string | null;
  factor: string | null;
  factorDate: DateLike | null;
  fitchRating: string | null;
  floatIndexType: string | null;
  floatMultiplier: number | null;
  floatOffset: number | null;
  floaterFlag: string | null;
  floatingRateFixingDate: DateLike | null;
  id: string;
  industrySectorCode: string | null;
  industrySubSectorCode: string | null;
  isSecuredFlag: string | null;
  issuerOrgId: string | null;
  lookBack: number | null;
  mifidIndexIdentifier: string | null;
  mifidIndexName: string | null;
  mifidIndexTerm: string | null;
  minimumDenomination: number | null;
  moodysRating: string | null;
  multiStepFloatFlag: string | null;
  offeringType: number | null;
  perpetualSecurityFlag: string | null;
  privatePlacementFlag: string | null;
  refinitivClassificationScheme: string | null;
  regulationIndicator: string | null;
  sandPRating: string | null;
  series: string | null;
  settlementDateConvention: string | null;
  sinkableFlag: string | null;
  totalAmountOutstanding: number | null;
}

export interface CollateralDetailsUpdate extends EntityUpdate {
  availableEvents: CrudEventEnum[];
  entity: CollateralDetails;
  entityType: 'COLLATERAL_DETAILS';
}

export interface CollateralIndexDetails {
  baseIndexValue: number | null;
  indexId: string | null;
  indexLag: IndexLag | null;
}

export interface CollateralPrice {
  cleanPrice: number;
  collateralId: string;
  lastCleanPriceUpdate: DateLike | null;
}

export interface CollateralPriceUpdate extends EntityUpdate {
  availableEvents: CrudEventEnum[];
  entity: CollateralPrice;
  entityType: 'COLLATERAL_PRICE';
}

export interface CollateralUpdate extends EntityUpdate {
  availableEvents: CrudEventEnum[];
  entity: Collateral;
  entityType: 'COLLATERAL';
}

export interface CollateralUpload {
  'Accrual Date': string | null;
  'Base Index Value': string | null;
  'Basis Convention': string | null;
  CFI: string | null;
  CUSIP: string | null;
  'Clean Price': string | null;
  'Collateral Type': string | null;
  Country: string | null;
  Coupon: string | null;
  'Coupon Type': string | null;
  Currency: string | null;
  FISN: string | null;
  'First Coupon Date': string | null;
  'First Settlement Date': string | null;
  Frequency: string | null;
  ISIN: string | null;
  Index: string | null;
  Issuer: string | null;
  Lag: string | null;
  'Load Results': string | null;
  'Maturity Date': string | null;
  Platform: string | null;
  Sector: string | null;
  'Short ID': string | null;
  'Ticket Size': string | null;
  Trading: string | null;
  'Trading Type': string | null;
}

export interface CollateralUploadUpdate extends EntityUpdate {
  availableEvents: CrudEventEnum[];
  entity: CollateralUpload;
  entityType: 'COLLATERAL_UPLOAD';
}

export interface CollateralUpsertEvent extends CrudUpsertEvent {
  entity: Collateral;
  eventType: 'CollateralUpsertEvent';
}

export interface CollateralView {
  CUSIP: string | null;
  Collateral: string | null;
  'Collateral Accrued Interest': number | null;
  'Collateral Id': string | null;
  'Collateral Price': number | null;
  Coupon: number | null;
  Currency: Currency | null;
  ISIN: string | null;
  Issuer: string | null;
  'Issuer Org ID': string | null;
}

export interface Collection {
  counterpartyIds: string[];
  createdAt: DateLike;
  createdBy: string;
  creatingCorrelationId: string;
  id: string;
  initiatorFundIds: string[];
  initiatorOrganisationId: string;
  isContainer: boolean;
  isCurve: boolean | null;
  isNested: boolean;
  lastUpdatedAt: DateLike | null;
  lastUpdatedBy: string | null;
  name: string;
  status: CollectionStatus;
  type: CollectionType | null;
  version: number;
}

export interface CollectionEventAssignOrders extends StateMachineCollectionEvent {
  collectionId: string | null;
  collectionType: CollectionType | null;
  eventType: 'CollectionEventAssignOrders';
  newCollectionName: string | null;
  orderIds: string[];
}

export interface CollectionEventCreate extends StateMachineCollectionEvent {
  collectionId: string | null;
  collectionName: string;
  collectionType: CollectionType | null;
  eventType: 'CollectionEventCreate';
  nested: boolean | null;
  orderIds: string[];
  previousCollectionId: string | null;
}

export interface CollectionEventDelete extends StateMachineCollectionEvent {
  collectionId: string;
  eventType: 'CollectionEventDelete';
}

export interface CollectionEventModifyOrders extends StateMachineCollectionEvent {
  collectionId: string;
  eventType: 'CollectionEventModifyOrders';
  updatedValues: { [P in NegotiationFieldMeta]?: any };
}

export interface CollectionEventModifyType extends StateMachineCollectionEvent {
  collectionId: string;
  collectionType: CollectionType;
  eventType: 'CollectionEventModifyType';
}

export interface CollectionEventRefresh extends StateMachineCollectionEvent {
  collectionId: string;
  eventType: 'CollectionEventRefresh';
}

export interface CollectionEventSubmit extends StateMachineCollectionEvent {
  collectionId: string;
  eventType: 'CollectionEventSubmit';
  initiatorFundIds: string[];
}

export interface CollectionItem {
  collectionId: string | null;
  forceIsBulk: boolean | null;
}

export interface CollectionUpdate extends EntityUpdate {
  availableEvents: CollectionEvent[];
  entity: Collection;
  entityType: 'COLLECTION';
}

export interface CollectionWithoutStatus {
  counterpartyIds: string[];
  id: string;
  initiatorFundIds: string[];
  initiatorOrganisationId: string;
  isContainer: boolean;
  isCurve: boolean | null;
  isNested: boolean;
  name: string;
  type: CollectionType | null;
}

export interface ColumnMapping {
  heading: string | null;
}

export interface ColumnMappingDecimal extends ColumnMapping {
  decimalShift: number | null;
}

export interface ColumnMappingEnum<T> extends ColumnMapping {
  defaultValue: T | null;
  values: { [index: string]: string };
}

export interface ColumnMappingLocalDate extends ColumnMapping {
  dateFormat: DateFormat | null;
}

export interface ColumnMappingString extends ColumnMapping {
}

export interface ComplianceCollateral {
  cusip: string | null;
  id: string;
  isin: string | null;
  onRecognisedExchange: boolean | null;
  ultimateParentSanctioned: boolean | null;
}

export interface ComplianceCollateralUpdate extends EntityUpdate {
  availableEvents: CrudEventEnum[];
  entity: ComplianceCollateral;
  entityType: 'COMPLIANCE_COLLATERAL';
}

export interface CompositeEvent extends StateMachineEvent, DealIdsProvider {
  childEvents: StateMachineEventUnion[];
  eventType: 'CompositeEvent';
}

export interface Counter {
  id: string;
  type: CounterType;
  value: number;
}

export interface CounterUpdate extends EntityUpdate {
  entity: Counter;
  entityType: 'COUNTER';
}

export interface CounterpartyGroup {
  counterpartyIds: string[];
  description: string;
  id: string;
}

export interface CounterpartyInstruction {
  axeId: string | null;
  counterpartyAlias: string | null;
  counterpartyId: string;
  internalAxeId: string | null;
  isAxed: boolean;
  masterLegalAgreementType: MasterLegalAgreementType | null;
  primeBrokerId: string | null;
  repoType: RepoType | null;
  tradeConditionFlag: TradeConditionFlag;
}

export interface Country {
  countryName: string;
  isoNumericCode: string;
  isoThreeDigitAlphaCode: string;
  isoTwoDigitAlphaCode: string;
  lastUpdatedAtByUser: DateLike | null;
  lastUpdatedByUser: string | null;
  sanctioned: boolean;
}

export interface CountryUpdate extends EntityUpdate {
  availableEvents: CrudEventEnum[];
  entity: Country;
  entityType: 'COUNTRY';
}

export interface CrudDeleteEvent extends CrudEvent {
  entityId: string;
  entityType: EntityTypeEnum;
  eventType: 'CrudDeleteEvent';
}

export interface CrudEvent {
  correlationId: string;
  eventType: 'CrudDeleteEvent' | 'ClearingHouseUpsertEvent' | 'CollateralUpsertEvent' | 'CollateralBasketUpsertEvent' | 'FatFingerProductSectorLimitUpsertEvent' | 'HaircutEntryUpsertEvent' | 'HolidayUpsertEvent' | 'IndexUpsertEvent' | 'OrganisationUpsertEvent' | 'PlatformUpsertEvent' | 'ProductUpsertEvent' | 'ProductSectorUpsertEvent' | 'ReferenceRateUpsertEvent' | 'SpreadsheetMappingUpsertEvent' | 'TradingControlLimitUpsertEvent' | 'TradingEntityClearingUpsertEvent' | 'TradingEntityUpsertEvent' | 'TradingEntityMetaUpsertEvent' | 'TradingRelationshipUpsertEvent' | 'UserUpsertEvent' | 'UserPreferenceUpsertEvent' | 'UserSettingsUpsertEvent';
}

export interface CrudUpsertEvent extends CrudEvent {
  entity: any | null;
  eventType: 'ClearingHouseUpsertEvent' | 'CollateralUpsertEvent' | 'CollateralBasketUpsertEvent' | 'FatFingerProductSectorLimitUpsertEvent' | 'HaircutEntryUpsertEvent' | 'HolidayUpsertEvent' | 'IndexUpsertEvent' | 'OrganisationUpsertEvent' | 'PlatformUpsertEvent' | 'ProductUpsertEvent' | 'ProductSectorUpsertEvent' | 'ReferenceRateUpsertEvent' | 'SpreadsheetMappingUpsertEvent' | 'TradingControlLimitUpsertEvent' | 'TradingEntityClearingUpsertEvent' | 'TradingEntityUpsertEvent' | 'TradingEntityMetaUpsertEvent' | 'TradingRelationshipUpsertEvent' | 'UserUpsertEvent' | 'UserPreferenceUpsertEvent' | 'UserSettingsUpsertEvent';
}

export interface DatabaseReportGatewayRequest {
  clusterClientSessionId: number | null;
  databaseReportRequest: DatabaseReportRequest | null;
  requestingUsername: string | null;
  sessionId: number | null;
}

export interface DatabaseReportGatewayRequestUpdate extends EntityUpdate {
  entity: DatabaseReportGatewayRequest;
  entityType: 'DATABASE_REPORT_REQUEST';
}

export interface DatabaseReportRequest {
  correlationId: string;
  filters: SubscriptionFilter[];
  reportType: ReportType;
}

export interface Deal {
  acceptedActor: AcceptedActor | null;
  accruedInterest: number | null;
  accruedInterestAmount: number | null;
  actualClearingDetails: ClearingDetails | null;
  allCommittedServicingTypes: ServicingType[];
  allInPrice: number | null;
  amended: boolean;
  assetType: AssetType | null;
  axeId: string | null;
  basisConvention: BasisConvention | null;
  basketAllocation: BasketAllocation | null;
  basketDealId: string | null;
  bic: string | null;
  blockDealId: string | null;
  bondPrice: number;
  branchBic: string | null;
  bundle: string | null;
  calculatedStartConsideration: number | null;
  callablePeriodDays: number | null;
  callableType: CallableType;
  cleanPrice: number;
  clearingHouseId: string | null;
  clearingType: TradingEntityClearingType | null;
  collateralId: string | null;
  collateralIssue: Issue | null;
  collectionId: string | null;
  committedServicingType: ServicingType | null;
  competitionCount: number | null;
  containerCollectionId: string | null;
  counterpartyAlias: string | null;
  counterpartyClearingType: TradingEntityClearingType | null;
  counterpartyId: string;
  counterpartyPrimaryCoverUserId: string | null;
  counterpartySecondaryCoverUserId: string | null;
  counterpartySponsorId: string | null;
  counterpartyTraderId: string;
  counterpartyTraderInitials: string | null;
  createdAt: DateLike;
  createdBy: string;
  createdDueToServicingType: ServicingType | null;
  createdFromCollectionType: CollectionType | null;
  creatingCorrelationId: string;
  currency: Currency;
  dealId: string;
  direction: Direction;
  dirtyPrice: number;
  endConsideration: number;
  endDate: DateLike | null;
  evergreenNoticePeriod: EvergreenNoticePeriod | null;
  externalNegotiationId: string | null;
  externalOrderId: string | null;
  externalSummaryNegotiationId: string | null;
  factor: number | null;
  fee: number | null;
  floatCrystallisationDays: number | null;
  fundAssignment: FundAssignment | null;
  fundId: string;
  fundPrimaryCoverUserId: string | null;
  fundSecondaryCoverUserId: string | null;
  haircut: number;
  haircutType: HaircutType | null;
  hideCompetition: boolean | null;
  indexRatio: number | null;
  initiatorBook: string | null;
  initiatorExecutionDecisionMaker: string;
  initiatorInvestmentDecisionMaker: string;
  initiatorOrganisationId: string | null;
  initiatorPrimeBrokerId: string | null;
  initiatorPrimeBrokerName: string | null;
  initiatorStrategy: string | null;
  initiatorUtiRtn: string;
  internalAxeId: string | null;
  isAxed: boolean;
  isInitialOrderUpdated: boolean | null;
  isSponsorCounterparty: boolean | null;
  lastEvent: DealEvent | null;
  lastUpdatedAt: DateLike;
  lastUpdatedAtSystem: DateLike;
  lastUpdatedBy: string;
  market: Market;
  marketIdentifierCode: MicCode;
  marketTradeDate: DateLike;
  marketType: MarketType;
  masterLegalAgreementType: MasterLegalAgreementType | null;
  negotiationId: string;
  negotiationStarted: DateLike;
  notesId: string | null;
  noticePeriodDays: number | null;
  notional: number;
  orderId: string;
  orderSubmitUserId: string | null;
  originalDealId: string;
  parentDealBasketId: string | null;
  positionId: string | null;
  preAgreed: boolean;
  preAssignedIndex: number | null;
  previousDealId: string | null;
  productId: string | null;
  productSectorId: string | null;
  rate: number | null;
  referenceRate: string | null;
  rejectedOrWithdrawnServicingType: ServicingType | null;
  replacedWithDealId: string | null;
  repoClassification: RepoClassification;
  repoInterest: number | null;
  repoInterestStartDate: DateLike | null;
  repoInterestToDate: number | null;
  repoType: RepoType;
  responderBook: string | null;
  responderExecutionDecisionMaker: string;
  responderInvestmentDecisionMaker: string;
  responderStrategy: string | null;
  responderUtiRtn: string;
  rightOfExit: number;
  rightOfSubstitution: number;
  rolled: boolean;
  secondaryBasketId: string | null;
  secondaryCollateralId: string | null;
  servicingAcceptedActor: AcceptedActor | null;
  servicingDetails: ServicingDetails | null;
  servicingMarketTradeDate: DateLike | null;
  settlement: DealSettlement | null;
  settlementVenue: SettlementVenue;
  sponsorId: string | null;
  startConsideration: number;
  startConsiderationOverride: boolean;
  startDate: DateLike;
  status: DealStatus;
  stockBorrowingLendingType: StockBorrowingLendingType | null;
  summaryNegotiationId: string | null;
  term: OrderTerm;
  tradeClassification: TradeClassification;
  tradeConditionFlag: TradeConditionFlag;
  tradeSplitIndex: number | null;
  tradedAt: DateLike;
  traderUserId: string;
  traderUserInitials: string | null;
  tradingType: TradingType;
  version: number;
}

export interface DealAllocateEvent extends StateMachineDealEvent, DealIdProvider {
  dealId: string;
  eventType: 'DealAllocateEvent';
}

export interface DealAllocateLateWarningEvent extends StateMachineDealEvent, DealIdProvider {
  dealId: string;
  eventType: 'DealAllocateLateWarningEvent';
}

export interface DealAmendEvent extends StateMachineDealEvent, DealIdProvider {
  dealId: string;
  dealUserAmendedAttributes: DealUserAmendedAttributes;
  eventType: 'DealAmendEvent';
}

export interface DealAssignFundEvent extends StateMachineDealEvent, DealIdProvider {
  dealId: string;
  eventType: 'DealAssignFundEvent';
}

export interface DealCancelAllocationsEvent extends StateMachineDealEvent, DealIdProvider {
  dealId: string;
  eventType: 'DealCancelAllocationsEvent';
}

export interface DealCancelAssignmentEvent extends StateMachineDealEvent, DealIdProvider {
  dealId: string;
  eventType: 'DealCancelAssignmentEvent';
}

export interface DealCancelEvent extends StateMachineDealEvent, DealIdProvider {
  dealId: string;
  eventType: 'DealCancelEvent';
}

export interface DealClearingCancelResponseReceivedEvent extends StateMachineDealEvent, DealIdProvider {
  dealId: string;
  dealVersion: number;
  eventType: 'DealClearingCancelResponseReceivedEvent';
  message: string;
  rejectReason: string | null;
  result: ClearingResponseResult;
}

export interface DealClearingCancelSentEvent extends StateMachineDealEvent, DealIdProvider {
  clearingInstance: ClearingInstance;
  contingencyMessage: string;
  dealId: string;
  dealVersion: number;
  egressPosition: number;
  eventType: 'DealClearingCancelSentEvent';
  message: string;
}

export interface DealClearingCreateResponseReceivedEvent extends StateMachineDealEvent, DealIdProvider {
  dealId: string;
  dealVersion: number;
  eventType: 'DealClearingCreateResponseReceivedEvent';
  message: string;
  rejectReason: string | null;
  result: ClearingResponseResult;
}

export interface DealClearingCreateSentEvent extends StateMachineDealEvent, DealIdProvider {
  clearingInstance: ClearingInstance;
  contingencyMessage: string;
  contingencyReverseMessage: string;
  dealId: string;
  dealVersion: number;
  egressPosition: number;
  eventType: 'DealClearingCreateSentEvent';
  message: string;
  reverseMessage: string;
}

export interface DealClearingFailedEvent extends StateMachineDealEvent, DealIdProvider {
  contingencyMessage: string | null;
  contingencyReverseMessage: string | null;
  dealId: string;
  egressPosition: number | null;
  errorMessage: string | null;
  eventType: 'DealClearingFailedEvent';
  message: string | null;
  messageType: ClearingMessageType | null;
  reverseMessage: string | null;
  type: Type;
}

export interface DealClearingFailedManuallyAcceptedEvent extends StateMachineDealEvent, DealIdProvider {
  dealId: string;
  eventType: 'DealClearingFailedManuallyAcceptedEvent';
}

export interface DealClearingFailedManuallyRejectedEvent extends StateMachineDealEvent, DealIdProvider {
  dealId: string;
  eventType: 'DealClearingFailedManuallyRejectedEvent';
}

export interface DealClearingFailedRetryEvent extends StateMachineDealEvent, DealIdProvider {
  dealId: string;
  eventType: 'DealClearingFailedRetryEvent';
}

export interface DealCollateralAssignmentStartSaveAllocateEvent extends StateMachineDealEvent, DealIdProvider {
  allocateBy: TradingType;
  collateralAllocations: CollateralAllocation[];
  dealId: string;
  eventType: 'DealCollateralAssignmentStartSaveAllocateEvent';
}

export interface DealCompleteEvent extends StateMachineDealEvent, DealIdProvider {
  dealId: string;
  eventType: 'DealCompleteEvent';
}

export interface DealExpireAllocationEvent extends StateMachineDealEvent, DealIdProvider {
  dealId: string;
  eventType: 'DealExpireAllocationEvent';
}

export interface DealExportData {
  Axe: boolean | null;
  'Basket Name': string | null;
  'Billing Currency': Currency | null;
  'Billing Notional': number | null;
  Book: string | null;
  Bundle: string | null;
  CSD: DealSettlement | null;
  CUSIP: string | null;
  'Call Days': number | null;
  Callable: ExportCallableType | null;
  'Clearing Venue': string | null;
  Collateral: string | null;
  'Collateral Accrued Interest': number | null;
  'Collateral Id': string | null;
  'Collateral Price': number | null;
  'Collection Id': string | null;
  'Collection Type': CollectionType | null;
  Counterparty: string | null;
  'Counterparty Alias': string | null;
  'Counterparty BIC': string | null;
  'Counterparty Firm': string | null;
  'Counterparty Firm Code': string | null;
  'Counterparty Firm LEI': string | null;
  'Counterparty GFID Code': string | null;
  'Counterparty LEI': string | null;
  'Counterparty Short Name': string | null;
  'Counterparty Trader': string | null;
  Coupon: number | null;
  'Creation Date': DateLike | null;
  Currency: Currency | null;
  'Deal Basket Id': string | null;
  Direction: Direction | null;
  'End Consideration': number | null;
  'End Date': DateLike | null;
  'FX Rate': number | null;
  Factor: number | null;
  Fee: number | null;
  Haircut: number | null;
  'Haircut Type': HaircutType | null;
  ISIN: string | null;
  Issuer: string | null;
  'Issuer Org ID': string | null;
  'Last Updated By': string | null;
  'Last Updated Date': DateLike | null;
  MIC: string | null;
  'Master Agreement Type': MasterLegalAgreementType | null;
  'My Entity': string | null;
  'My Entity BIC': string | null;
  'My Entity GFID Code': string | null;
  'My Entity LEI': string | null;
  'My Entity Short Name': string | null;
  'My Firm': string | null;
  'My Firm Code': string | null;
  'My Firm LEI': string | null;
  'Negotiation ID': string | null;
  Notional: number | null;
  'Original Id': string | null;
  Originator: string | null;
  'Parent Id': string | null;
  'Pre Agreed': boolean | null;
  'Primary Sales Cover': string | null;
  'Prime Broker': string | null;
  Product: string | null;
  Rate: number | null;
  'Reference Rate': string | null;
  'Repo Interest': number | null;
  'Repo Type': RepoType | null;
  'Rfq ID': string | null;
  'Right of Exit': number | null;
  'Right of Substitution': number | null;
  'Roll/Unwind': TradeConditionFlag | null;
  'SBL Type': StockBorrowingLendingType | null;
  'Secondary Collateral': string | null;
  'Secondary Sales Cover': string | null;
  Sector: string | null;
  Settlement: SettlementVenue | null;
  'Start Consideration': number | null;
  'Start Date': DateLike | null;
  Status: DealStatus | null;
  Strategy: string | null;
  Term: OrderTerm | null;
  'Trade Date': DateLike | null;
  'Trade ID': string;
  'Trade type': RepoClassification | null;
  Trader: string | null;
  UTI: string | null;
  collectionId: string | null;
  collectionType: CollectionType | null;
  counterpartyEntityShortName: string | null;
  counterpartyGfidCode: string | null;
  counterpartyOrganisationLegalName: string | null;
  counterpartyOrganisationLei: string | null;
  counterpartyPrimaryCoverUsername: string | null;
  counterpartyPublishedName: string | null;
  counterpartySecondaryCoverUsername: string | null;
  counterpartyShortCode: string | null;
  counterpartyTraderUserId: string | null;
  counterpartyTradingEntityBic: string | null;
  counterpartyTradingEntityId: string | null;
  counterpartyTradingEntityLei: string | null;
  dealExportFiles: DealExportFile[] | null;
  direction: Direction | null;
  fundEntityShortName: string | null;
  fundGfidCode: string | null;
  fundOrganisationLegalName: string | null;
  fundOrganisationLei: string | null;
  fundPrimaryCoverUsername: string | null;
  fundPublishedName: string | null;
  fundSecondaryCoverUsername: string | null;
  fundShortCode: string | null;
  fundTradingEntityBic: string | null;
  fundTradingEntityId: string | null;
  fundTradingEntityLei: string | null;
  fxRate: FxRate | null;
  generatingFileForTradingEntityId: string | null;
  initiatorUtiRtn: string | null;
  isCounterpartyFifteenASix: boolean | null;
  isFundFifteenASix: boolean | null;
  market: Market | null;
  responderBook: string | null;
  responderStrategy: string | null;
  responderUtiRtn: string | null;
  traderUserId: string | null;
}

export interface DealExportEntry {
  dealExportData: DealExportData;
  timeStamp: DateLike;
}

export interface DealExportEntryUpdate extends EntityUpdate {
  entity: DealExportEntry;
  entityType: 'DEAL_EXPORT';
}

export interface DealExtendAllocationTimeEvent extends StateMachineDealEvent, DealIdProvider {
  dealId: string;
  eventType: 'DealExtendAllocationTimeEvent';
}

export interface DealFundAssignmentStartSaveAssignmentEvent extends StateMachineDealEvent, DealIdProvider {
  assignments: FundTradingEntityAssignment[];
  dealId: string;
  eventType: 'DealFundAssignmentStartSaveAssignmentEvent';
}

export interface DealHistoryUpdate extends EntityUpdate {
  entity: Deal;
  entityType: 'DEAL_HISTORY';
}

export interface DealIdProvider {
}

export interface DealIdsProvider {
}

export interface DealInterestPeriodUpdate extends EntityUpdate {
  entity: Deal;
  entityType: 'DEAL_INTEREST_PERIOD';
}

export interface DealRecalculateEvent extends StateMachineDealEvent, DealIdProvider {
  dealId: string;
  eventType: 'DealRecalculateEvent';
}

/**
 * @deprecated
 */
export interface DealRefreshEvent extends StateMachineDealEvent {
  dealId: string;
  eventType: 'DealRefreshEvent';
}

export interface DealSaveAllocationsEvent extends StateMachineDealEvent, DealIdProvider {
  applyDefaultHaircut: boolean | null;
  calculatingTradingType: TradingType;
  collateralAllocations: CollateralAllocation[];
  dealId: string;
  eventType: 'DealSaveAllocationsEvent';
  haircutType: HaircutType | null;
  source: SaveAllocationsSource;
  trimIndex: number | null;
}

export interface DealSaveFundAssignmentEvent extends StateMachineDealEvent, DealIdProvider {
  assignments: FundTradingEntityAssignment[];
  dealId: string;
  eventType: 'DealSaveFundAssignmentEvent';
  failOnValidation: boolean | null;
  trimIndex: number | null;
}

export interface DealServicingAcceptEvent extends StateMachineDealEvent, DealIdProvider {
  dealId: string;
  eventType: 'DealServicingAcceptEvent';
  externalCounterId: string | null;
  externalTerminationServicingId: string | null;
  replacedWithDealId: string | null;
  servicingType: ServicingType | null;
}

export interface DealServicingCancelEvent extends StateMachineDealEvent, DealIdProvider {
  dealId: string;
  eventType: 'DealServicingCancelEvent';
  externalTerminationServicingId: string | null;
}

export interface DealServicingRejectEvent extends StateMachineDealEvent, DealIdProvider {
  dealId: string;
  eventType: 'DealServicingRejectEvent';
  externalTerminationServicingId: string | null;
  servicingType: ServicingType | null;
}

export interface DealServicingRespondAttributes {
  dealId: string;
  effectiveDate: DateLike | null;
  fee: number | null;
  haircut: number | null;
  haircutType: HaircutType | null;
  notional: number | null;
  rate: number | null;
  response: MidTradeResponse;
}

export interface DealServicingRespondEventBulk extends ClientMessageEventPayload, DealIdsProvider {
  dealServicingRespondAttributesList: DealServicingRespondAttributes[];
  eventType: 'DealServicingRespondEventBulk';
}

export interface DealServicingResponderLockEvent extends StateMachineDealEvent, DealIdProvider {
  dealId: string;
  eventType: 'DealServicingResponderLockEvent';
}

export interface DealServicingResponderUnlockEvent extends StateMachineDealEvent, DealIdProvider {
  dealId: string;
  eventType: 'DealServicingResponderUnlockEvent';
}

export interface DealServicingRevertAttributesEvent extends StateMachineDealEvent, DealIdProvider {
  dealId: string;
  eventType: 'DealServicingRevertAttributesEvent';
}

export interface DealServicingSendEvent extends StateMachineDealEvent, DealIdProvider {
  dealId: string;
  eventType: 'DealServicingSendEvent';
  externalResponderServicingId: string | null;
}

export interface DealServicingStartAdminCancelEvent extends DealServicingStartEvent {
  dealId: string;
  eventType: 'DealServicingStartAdminCancelEvent';
}

export interface DealServicingStartAdminCloseOpenEvent extends DealServicingStartEvent {
  dealId: string;
  eventType: 'DealServicingStartAdminCloseOpenEvent';
  recalculateCollateralPrice: boolean | null;
}

export interface DealServicingStartAdminCloseOpenEventBulk extends ClientMessageEventPayload, DealIdsProvider {
  dealIds: string[];
  eventType: 'DealServicingStartAdminCloseOpenEventBulk';
  recalculateCollateralPrice: boolean | null;
}

export interface DealServicingStartAdminRerateEvent extends DealServicingStartEvent {
  dealId: string;
  eventType: 'DealServicingStartAdminRerateEvent';
}

export interface DealServicingStartAdminRerateEventBulk extends ClientMessageEventPayload, DealIdsProvider {
  dealIds: string[];
  eventType: 'DealServicingStartAdminRerateEventBulk';
}

export interface DealServicingStartAdminSubstitutionEvent extends DealServicingStartEvent {
  dealId: string;
  eventType: 'DealServicingStartAdminSubstitutionEvent';
}

export interface DealServicingStartAdminSubstitutionEventBulk extends ClientMessageEventPayload, DealIdsProvider {
  dealIds: string[];
  eventType: 'DealServicingStartAdminSubstitutionEventBulk';
}

export interface DealServicingStartAdminTerminateEvent extends DealServicingStartEvent {
  dealId: string;
  eventType: 'DealServicingStartAdminTerminateEvent';
}

export interface DealServicingStartAdminTerminateEventBulk extends ClientMessageEventPayload, DealIdsProvider {
  dealIds: string[];
  eventType: 'DealServicingStartAdminTerminateEventBulk';
}

export interface DealServicingStartAmendEvent extends DealServicingStartEvent {
  dealId: string;
  eventType: 'DealServicingStartAmendEvent';
}

export interface DealServicingStartAttributes extends DealIdProvider {
  action: MidTradeAction;
  dealId: string;
  effectiveDate: DateLike | null;
  fee: number | null;
  haircut: number | null;
  haircutType: HaircutType | null;
  notional: number | null;
  rate: number | null;
}

export interface DealServicingStartCloseOpenAttributes extends DealIdProvider {
  dealId: string;
}

export interface DealServicingStartCloseOpenEvent extends DealServicingStartEvent {
  dealId: string;
  eventType: 'DealServicingStartCloseOpenEvent';
  recalculateCollateralPrice: boolean | null;
}

export interface DealServicingStartCloseOpenEventBulk extends ClientMessageEventPayload, DealIdsProvider {
  dealServicingStartCloseOpenAttributesList: DealServicingStartCloseOpenAttributes[];
  eventType: 'DealServicingStartCloseOpenEventBulk';
  recalculateCollateralPrice: boolean | null;
}

export interface DealServicingStartEvent extends StateMachineDealEvent, DealIdProvider {
  eventType: 'DealServicingStartAmendEvent' | 'DealServicingStartCloseOpenEvent' | 'DealServicingStartRerateEvent' | 'DealServicingStartReturnRecallEvent' | 'DealServicingStartRollInPlaceEvent' | 'DealServicingStartSubstituteEvent' | 'DealServicingStartTerminateEvent' | 'DealServicingStartAdminCancelEvent' | 'DealServicingStartAdminCloseOpenEvent' | 'DealServicingStartAdminRerateEvent' | 'DealServicingStartAdminTerminateEvent' | 'DealServicingStartAdminSubstitutionEvent' | 'DealServicingStartTraderAmendEvent' | 'DealServicingStartTraderCancelEvent' | 'DealServicingStartRollEvent';
  externalServicingId: string | null;
}

export interface DealServicingStartEventBulk extends ClientMessageEventPayload, DealIdsProvider {
  dealServicingStartAttributesList: DealServicingStartAttributes[];
  eventType: 'DealServicingStartEventBulk';
}

export interface DealServicingStartRerateAttributes extends DealIdProvider {
  dealId: string;
}

export interface DealServicingStartRerateEvent extends DealServicingStartEvent {
  dealId: string;
  eventType: 'DealServicingStartRerateEvent';
}

export interface DealServicingStartRerateEventBulk extends ClientMessageEventPayload, DealIdsProvider {
  dealServicingStartRerateAttributesList: DealServicingStartRerateAttributes[];
  eventType: 'DealServicingStartRerateEventBulk';
}

export interface DealServicingStartReturnRecallEvent extends DealServicingStartEvent {
  dealId: string;
  eventType: 'DealServicingStartReturnRecallEvent';
}

export interface DealServicingStartRollEvent extends DealServicingStartEvent {
  dealId: string;
  eventType: 'DealServicingStartRollEvent';
}

export interface DealServicingStartRollInPlaceEvent extends DealServicingStartEvent {
  dealId: string;
  eventType: 'DealServicingStartRollInPlaceEvent';
}

export interface DealServicingStartRollInPlaceEventBulk extends ClientMessageEventPayload, DealIdsProvider {
  dealIds: string[];
  eventType: 'DealServicingStartRollInPlaceEventBulk';
}

export interface DealServicingStartSubstituteEvent extends DealServicingStartEvent {
  dealId: string;
  eventType: 'DealServicingStartSubstituteEvent';
}

export interface DealServicingStartTerminateEvent extends DealServicingStartEvent {
  dealId: string;
  eventType: 'DealServicingStartTerminateEvent';
}

export interface DealServicingStartTerminateEventBulk extends ClientMessageEventPayload, DealIdsProvider {
  dealIds: string[];
  eventType: 'DealServicingStartTerminateEventBulk';
}

export interface DealServicingStartTraderAmendEvent extends DealServicingStartEvent {
  dealId: string;
  eventType: 'DealServicingStartTraderAmendEvent';
}

export interface DealServicingStartTraderCancelEvent extends DealServicingStartEvent {
  dealId: string;
  eventType: 'DealServicingStartTraderCancelEvent';
}

export interface DealServicingUpdateAttributesEvent extends StateMachineDealEvent, DealIdProvider {
  dealId: string;
  decisionMakerOrientedByNegotiationSide: boolean;
  eventType: 'DealServicingUpdateAttributesEvent';
  failOnValidation: boolean | null;
  field: NegotiationFieldMeta;
  meta: DealServicingUpdateAttributesMeta | null;
  revertFirst: boolean | null;
  updatedRawValue: any | null;
  updatedValue: any;
}

export interface DealServicingUpdateAttributesMeta {
  haircutSign: HaircutSign | null;
}

export interface DealServicingUpdateRegulatoryAttributesEvent extends StateMachineDealEvent, DealIdProvider {
  dealId: string;
  eventType: 'DealServicingUpdateRegulatoryAttributesEvent';
  executionDecisionMaker: string | null;
  investmentDecisionMaker: string | null;
  revertFirst: boolean | null;
}

export interface DealServicingWithdrawEvent extends StateMachineDealEvent, DealIdProvider {
  dealId: string;
  eventType: 'DealServicingWithdrawEvent';
  externalTerminationServicingId: string | null;
  servicingType: ServicingType | null;
}

export interface DealServicingWorkflowCancelEvent extends StateMachineDealEvent, DealIdProvider {
  dealId: string;
  eventType: 'DealServicingWorkflowCancelEvent';
  servicingType: ServicingType;
}

export interface DealServicingWorkflowCompleteEvent extends StateMachineDealEvent, DealIdProvider {
  dealId: string;
  eventType: 'DealServicingWorkflowCompleteEvent';
  replacedWithDealId: string;
  servicingType: ServicingType;
}

/**
 * @deprecated
 */
export interface DealSettledEvent extends StateMachineDealEvent, DealIdProvider {
  dealId: string;
  eventType: 'DealSettledEvent';
}

export interface DealStartAllocationsEvent extends StateMachineDealEvent, DealIdProvider {
  applyDefaultHaircut: boolean;
  dealId: string;
  eventType: 'DealStartAllocationsEvent';
}

export interface DealStartFundAssignmentEvent extends StateMachineDealEvent, DealIdProvider {
  dealId: string;
  eventType: 'DealStartFundAssignmentEvent';
}

export interface DealTradeEvent extends StateMachineDealEvent {
  dealDetail: any;
  dealTrigger: DealTrigger;
  eventType: 'DealTradeEvent';
  internalId: string;
  negotiationDetail: any;
  preAssignedIndex: number | null;
  tradeSplitIndex: number | null;
}

export interface DealUpdate extends EntityUpdate {
  availableEvents: DealEvent[];
  entity: Deal;
  entityType: 'DEAL';
  validationIssues: ValidationIssue[];
}

export interface DealUpdateAllocationCollateralsEvent extends StateMachineDealEvent, DealIdProvider {
  dealId: string;
  eventType: 'DealUpdateAllocationCollateralsEvent';
}

export interface DealUpdateAvailableEventsEvent extends StateMachineDealEvent, DealIdProvider {
  dealId: string;
  eventType: 'DealUpdateAvailableEventsEvent';
}

export interface DealUpdateFundAssignmentReminderEvent extends StateMachineDealEvent, DealIdProvider {
  dealId: string;
  eventType: 'DealUpdateFundAssignmentReminderEvent';
}

export interface DealUserAmendedAttributes {
  bondPrice: number | null;
  bundle: string | null;
  counterpartyId: string | null;
  counterpartySponsorId: string | null;
  fundId: string | null;
  haircut: number | null;
  initiatorBook: string | null;
  initiatorExecutionDecisionMaker: string | null;
  initiatorInvestmentDecisionMaker: string | null;
  initiatorPrimeBrokerId: string | null;
  initiatorStrategy: string | null;
  notional: number | null;
  preAgreed: boolean | null;
  rate: number | null;
  responderBook: string | null;
  responderExecutionDecisionMaker: string | null;
  responderInvestmentDecisionMaker: string | null;
  responderStrategy: string | null;
  sponsorId: string | null;
  startDate: DateLike | null;
  term: OrderTerm | null;
}

export interface DealWithoutStatus {
  acceptedActor: AcceptedActor | null;
  accruedInterest: number | null;
  accruedInterestAmount: number | null;
  actualClearingDetails: ClearingDetails | null;
  allCommittedServicingTypes: ServicingType[];
  allInPrice: number | null;
  amended: boolean;
  assetType: AssetType | null;
  axeId: string | null;
  basisConvention: BasisConvention | null;
  basketAllocation: BasketAllocation | null;
  basketDealId: string | null;
  bic: string | null;
  blockDealId: string | null;
  bondPrice: number;
  branchBic: string | null;
  bundle: string | null;
  calculatedStartConsideration: number | null;
  callablePeriodDays: number | null;
  callableType: CallableType;
  cleanPrice: number;
  clearingHouseId: string | null;
  clearingType: TradingEntityClearingType | null;
  collateralId: string | null;
  collateralIssue: Issue | null;
  collectionId: string | null;
  committedServicingType: ServicingType | null;
  competitionCount: number | null;
  containerCollectionId: string | null;
  counterpartyAlias: string | null;
  counterpartyClearingType: TradingEntityClearingType | null;
  counterpartyId: string;
  counterpartyPrimaryCoverUserId: string | null;
  counterpartySecondaryCoverUserId: string | null;
  counterpartySponsorId: string | null;
  counterpartyTraderId: string;
  createdDueToServicingType: ServicingType | null;
  createdFromCollectionType: CollectionType | null;
  currency: Currency;
  dealId: string;
  direction: Direction;
  dirtyPrice: number;
  endConsideration: number;
  endDate: DateLike | null;
  evergreenNoticePeriod: EvergreenNoticePeriod | null;
  externalNegotiationId: string | null;
  externalOrderId: string | null;
  externalSummaryNegotiationId: string | null;
  factor: number | null;
  fee: number | null;
  floatCrystallisationDays: number | null;
  fundAssignment: FundAssignment | null;
  fundId: string;
  fundPrimaryCoverUserId: string | null;
  fundSecondaryCoverUserId: string | null;
  haircut: number;
  haircutType: HaircutType | null;
  hideCompetition: boolean | null;
  indexRatio: number | null;
  initiatorBook: string | null;
  initiatorExecutionDecisionMaker: string;
  initiatorInvestmentDecisionMaker: string;
  initiatorOrganisationId: string | null;
  initiatorPrimeBrokerId: string | null;
  initiatorStrategy: string | null;
  initiatorUtiRtn: string;
  internalAxeId: string | null;
  isAxed: boolean;
  isInitialOrderUpdated: boolean | null;
  isSponsorCounterparty: boolean | null;
  market: Market;
  marketIdentifierCode: MicCode;
  marketTradeDate: DateLike;
  marketType: MarketType;
  masterLegalAgreementType: MasterLegalAgreementType | null;
  negotiationId: string;
  negotiationStarted: DateLike;
  notesId: string | null;
  noticePeriodDays: number | null;
  notional: number;
  orderId: string;
  orderSubmitUserId: string | null;
  parentDealBasketId: string | null;
  positionId: string | null;
  preAgreed: boolean;
  preAssignedIndex: number | null;
  productId: string | null;
  productSectorId: string | null;
  rate: number | null;
  referenceRate: string | null;
  repoClassification: RepoClassification;
  repoInterest: number | null;
  repoInterestStartDate: DateLike | null;
  repoInterestToDate: number | null;
  repoType: RepoType;
  responderBook: string | null;
  responderExecutionDecisionMaker: string;
  responderInvestmentDecisionMaker: string;
  responderStrategy: string | null;
  responderUtiRtn: string;
  rightOfExit: number;
  rightOfSubstitution: number;
  rolled: boolean;
  secondaryBasketId: string | null;
  secondaryCollateralId: string | null;
  servicingAcceptedActor: AcceptedActor | null;
  servicingMarketTradeDate: DateLike | null;
  settlement: DealSettlement | null;
  settlementVenue: SettlementVenue;
  sponsorId: string | null;
  startConsideration: number;
  startConsiderationOverride: boolean;
  startDate: DateLike;
  stockBorrowingLendingType: StockBorrowingLendingType | null;
  summaryNegotiationId: string | null;
  term: OrderTerm;
  tradeClassification: TradeClassification;
  tradeConditionFlag: TradeConditionFlag;
  tradeSplitIndex: number | null;
  tradedAt: DateLike;
  traderUserId: string;
  tradingType: TradingType;
}

export interface DevSignal extends DealIdsProvider {
  correlationId: string;
  effectiveInstant: DateLike;
  forEntities: string[] | null;
  forUsers: string[] | null;
  message: string;
  sentInstant: DateLike;
  signalType: SignalType;
  tradableType: string | null;
  updateUserGroup: UpdateUserGroup | null;
}

export interface DevSignalUpdate extends EntityUpdate {
  entity: DevSignal;
  entityType: 'DEV_SIGNAL';
}

export interface EgressPosition {
  id: string | null;
  lastUpdated: DateLike | null;
  position: number | null;
  type: EgressPositionIdentifier | null;
}

export interface EgressPositionUpdate extends EntityUpdate {
  entity: EgressPosition;
  entityType: 'EGRESS_POSITION';
}

export interface EntityDeleteMessage extends ServerMessage {
  entityId: string;
  entityType: EntityTypeEnum;
  negotiationSide: NegotiationSide | null;
  type: 'ENTITY_DELETE';
}

export interface EntityIdAndType {
  entityId: string | null;
  entityType: EntityTypeEnum | null;
}

export interface EntityRemoveSubscriptionIdsMessage extends ServerMessage {
  entityId: string;
  entityType: EntityTypeEnum;
  negotiationSide: NegotiationSide | null;
  subscriptionIdentifiers: string[];
  type: 'ENTITY_REMOVE_SUBSCRIPTION_IDENTIFIERS';
}

export interface EntitySnapshotBeginMessage extends ServerMessage {
  entityType: EntityTypeEnum;
  snapshotSize: number;
  type: 'ENTITY_SNAPSHOT_BEGIN';
}

export interface EntitySnapshotEndMessage extends ServerMessage {
  entityType: EntityTypeEnum;
  subscriptionIds: string[];
  type: 'ENTITY_SNAPSHOT_END';
}

export interface EntityStreamSnapshotsBeginMessage extends ServerMessage {
  type: 'ENTITY_STREAM_SNAPSHOTS_BEGIN';
}

export interface EntityStreamSnapshotsEndMessage extends ServerMessage {
  type: 'ENTITY_STREAM_SNAPSHOTS_END';
}

export interface EntitySubscriptionMeta {
  pageNumber: number;
  subscriptionId: string;
  summaryResponse: SummaryResponse;
  totalPages: number;
  totalResultCount: number;
}

export interface EntitySubscriptionMetaMessage extends ServerMessage {
  entityType: EntityTypeEnum;
  subscriptionMetaMap: { [index: string]: EntitySubscriptionMeta };
  type: 'ENTITY_SUBSCRIPTION_META';
}

export interface EntityUpdate {
  entityId: string;
  entityType: 'APPLICATION_ALERT' | 'AUDIT_ENTRY' | 'AUTHORISED_USER' | 'AXE' | 'BEST_PRICE' | 'BATCH_DATA' | 'CLEARING_HOUSE_INTERACTION' | 'COUNTRY' | 'CLEARING_HOUSE' | 'CLUSTER_CLIENT_STATE' | 'CLUSTER_PONG' | 'CLUSTER_SESSION' | 'COLLATERAL_BASKET' | 'COLLATERAL_PRICE' | 'COLLATERAL' | 'COLLATERAL_DETAILS' | 'COLLATERAL_UPLOAD' | 'COLLECTION' | 'COMPLIANCE_COLLATERAL' | 'COUNTER' | 'DATABASE_REPORT_REQUEST' | 'DEAL_EXPORT' | 'DEAL_HISTORY' | 'DEAL_INTEREST_PERIOD' | 'DEAL' | 'DEV_SIGNAL' | 'EGRESS_POSITION' | 'EONIA_RATE' | 'ESTR_RATE' | 'RETRY_FAILED_EXPORT' | 'FAT_FINGER_PRODUCT_SECTOR' | 'FAT_FINGER_USER' | 'FX_RATE' | 'HAIRCUT_BAND' | 'HAIRCUT_COLLATERAL_BAND' | 'HAIRCUT_ENTRY' | 'HOLIDAY' | 'IMPORT_COMPLETE' | 'INDEX' | 'IN_FLIGHT_ORGANISATION' | 'IN_FLIGHT_TARGET_ORGANISATION' | 'NEGOTIATION_AGGREGATE' | 'NEGOTIATION_HISTORY' | 'NEGOTIATION_OVERRIDE' | 'NEGOTIATION' | 'NOTES' | 'ORDER_RECORD_KEEPING' | 'ORDER' | 'ORGANISATION_META' | 'ORGANISATION' | 'ISSUER_ORGANISATION' | 'PERMISSION_REPORT_REQUEST' | 'PLATFORM' | 'PRIME_BROKER' | 'PRODUCT_SECTOR' | 'PRODUCT_SECTOR_MAPPING' | 'PRODUCT_SECTOR_MAPPING_DRAFT' | 'PRODUCT' | 'QUERY_REQUEST' | 'REFERENCE_DATA_MANAGEMENT_DATA' | 'REFERENCE_RATE' | 'RESET_SOTW' | 'SOTW_SENT' | 'SPONSOR' | 'SPREADSHEET_MAPPING' | 'TRADING_CONTROL_LIMIT' | 'TRADING_CONTROL_LIMIT_USER_UTILISATION' | 'TRADING_ENTITY_CLEARING' | 'TRADING_ENTITY_META' | 'TRADING_ENTITY' | 'TRADING_RELATIONSHIP' | 'USER_PREFERENCE' | 'USER_SETTINGS' | 'USER' | 'WORKING_DAYS' | 'COLLATERAL_REFINITIV_IMPORT_RESULT' | 'PRODUCT_SECTOR_MAPPING_STATE';
  subscriptionIdentifiers: string[];
}

export interface EntityUpdateMessage extends ServerMessage {
  isSnapshot: boolean;
  type: 'ENTITY_UPDATE';
  update: EntityUpdateUnion;
}

export interface EntityUpdateWithSide {
  negotiationSide: NegotiationSide | null;
}

export interface EntityWrapper {
  data: any | null;
  entityState: EntityState | null;
  entityTypeEnum: EntityTypeEnum | null;
  id: string | null;
  validationIssues: ValidationIssue[] | null;
}

export interface EoniaRate extends Rate {
  id: string;
  rate: number;
  rateFromDate: DateLike;
}

export interface EoniaRateUpdate extends EntityUpdate {
  entity: EoniaRate;
  entityType: 'EONIA_RATE';
}

export interface EstrRate extends Rate {
  id: string;
  rate: number;
  rateFromDate: DateLike;
}

export interface EstrRateUpdate extends EntityUpdate {
  entity: EstrRate;
  entityType: 'ESTR_RATE';
}

export interface EventResponseMessage extends ServerMessage {
  correlationId: string;
  externalMessage: string | null;
  messageClientFriendly: boolean;
  messages: string[];
  status: EventResponseStatus;
  type: 'EVENT_RESPONSE';
  validationIssues: ValidationIssue[];
}

export interface ExportFailedRetryData extends ClusterPublishFilter {
  entity: any;
  entityKeys: { [index: string]: any };
  eventType: number;
  failedExporters: string[];
  failedPosition: number;
  id: string;
}

export interface ExportFailedRetryDataUpdate extends EntityUpdate {
  entity: ExportFailedRetryData;
  entityType: 'RETRY_FAILED_EXPORT';
}

export interface FatFingerProductSectorLimit {
  amountRestriction: number;
  amountWarn: number;
  cashAmountRestriction: number | null;
  cashAmountWarn: number | null;
  fixedRatePrice: number;
  fixedRateRestriction: number | null;
  fixedRateWarnLevel: number | null;
  floatRateRestriction: number | null;
  floatRateSpread: number | null;
  floatRateWarnLevel: number | null;
  notionalAmountRestriction: number | null;
  notionalAmountWarn: number | null;
  productSectorId: string;
}

export interface FatFingerProductSectorLimitUpdate extends EntityUpdate {
  availableEvents: CrudEventEnum[];
  entity: FatFingerProductSectorLimit;
  entityType: 'FAT_FINGER_PRODUCT_SECTOR';
}

export interface FatFingerProductSectorLimitUpsertEvent extends CrudUpsertEvent {
  entity: FatFingerProductSectorLimit;
  eventType: 'FatFingerProductSectorLimitUpsertEvent';
}

export interface FatFingerUserLimit {
  cashAmountRestriction: number | null;
  cashAmountWarn: number | null;
  fixedRateRestriction: number | null;
  fixedRateWarnLevel: number | null;
  floatRateRestriction: number | null;
  floatRateWarnLevel: number | null;
  id: string;
  notionalAmountRestriction: number | null;
  notionalAmountWarn: number | null;
  productSectorId: string;
  userId: string;
}

export interface FatFingerUserLimitUpdate extends EntityUpdate {
  availableEvents: CrudEventEnum[];
  entity: FatFingerUserLimit;
  entityType: 'FAT_FINGER_USER';
}

export interface FundAssignment {
  amountAssigned: number | null;
  assignmentReminderCount: number;
  assignments: FundTradingEntityAssignment[];
  remainingAssignmentRequired: number | null;
  trimIndex: number | null;
  userIdWithLock: string | null;
  validationIssues: ValidationIssue[] | null;
}

export interface FundTradingEntityAssignment {
  accruedInterest: number | null;
  bondPrice: number | null;
  cleanPrice: number | null;
  dirtyPrice: number | null;
  endConsideration: number | null;
  initiatorBook: string | null;
  initiatorStrategy: string | null;
  notional: number | null;
  selected: boolean | null;
  startConsideration: number | null;
  tradingEntityId: string;
  validationIssues: ValidationIssue[] | null;
}

export interface FxRate {
  baseCurrency: Currency;
  id: string;
  lastUpdatedAt: DateLike | null;
  quoteCurrency: Currency;
  rate: number;
}

export interface FxRateUpdate extends EntityUpdate {
  availableEvents: CrudEventEnum[];
  entity: FxRate;
  entityType: 'FX_RATE';
}

export interface HaircutBand {
  id: string;
  maxDays: number | null;
  minDays: number;
}

export interface HaircutBandUpdate extends EntityUpdate {
  availableEvents: CrudEventEnum[];
  entity: HaircutBand;
  entityType: 'HAIRCUT_BAND';
}

export interface HaircutCollateralBand {
  id: string;
  maxYears: number | null;
  minYears: number | null;
}

export interface HaircutCollateralBandUpdate extends EntityUpdate {
  availableEvents: CrudEventEnum[];
  entity: HaircutCollateralBand;
  entityType: 'HAIRCUT_COLLATERAL_BAND';
}

export interface HaircutEntry {
  collateralMaturityMaxYears: number | null;
  collateralMaturityMinYears: number | null;
  haircutType: HaircutType;
  id: string;
  initiatorOrganisationId: string | null;
  initiatorTradingEntityId: string | null;
  productId: string | null;
  productSectorId: string | null;
  responderOrganisationId: string;
  responderTradingEntityId: string | null;
  valuesByBand: { [index: string]: number } | null;
}

export interface HaircutEntryUpdate extends EntityUpdate {
  availableEvents: CrudEventEnum[];
  entity: HaircutEntry;
  entityType: 'HAIRCUT_ENTRY';
}

export interface HaircutEntryUpsertEvent extends CrudUpsertEvent {
  entity: HaircutEntry;
  eventType: 'HaircutEntryUpsertEvent';
}

export interface HeartbeatMessage extends ServerMessage {
  now: DateLike;
  type: 'HEARTBEAT';
}

export interface HistoricalQueryResponseMessage extends ServerMessage {
  correlationId: string;
  entityTypeEnum: EntityTypeEnum;
  page: number | null;
  pageSize: number | null;
  result: HistoricalQueryResult;
  resultCount: number;
  subscriptionIdentifier: string;
  totalPages: number | null;
  totalResultCount: number;
  type: 'HISTORICAL_QUERY_RESPONSE';
}

export interface Holiday {
  currency: string;
  holidays: DateLike[];
  status: EntityStatus;
}

export interface HolidayUpdate extends EntityUpdate {
  availableEvents: CrudEventEnum[];
  entity: Holiday;
  entityType: 'HOLIDAY';
}

export interface HolidayUpsertEvent extends CrudUpsertEvent {
  entity: Holiday;
  eventType: 'HolidayUpsertEvent';
}

export interface IExtractionResponseContents {
  accrualDate: DateLike | null;
  assetStatus: string | null;
  assetStatusEffectiveDate: DateLike | null;
  assetSubType: string | null;
  assetType: string | null;
  baseIndex: string | null;
  bondType: string | null;
  callableFlag: string | null;
  cfiCode: string | null;
  countryOfIncorporation: string | null;
  countryOfIssuance: string | null;
  countryOfIssuanceDescription: string | null;
  countryOfRiskAll: string | null;
  countryOfRiskPrimary: string | null;
  countryOfRiskUltimateParentAll: string | null;
  countryOfRiskUltimateParentPrimary: string | null;
  couponFrequency: number | null;
  couponRate: number | null;
  couponRateEffectiveDate: DateLike | null;
  couponType: string | null;
  currencyCode: string | null;
  cusip: string | null;
  dayCountCode: number | null;
  debtType: string | null;
  denominationIncrement: number | null;
  domicile: string | null;
  domicileDescription: string | null;
  ecbIndicator: string | null;
  eeaVenueEligible: string | null;
  endOfMonthPaymentFlag: string | null;
  exDividendCalendar: string | null;
  exDividendRule: string | null;
  exDividendValue: string | null;
  factor: string | null;
  factorDate: DateLike | null;
  financialInstrumentShortName: string | null;
  firstCouponDate: DateLike | null;
  fitchRating: string | null;
  floatIndexType: string | null;
  floatMultiplier: number | null;
  floatOffset: number | null;
  floaterFlag: string | null;
  floatingRateFixingDate: DateLike | null;
  governmentBondType: string | null;
  identifier: string | null;
  identifierType: InstrumentIdentifierType | null;
  inEsmaFirds: string | null;
  indexLinkedBondBaseIndex: number | null;
  industrySectorCode: string | null;
  industrySubSectorCode: string | null;
  instrumentFullNameEsma: string | null;
  isin: string | null;
  issueDate: DateLike | null;
  issuerLEI: string | null;
  issuerOrgID: string | null;
  issuerShortName: string | null;
  lookBack: number | null;
  maturityDate: DateLike | null;
  mifidIndexIdentifier: string | null;
  mifidIndexName: string | null;
  mifidIndexTerm: string | null;
  minimumDenomination: number | null;
  moodysRating: string | null;
  multiStepFloatFlag: string | null;
  offeringType: number | null;
  parValue: number | null;
  perpetualSecurityFlag: string | null;
  privatePlacementFlag: string | null;
  prospectusDate: DateLike | null;
  refinitivClassificationScheme: string | null;
  regulationIndicator: string | null;
  ric: string | null;
  rule144aIndicator: string | null;
  sandPRating: string | null;
  securityDescription: string | null;
  seniorityCode: string | null;
  seniorityTypeCode: string | null;
  series: string | null;
  settlementDateConvention: string | null;
  sinkableFlag: string | null;
  ticker: string | null;
  totalAmountIssued: number | null;
  totalAmountOutstanding: number | null;
  ukVenueEligible: string | null;
}

export interface ImportComplete {
  filename: string | null;
  importType: ImportType | null;
}

export interface ImportCompleteUpdate extends EntityUpdate {
  availableEvents: CrudEventEnum[];
  entity: ImportComplete;
  entityType: 'IMPORT_COMPLETE';
}

export interface InFlightOrganisation {
  inFlightByCollateral: { [index: string]: InFlightOrganisationCollateral };
  lastUpdateEntityId: string | null;
  organisationId: string;
  version: number | null;
}

export interface InFlightOrganisationCollateral {
  inFlightBuy: number;
  inFlightDate: DateLike;
  inFlightSell: number;
  market: Market;
}

export interface InFlightOrganisationUpdate extends EntityUpdate {
  availableEvents: CrudEventEnum[];
  entity: InFlightOrganisation;
  entityType: 'IN_FLIGHT_ORGANISATION';
}

export interface InFlightTargetOrganisation {
  organisationId: string;
  targetsByCollateralId: { [index: string]: InFlightTargetOrganisationCollateral };
}

export interface InFlightTargetOrganisationCollateral {
  inFlightDate: DateLike;
  market: Market;
  targetBuy: number;
  targetSell: number;
}

export interface InFlightTargetOrganisationUpdate extends EntityUpdate {
  availableEvents: CrudEventEnum[];
  entity: InFlightTargetOrganisation;
  entityType: 'IN_FLIGHT_TARGET_ORGANISATION';
}

export interface Index {
  code: string;
  description: string | null;
  id: string;
  isin: string | null;
  lagAmount: number | null;
  lagType: LagType | null;
  lastUpdatedAt: DateLike | null;
  lastUpdatedBy: string | null;
  refinitivBaseIndex: string | null;
  status: EntityStatus;
  values: { [index: string]: number };
}

export interface IndexUpdate extends EntityUpdate {
  availableEvents: CrudEventEnum[];
  entity: Index;
  entityType: 'INDEX';
}

export interface IndexUpsertEvent extends CrudUpsertEvent {
  entity: Index;
  eventType: 'IndexUpsertEvent';
}

export interface IssuerOrganisation {
  complianceSuspended: boolean | null;
  id: string;
  issuerLei: string | null;
  issuerName: string | null;
  issuerOrganisationId: number | null;
  lastUpdateAt: DateLike | null;
  lastUpdateBy: string | null;
  sanctionScreened: boolean | null;
  tradedOnUsExchange: boolean | null;
  ultimateParentIssuerOrganisationId: number | null;
  ultimateParentPermId: string | null;
}

export interface IssuerOrganisationUpdate extends EntityUpdate {
  availableEvents: CrudEventEnum[];
  entity: IssuerOrganisation;
  entityType: 'ISSUER_ORGANISATION';
}

export interface LongValue extends TypeSpecificField {
  type: 'long';
  value: number | null;
}

export interface MarketDetails {
  allowBackDatedDeals: boolean;
  lastMarketAutoClose: DateLike;
  lastMarketClose: DateLike;
  lastMarketCloseWasAutoClose: boolean;
  lastMarketOpen: DateLike;
  lastMarketPreOpen: DateLike;
  lastMarketStatus: MarketStatus | null;
  marketStatus: MarketStatus;
  tradeDate: DateLike;
  tradingStatus: TradingStatus;
}

export interface NegotiableField {
  modified: boolean;
}

export interface NegotiableFieldOptional<TYPE> extends NegotiableField {
  initiatorLastValue: TYPE | null;
  modifiedValue: TYPE | null;
  negotiated: TYPE | null;
  original: TYPE | null;
  responderLastValue: TYPE | null;
}

export interface NegotiableFieldRequired<TYPE> extends NegotiableField {
  initiatorLastValue: TYPE;
  modifiedValue: TYPE | null;
  negotiated: TYPE;
  original: TYPE;
  responderLastValue: TYPE;
}

export interface Negotiation {
  absoluteConsideration: number | null;
  accruedInterest: number | null;
  accruedInterestAmount: number | null;
  allInPrice: number | null;
  allowPartial: boolean;
  assetType: AssetType | null;
  axeId: string | null;
  basisConvention: BasisConvention | null;
  basketAllocation: BasketAllocation | null;
  basketId: string | null;
  batchId: string;
  blockTrade: boolean;
  bondPrice: NegotiableFieldRequired<number>;
  bundle: string | null;
  calculatedStartConsideration: number | null;
  callablePeriodDays: number | null;
  callableType: CallableType;
  canSendBackToStaging: boolean;
  cleanPrice: number | null;
  clearingHouseId: string | null;
  clearingType: TradingEntityClearingType | null;
  collateralId: string | null;
  collateralIssue: Issue | null;
  collectionId: string | null;
  comments: string | null;
  competitionCount: number | null;
  containerCollectionId: string | null;
  counterpartyAlias: string | null;
  counterpartyClearingType: TradingEntityClearingType | null;
  counterpartyIdNegotiableField: NegotiableFieldRequired<string>;
  counterpartyPrimaryCoverUserId: string | null;
  counterpartySecondaryCoverUserId: string | null;
  counterpartySponsorId: NegotiableFieldOptional<string>;
  counterpartyTraderId: string | null;
  createdAt: DateLike;
  createdBy: string;
  creatingCorrelationId: string;
  csd: DealSettlement | null;
  currency: Currency;
  dateSource: DateSource | null;
  dealId: string | null;
  direction: Direction | null;
  dirtyPrice: number | null;
  endConsideration: number | null;
  endDate: NegotiableFieldRequired<DateLike>;
  evergreenNoticePeriod: EvergreenNoticePeriod | null;
  externalNegotiationId: string | null;
  externalOrderId: string | null;
  externalQuoteId: string | null;
  externalQuoteResponseId: string | null;
  externalSummaryNegotiationId: string | null;
  factor: number | null;
  fee: NegotiableFieldOptional<number> | null;
  floatCrystallisationDays: number | null;
  fundId: string;
  fundPrimaryCoverUserId: string | null;
  fundSecondaryCoverUserId: string | null;
  haircut: NegotiableFieldOptional<number>;
  haircutType: NegotiableFieldOptional<HaircutType>;
  hideCompetition: boolean | null;
  indexRatio: number | null;
  initiatorBook: NegotiableFieldOptional<string>;
  initiatorExecutionDecisionMaker: NegotiableFieldRequired<string>;
  initiatorInvestmentDecisionMaker: NegotiableFieldRequired<string>;
  initiatorOrganisationId: string;
  initiatorPrimeBrokerId: string | null;
  initiatorPrimeBrokerName: string | null;
  initiatorStrategy: NegotiableFieldOptional<string>;
  internalAxeId: string | null;
  isAxed: NegotiableFieldRequired<boolean>;
  isCurve: boolean;
  isSponsorCounterparty: boolean | null;
  lastEvent: NegotiationEvent | null;
  lastInitiatorId: string | null;
  lastInitiatorInitials: string | null;
  lastQuoted: number | null;
  lastQuotedAt: DateLike | null;
  lastResponderId: string | null;
  lastResponderInitials: string | null;
  lastTriggerEvent: NegotiationEvent | null;
  lastUpdatedAt: DateLike;
  lastUpdatedAtSystem: DateLike;
  lastUpdatedBy: string;
  market: Market;
  marketIdentifierCode: MicCode;
  marketTradeDate: DateLike;
  marketType: MarketType;
  masterLegalAgreementType: MasterLegalAgreementType | null;
  modified: boolean;
  negotiationId: string;
  negotiationMeta: NegotiationMeta;
  negotiationType: NegotiationType;
  nestedLegs: NestedLegs | null;
  notesId: string | null;
  noticePeriodDays: number | null;
  notional: NegotiableFieldRequired<number>;
  otwEndTime: DateLike | null;
  otwStartTime: DateLike | null;
  parentOrderId: string;
  positionId: string | null;
  preAgreed: NegotiableFieldOptional<boolean>;
  preAssigned: boolean | null;
  productId: string | null;
  productSectorId: string | null;
  quotedAtLeastOnce: boolean | null;
  rate: NegotiableFieldOptional<number>;
  referenceRate: string | null;
  refresh: boolean | null;
  repoClassification: RepoClassification;
  repoInterest: number | null;
  repoType: RepoType | null;
  responderBook: NegotiableFieldOptional<string>;
  responderExecutionDecisionMaker: NegotiableFieldOptional<string>;
  responderInvestmentDecisionMaker: NegotiableFieldOptional<string>;
  responderStrategy: NegotiableFieldOptional<string>;
  rfqEndTime: DateLike;
  rfqStartTime: DateLike;
  rfqType: NegotiableFieldRequired<RfqType>;
  rightOfExit: number;
  rightOfSubstitution: number;
  secondaryBasketId: NegotiableFieldOptional<string> | null;
  secondaryCollateralId: NegotiableFieldOptional<string> | null;
  sentBackToStaging: boolean;
  servicingTrigger: ServicingTrigger | null;
  settlementVenueNegotiable: NegotiableFieldRequired<SettlementVenue>;
  sponsorId: string | null;
  startConsideration: NegotiableFieldOptional<number>;
  startConsiderationOverride: NegotiableFieldRequired<boolean>;
  startDate: NegotiableFieldRequired<DateLike>;
  status: NegotiationStatus;
  stockBorrowingLendingType: NegotiableFieldOptional<StockBorrowingLendingType> | null;
  summaryEveryChildLegModified: boolean;
  summaryModified: boolean;
  summaryNegotiationId: string | null;
  targetFillNotional: number | null;
  targetFillStartConsideration: number | null;
  term: NegotiableFieldRequired<OrderTerm>;
  totalFillNotional: number | null;
  totalFillStartConsideration: number | null;
  tradeClassification: TradeClassification;
  tradeConditionFlag: NegotiableFieldRequired<TradeConditionFlag>;
  tradedAt: DateLike | null;
  traderUserId: string | null;
  tradingType: TradingType;
  uploadRowIndex: string | null;
  userHasUpdatedNotionalOrCash: boolean | null;
  userIdWithLock: string | null;
  userModified: boolean;
  version: string;
}

export interface NegotiationAggregate {
  negotiationAggregateId: string;
  negotiationAggregateItems: NegotiationAggregateItem[];
  negotiationAggregateKey: NegotiationAggregateKey;
}

export interface NegotiationAggregateItem {
  negotiationId: string;
}

export interface NegotiationAggregateKey {
  fundId: string | null;
  organisationId: string;
}

export interface NegotiationAggregateUpdate extends EntityUpdate {
  entity: NegotiationAggregate;
  entityType: 'NEGOTIATION_AGGREGATE';
}

export interface NegotiationEventAccept extends StateMachineNegotiationEvent {
  eventType: 'NegotiationEventAccept';
  negotiationId: string;
  quoteResponseId: string | null;
}

export interface NegotiationEventAcceptBulk extends ClientMessageEventPayload {
  eventType: 'NegotiationEventAcceptBulk';
  negotiationEventAcceptBulkNegotiations: NegotiationEventAcceptBulkNegotiation[];
}

export interface NegotiationEventAcceptBulkNegotiation {
  negotiationId: string;
  negotiationSide: NegotiationSide;
  quoteResponseId: string | null;
}

export interface NegotiationEventBackToStaging extends StateMachineNegotiationEvent {
  collectionId: string | null;
  eventType: 'NegotiationEventBackToStaging';
  negotiationId: string;
}

export interface NegotiationEventCancel extends StateMachineNegotiationEvent {
  eventType: 'NegotiationEventCancel';
  negotiationId: string;
  quoteResponseId: string | null;
}

export interface NegotiationEventCancelBulk extends ClientMessageEventPayload {
  cloneOriginalOrder: boolean;
  eventType: 'NegotiationEventCancelBulk';
  negotiationIds: string[];
  negotiationSide: NegotiationSide;
}

export interface NegotiationEventCounterOffer extends StateMachineNegotiationEvent {
  eventType: 'NegotiationEventCounterOffer';
  negotiationId: string;
  refresh: boolean;
}

export interface NegotiationEventCounterOfferWithAttributes extends StateMachineNegotiationEvent {
  bondPriceNegotiated: number;
  endDateNegotiated: DateLike;
  eventType: 'NegotiationEventCounterOfferWithAttributes';
  externalQuoteResponseId: string | null;
  feeNegotiated: number;
  haircutNegotiated: number;
  haircutTypeNegotiated: HaircutType | null;
  isAxedNegotiated: boolean;
  negotiationId: string;
  notionalNegotiated: number;
  preAgreedNegotiated: boolean;
  rateNegotiated: number;
  refresh: boolean;
  secondaryBasketIdNegotiated: string;
  startConsiderationNegotiated: number | null;
  startConsiderationOverrideNegotiated: boolean | null;
  startDateNegotiated: DateLike;
  termNegotiated: OrderTerm;
  tradeConditionFlagNegotiated: TradeConditionFlag;
}

export interface NegotiationEventExpire extends StateMachineNegotiationEvent {
  eventType: 'NegotiationEventExpire';
  negotiationId: string;
  orderExpired: boolean | null;
}

export interface NegotiationEventInitiatorRevertAttributes extends StateMachineNegotiationEvent {
  eventType: 'NegotiationEventInitiatorRevertAttributes';
  negotiationId: string;
}

export interface NegotiationEventLock extends StateMachineNegotiationEvent {
  eventType: 'NegotiationEventLock';
  negotiationId: string;
}

export interface NegotiationEventLockQuote extends StateMachineNegotiationEvent {
  eventType: 'NegotiationEventLockQuote';
  isLastLook: boolean | null;
  negotiationQuote: NegotiationEventQuoteWithAttributes | null;
}

export interface NegotiationEventMiss extends StateMachineNegotiationEvent {
  eventType: 'NegotiationEventMiss';
  negotiationId: string;
}

export interface NegotiationEventOrderCancelled extends StateMachineNegotiationEvent {
  eventType: 'NegotiationEventOrderCancelled';
  negotiationId: string;
}

export interface NegotiationEventQuote extends StateMachineNegotiationEvent {
  eventType: 'NegotiationEventQuote';
  isLastLook: boolean;
  negotiationId: string;
  validUntilTime: DateLike | null;
}

export interface NegotiationEventQuoteWithAttributes extends StateMachineNegotiationEvent {
  eventType: 'NegotiationEventQuoteWithAttributes';
  isLastLook: boolean;
  negotiationId: string;
  negotiationUserModifiedAttributes: NegotiationUserModifiedAttributes;
  validUntilTime: DateLike | null;
}

export interface NegotiationEventRefresh extends StateMachineNegotiationEvent {
  eventType: 'NegotiationEventRefresh';
  negotiationId: string;
}

export interface NegotiationEventReject extends StateMachineNegotiationEvent {
  eventType: 'NegotiationEventReject';
  externalQuoteResponseId: string | null;
  negotiationId: string;
}

export interface NegotiationEventRequest extends StateMachineNegotiationEvent {
  batchId: string | null;
  eventType: 'NegotiationEventRequest';
  negotiation: NegotiationWithoutStatus | null;
  price: number | null;
}

export interface NegotiationEventResponderRevertAttributes extends StateMachineNegotiationEvent {
  eventType: 'NegotiationEventResponderRevertAttributes';
  negotiationId: string;
}

/**
 * @deprecated
 */
export interface NegotiationEventTagAndInvoke extends StateMachineNegotiationEvent {
  eventType: 'NegotiationEventTagAndInvoke';
  events: StateMachineNegotiationEvent[];
  negotiationId: string;
  negotiationTags: NegotiationTags;
}

export interface NegotiationEventTagAndInvokeMany extends StateMachineNegotiationEvent {
  eventType: 'NegotiationEventTagAndInvokeMany';
  negotiationId: string;
  taggedEvents: TaggedNegotiationEvent[];
}

export interface NegotiationEventUnlock extends StateMachineNegotiationEvent {
  eventType: 'NegotiationEventUnlock';
  negotiationId: string;
}

export interface NegotiationEventUpdateAttributes extends StateMachineNegotiationEvent {
  eventType: 'NegotiationEventUpdateAttributes';
  meta: NegotiationUpdateAttributesMeta | null;
  negotiationId: string;
  updatedRawValues: { [P in NegotiationFieldMeta]?: any };
  updatedValues: { [P in NegotiationFieldMeta]?: any };
}

export interface NegotiationEventUpdateAttributesBulk extends ClientMessageEventPayload {
  eventType: 'NegotiationEventUpdateAttributesBulk';
  negotiationSide: NegotiationSide;
  negotiationUpdateAttributesList: NegotiationUpdateAttributes[];
}

export interface NegotiationEventUpdateBestPrice extends StateMachineNegotiationEvent {
  bestPriceStrategiesToRemove: BestPriceStrategy[];
  eventType: 'NegotiationEventUpdateBestPrice';
  negotiationId: string;
}

export interface NegotiationEventUpdateContainerSummary extends StateMachineNegotiationEvent {
  eventType: 'NegotiationEventUpdateContainerSummary';
  negotiationId: string;
  triggeringNegotiationDetailSummary: any | null;
}

export interface NegotiationEventUpdatePreAssignmentAttributes extends StateMachineNegotiationEvent {
  assignmentIndex: number;
  eventType: 'NegotiationEventUpdatePreAssignmentAttributes';
  field: NegotiationFieldMeta;
  negotiationId: string;
  updatedValue: any;
}

export interface NegotiationEventUpdateRegulatoryAttributes extends StateMachineNegotiationEvent {
  eventType: 'NegotiationEventUpdateRegulatoryAttributes';
  executionDecisionMaker: string | null;
  investmentDecisionMaker: string | null;
  negotiationId: string;
}

export interface NegotiationEventUpdateSummary extends StateMachineNegotiationEvent {
  eventType: 'NegotiationEventUpdateSummary';
  negotiationId: string;
  triggeringEvent: StateMachineNegotiationEvent;
  triggeringNegotiationDetailItem: any | null;
  triggeringNegotiationDetailItemOrElseThrow: any | null;
}

export interface NegotiationEventUpdateTotalFill extends StateMachineNegotiationEvent {
  eventType: 'NegotiationEventUpdateTotalFill';
  negotiationId: string;
  notional: number;
  startConsideration: number;
}

export interface NegotiationEventValidate extends StateMachineNegotiationEvent {
  eventType: 'NegotiationEventValidate';
  negotiationId: string;
}

export interface NegotiationEventWithdraw extends StateMachineNegotiationEvent {
  eventType: 'NegotiationEventWithdraw';
  negotiationId: string;
}

export interface NegotiationHistory {
  accruedInterest: number | null;
  allowPartial: boolean;
  assetType: AssetType | null;
  axeId: string | null;
  axed: boolean;
  basketAllocation: BasketAllocation | null;
  basketId: string | null;
  batchId: string | null;
  blockTrade: boolean;
  bondPrice: number;
  bundle: string | null;
  calculatedStartConsideration: number | null;
  callablePeriodDays: number | null;
  callableType: CallableType;
  canSendBackToStaging: boolean;
  childBasketIds: string[];
  childCollateralIds: string[];
  childFundIds: string[];
  childNegotiationHistoryIds: NegotiationVersionedId[];
  clearingHouseId: string | null;
  collateralId: string | null;
  collectionId: string | null;
  competitionCount: number | null;
  containerCollectionId: string | null;
  counterpartyId: string;
  counterpartyPrimaryCoverUserId: string | null;
  counterpartySecondaryCoverUserId: string | null;
  counterpartySponsorId: string | null;
  createdAt: DateLike;
  createdBy: string;
  creatingCorrelationId: string;
  csd: DealSettlement | null;
  currency: Currency;
  direction: Direction | null;
  endConsideration: number | null;
  endDate: DateLike;
  euAmount: number;
  externalNegotiationId: string | null;
  externalOrderId: string | null;
  externalQuoteId: string | null;
  externalQuoteResponseId: string | null;
  externalSummaryNegotiationId: string | null;
  factor: number | null;
  fee: number | null;
  floatCrystallisationDays: number | null;
  fundId: string;
  fundPrimaryCoverUserId: string | null;
  fundSecondaryCoverUserId: string | null;
  haircut: number;
  haircutType: HaircutType | null;
  id: string;
  initiatorBook: string | null;
  initiatorExecutionDecisionMaker: string;
  initiatorInvestmentDecisionMaker: string;
  initiatorOrganisationId: string | null;
  initiatorStrategy: string | null;
  internalAxeId: string | null;
  isSponsorCounterparty: boolean | null;
  lastEvent: NegotiationEvent | null;
  lastInitiatorId: string | null;
  lastInitiatorInitials: string | null;
  lastResponderId: string | null;
  lastResponderInitials: string | null;
  lastTriggerEvent: NegotiationEvent | null;
  lastUpdatedAt: DateLike;
  lastUpdatedAtSystem: DateLike;
  lastUpdatedBy: string;
  lastUpdatedThroughFix: boolean | null;
  latest: boolean;
  market: Market;
  marketIdentifierCode: MicCode;
  marketTradeDate: DateLike | null;
  marketType: MarketType;
  masterLegalAgreementType: MasterLegalAgreementType | null;
  negotiationId: string;
  negotiationType: NegotiationType;
  notesId: string | null;
  noticePeriodDays: number | null;
  notional: number;
  parentOrderId: string;
  preAgreed: boolean;
  productId: string | null;
  productSectorId: string | null;
  quotedAtLeastOnce: boolean | null;
  rate: number;
  referenceRate: string | null;
  refresh: boolean;
  repoClassification: RepoClassification;
  repoInterest: number | null;
  repoType: RepoType;
  responderBook: string | null;
  responderExecutionDecisionMaker: string | null;
  responderInvestmentDecisionMaker: string | null;
  responderStrategy: string | null;
  rfqEndTime: DateLike;
  rfqStartTime: DateLike;
  rfqType: RfqType;
  rightOfExit: number | null;
  rightOfSubstitution: number | null;
  secondaryBasketId: string | null;
  secondaryCollateralId: string | null;
  sentBackToStaging: boolean;
  settlementVenue: SettlementVenue;
  sponsorId: string | null;
  startConsideration: number;
  startConsiderationOverride: boolean | null;
  startDate: DateLike;
  status: NegotiationStatus;
  stockBorrowingLendingType: StockBorrowingLendingType | null;
  summaryNegotiationId: string | null;
  summaryNegotiationVersionedId: NegotiationVersionedId | null;
  term: OrderTerm;
  tradeConditionFlag: TradeConditionFlag;
  tradedAt: DateLike;
  usAmount: number;
  version: number;
}

export interface NegotiationHistoryUpdate extends EntityUpdate, EntityUpdateWithSide {
  entity: NegotiationHistory;
  entityType: 'NEGOTIATION_HISTORY';
  negotiationSide: NegotiationSide;
}

export interface NegotiationHistoryWithoutStatus {
  accruedInterest: number | null;
  allowPartial: boolean;
  assetType: AssetType | null;
  axeId: string | null;
  axed: boolean;
  basketAllocation: BasketAllocation | null;
  basketId: string | null;
  batchId: string | null;
  blockTrade: boolean;
  bondPrice: number;
  bundle: string | null;
  calculatedStartConsideration: number | null;
  callablePeriodDays: number | null;
  callableType: CallableType;
  canSendBackToStaging: boolean;
  childBasketIds: string[];
  childCollateralIds: string[];
  childFundIds: string[];
  childNegotiationHistoryIds: NegotiationVersionedId[];
  clearingHouseId: string | null;
  collateralId: string | null;
  collectionId: string | null;
  competitionCount: number | null;
  containerCollectionId: string | null;
  counterpartyId: string;
  counterpartyPrimaryCoverUserId: string | null;
  counterpartySecondaryCoverUserId: string | null;
  counterpartySponsorId: string | null;
  createdAt: DateLike;
  createdBy: string;
  creatingCorrelationId: string;
  csd: DealSettlement | null;
  currency: Currency;
  direction: Direction | null;
  endConsideration: number | null;
  endDate: DateLike;
  euAmount: number;
  externalNegotiationId: string | null;
  externalOrderId: string | null;
  externalQuoteId: string | null;
  externalQuoteResponseId: string | null;
  externalSummaryNegotiationId: string | null;
  factor: number | null;
  fee: number | null;
  floatCrystallisationDays: number | null;
  fundId: string;
  fundPrimaryCoverUserId: string | null;
  fundSecondaryCoverUserId: string | null;
  haircut: number;
  haircutType: HaircutType | null;
  id: string;
  initiatorBook: string | null;
  initiatorExecutionDecisionMaker: string;
  initiatorInvestmentDecisionMaker: string;
  initiatorOrganisationId: string | null;
  initiatorStrategy: string | null;
  internalAxeId: string | null;
  isSponsorCounterparty: boolean | null;
  lastEvent: NegotiationEvent | null;
  lastInitiatorId: string | null;
  lastResponderId: string | null;
  lastTriggerEvent: NegotiationEvent | null;
  lastUpdatedAt: DateLike;
  lastUpdatedAtSystem: DateLike;
  lastUpdatedBy: string;
  lastUpdatedThroughFix: boolean | null;
  latest: boolean;
  market: Market;
  marketIdentifierCode: MicCode;
  marketTradeDate: DateLike | null;
  marketType: MarketType;
  masterLegalAgreementType: MasterLegalAgreementType | null;
  negotiationId: string;
  negotiationType: NegotiationType;
  notesId: string | null;
  noticePeriodDays: number | null;
  notional: number;
  parentOrderId: string;
  preAgreed: boolean;
  productId: string | null;
  productSectorId: string | null;
  quotedAtLeastOnce: boolean | null;
  rate: number;
  referenceRate: string | null;
  refresh: boolean;
  repoClassification: RepoClassification;
  repoInterest: number | null;
  repoType: RepoType;
  responderBook: string | null;
  responderExecutionDecisionMaker: string | null;
  responderInvestmentDecisionMaker: string | null;
  responderStrategy: string | null;
  rfqEndTime: DateLike;
  rfqStartTime: DateLike;
  rfqType: RfqType;
  rightOfExit: number | null;
  rightOfSubstitution: number | null;
  secondaryBasketId: string | null;
  secondaryCollateralId: string | null;
  sentBackToStaging: boolean;
  settlementVenue: SettlementVenue;
  sponsorId: string | null;
  startConsideration: number;
  startConsiderationOverride: boolean | null;
  startDate: DateLike;
  status: NegotiationStatus;
  stockBorrowingLendingType: StockBorrowingLendingType | null;
  summaryNegotiationId: string | null;
  summaryNegotiationVersionedId: NegotiationVersionedId | null;
  term: OrderTerm;
  tradeConditionFlag: TradeConditionFlag;
  tradedAt: DateLike;
  usAmount: number;
  version: number;
}

export interface NegotiationInstruction {
  clearingHouseId: string | null;
  clearingType: TradingEntityClearingType | null;
  counterpartyInstructions: CounterpartyInstruction[];
  csd: DealSettlement | null;
  externalNegotiationId: string | null;
  externalSummaryNegotiationId: string | null;
  fixExpireTime: DateLike | null;
  hideCompetition: boolean | null;
  rfqDurationMins: number;
  settlementVenue: SettlementVenue | null;
  sponsorId: string | null;
}

export interface NegotiationMeta {
  childBasketIds: string[];
  childCollateralIds: string[];
  childFundIds: string[];
  childNegotiationIds: string[];
  childSalesCoverIds: string[];
  childSecondaryBasketIds: string[];
  euAmount: number;
  usAmount: number;
}

export interface NegotiationOverride {
  modified: boolean;
  negotiationId: string;
  negotiationStatus: NegotiationStatus;
  newVersionParent: number;
  newVersionSide: number;
  overriddenBySide: NegotiationSide;
  overriddenSide: OverriddenNegotiationSide;
  overriddenVersionParent: number;
  overriddenVersionSide: number;
  summaryEveryLegModified: boolean;
  updatedValues: { [P in NegotiationFieldMeta]?: any };
  userHasUpdatedNotionalOrCash: boolean;
  userIdWithLock: string | null;
}

export interface NegotiationOverrideUpdate extends EntityUpdate, EntityUpdateWithSide {
  entity: NegotiationOverride;
  entityType: 'NEGOTIATION_OVERRIDE';
  initiatorAvailableEvents: NegotiationEvent[];
  lastUpdatedAt: DateLike;
  lastUpdatedById: string;
  negotiationSide: NegotiationSide;
  responderWithLockAvailableEvents: NegotiationEvent[];
  responderWithoutLockAvailableEvents: NegotiationEvent[];
  validationIssues: ValidationIssue[];
}

export interface NegotiationTags {
  executionDecisionMaker: string;
  investmentDecisionMaker: string;
}

export interface NegotiationUpdate extends EntityUpdate, EntityUpdateWithSide {
  entity: Negotiation;
  entityType: 'NEGOTIATION';
  initiatorAvailableEvents: NegotiationEvent[];
  negotiationSide: NegotiationSide;
  responderWithLockAvailableEvents: NegotiationEvent[];
  responderWithoutLockAvailableEvents: NegotiationEvent[];
  validationIssues: ValidationIssue[];
}

export interface NegotiationUpdateAttributes {
  meta: NegotiationUpdateAttributesMeta | null;
  negotiationId: string;
  updatedRawValues: { [P in NegotiationFieldMeta]?: any };
  updatedValues: { [P in NegotiationFieldMeta]?: any };
}

export interface NegotiationUpdateAttributesMeta {
  haircutSign: HaircutSign | null;
}

export interface NegotiationUserModifiedAttributes {
  bondPriceNegotiated: number;
  counterpartySponsorIdNegotiated: string | null;
  endDateNegotiated: DateLike;
  externalQuoteId: string | null;
  haircutNegotiated: number;
  haircutTypeNegotiated: HaircutType | null;
  initiatorExecutionDecisionMakerNegotiated: string | null;
  initiatorInvestmentDecisionMakerNegotiated: string | null;
  isAxedNegotiated: boolean;
  notionalNegotiated: number;
  preAgreedNegotiated: boolean | null;
  rateNegotiated: number;
  responderExecutionDecisionMakerNegotiated: string | null;
  responderInvestmentDecisionMakerNegotiated: string | null;
  rfqTypeNegotiated: RfqType;
  startConsiderationNegotiated: number;
  startConsiderationOverrideNegotiated: boolean | null;
  startDateNegotiated: DateLike;
  termNegotiated: OrderTerm;
  tradeConditionFlagNegotiated: TradeConditionFlag;
}

export interface NegotiationVersionedId {
  id: string;
  negotiationId: string;
  version: number;
}

export interface NegotiationWithoutStatus {
  absoluteConsideration: number | null;
  accruedInterest: number | null;
  accruedInterestAmount: number | null;
  allInPrice: number | null;
  allowPartial: boolean;
  assetType: AssetType | null;
  axeId: string | null;
  basisConvention: BasisConvention | null;
  basketAllocation: BasketAllocation | null;
  basketId: string | null;
  blockTrade: boolean;
  bondPrice: NegotiableFieldRequired<number>;
  bundle: string | null;
  calculatedStartConsideration: number | null;
  callablePeriodDays: number | null;
  callableType: CallableType;
  canSendBackToStaging: boolean;
  cleanPrice: number | null;
  clearingHouseId: string | null;
  clearingType: TradingEntityClearingType | null;
  collateralId: string | null;
  collateralIssue: Issue | null;
  collectionId: string | null;
  comments: string | null;
  competitionCount: number | null;
  containerCollectionId: string | null;
  counterpartyAlias: string | null;
  counterpartyClearingType: TradingEntityClearingType | null;
  counterpartyIdNegotiableField: NegotiableFieldRequired<string>;
  counterpartyPrimaryCoverUserId: string | null;
  counterpartySecondaryCoverUserId: string | null;
  counterpartySponsorId: NegotiableFieldOptional<string>;
  counterpartyTraderId: string | null;
  csd: DealSettlement | null;
  currency: Currency;
  dateSource: DateSource | null;
  dealId: string | null;
  direction: Direction | null;
  dirtyPrice: number | null;
  endConsideration: number | null;
  endDate: NegotiableFieldRequired<DateLike>;
  evergreenNoticePeriod: EvergreenNoticePeriod | null;
  externalNegotiationId: string | null;
  externalOrderId: string | null;
  externalQuoteId: string | null;
  externalQuoteResponseId: string | null;
  externalSummaryNegotiationId: string | null;
  factor: number | null;
  fee: NegotiableFieldOptional<number> | null;
  floatCrystallisationDays: number | null;
  fundId: string;
  fundPrimaryCoverUserId: string | null;
  fundSecondaryCoverUserId: string | null;
  haircut: NegotiableFieldOptional<number>;
  haircutType: NegotiableFieldOptional<HaircutType>;
  hideCompetition: boolean | null;
  indexRatio: number | null;
  initiatorBook: NegotiableFieldOptional<string>;
  initiatorExecutionDecisionMaker: NegotiableFieldRequired<string>;
  initiatorInvestmentDecisionMaker: NegotiableFieldRequired<string>;
  initiatorOrganisationId: string;
  initiatorPrimeBrokerId: string | null;
  initiatorStrategy: NegotiableFieldOptional<string>;
  internalAxeId: string | null;
  isAxed: NegotiableFieldRequired<boolean>;
  isCurve: boolean;
  isSponsorCounterparty: boolean | null;
  lastQuoted: number | null;
  market: Market;
  marketIdentifierCode: MicCode;
  marketTradeDate: DateLike;
  marketType: MarketType;
  masterLegalAgreementType: MasterLegalAgreementType | null;
  modified: boolean;
  negotiationId: string;
  negotiationMeta: NegotiationMeta;
  negotiationType: NegotiationType;
  nestedLegs: NestedLegs | null;
  notesId: string | null;
  noticePeriodDays: number | null;
  notional: NegotiableFieldRequired<number>;
  otwEndTime: DateLike | null;
  otwStartTime: DateLike | null;
  parentOrderId: string;
  positionId: string | null;
  preAgreed: NegotiableFieldOptional<boolean>;
  preAssigned: boolean | null;
  productId: string | null;
  productSectorId: string | null;
  rate: NegotiableFieldOptional<number>;
  referenceRate: string | null;
  repoClassification: RepoClassification;
  repoInterest: number | null;
  repoType: RepoType | null;
  responderBook: NegotiableFieldOptional<string>;
  responderExecutionDecisionMaker: NegotiableFieldOptional<string>;
  responderInvestmentDecisionMaker: NegotiableFieldOptional<string>;
  responderStrategy: NegotiableFieldOptional<string>;
  rfqEndTime: DateLike;
  rfqStartTime: DateLike;
  rfqType: NegotiableFieldRequired<RfqType>;
  rightOfExit: number;
  rightOfSubstitution: number;
  secondaryBasketId: NegotiableFieldOptional<string> | null;
  secondaryCollateralId: NegotiableFieldOptional<string> | null;
  sentBackToStaging: boolean;
  servicingTrigger: ServicingTrigger | null;
  settlementVenueNegotiable: NegotiableFieldRequired<SettlementVenue>;
  sponsorId: string | null;
  startConsideration: NegotiableFieldOptional<number>;
  startConsiderationOverride: NegotiableFieldRequired<boolean>;
  startDate: NegotiableFieldRequired<DateLike>;
  stockBorrowingLendingType: NegotiableFieldOptional<StockBorrowingLendingType> | null;
  summaryEveryChildLegModified: boolean;
  summaryModified: boolean;
  summaryNegotiationId: string | null;
  targetFillNotional: number | null;
  targetFillStartConsideration: number | null;
  term: NegotiableFieldRequired<OrderTerm>;
  totalFillNotional: number | null;
  totalFillStartConsideration: number | null;
  tradeClassification: TradeClassification;
  tradeConditionFlag: NegotiableFieldRequired<TradeConditionFlag>;
  traderUserId: string | null;
  tradingType: TradingType;
  uploadRowIndex: string | null;
  userHasUpdatedNotionalOrCash: boolean | null;
  userIdWithLock: string | null;
  userModified: boolean;
}

export interface NestedLegItem {
  dealId: string | null;
  negotiationFields: { [P in NegotiationFieldMeta]?: NegotiationField };
  validationIssues: ValidationIssue[] | null;
}

export interface NestedLegs {
  legs: NestedLegItem[];
}

export interface Notes {
  createdAt: DateLike;
  id: string;
  initiatorOrganisationId: string;
  values: { [index: string]: string };
}

export interface NotesUpdate extends EntityUpdate {
  availableEvents: CrudEventEnum[];
  entity: Notes;
  entityType: 'NOTES';
}

export interface Order {
  accruedInterest: number | null;
  accruedInterestAmount: number | null;
  allInPrice: number | null;
  allowPartial: boolean;
  assetType: AssetType | null;
  basisConvention: BasisConvention | null;
  basketAllocation: BasketAllocation | null;
  blockTrade: boolean | null;
  bondPrice: number | null;
  book: string | null;
  bundle: string | null;
  calculatedStartConsideration: number | null;
  callablePeriodDays: number | null;
  callableType: CallableType;
  cleanPrice: number | null;
  collateralId: string | null;
  collateralIssue: Issue | null;
  collectionId: string | null;
  complianceStatus: ComplianceStatus;
  containerCollectionId: string | null;
  createdAt: DateLike;
  createdBy: string;
  creatingCorrelationId: string;
  currency: Currency | null;
  dateSource: DateSource | null;
  direction: Direction | null;
  dirtyPrice: number | null;
  endConsideration: number | null;
  endDate: DateLike | null;
  evergreenNoticePeriod: EvergreenNoticePeriod | null;
  executionDecisionMaker: string | null;
  externalOrderId: string | null;
  factor: number | null;
  fee: number | null;
  floatCrystallisationDays: number | null;
  fundId: string | null;
  haircut: number | null;
  haircutType: HaircutType | null;
  indexRatio: number | null;
  initiatorOrganisationId: string;
  investmentDecisionMaker: string | null;
  lastUpdatedAt: DateLike | null;
  lastUpdatedBy: string | null;
  market: Market | null;
  marketIdentifierCode: MicCode | null;
  marketTradeDate: DateLike | null;
  marketType: MarketType | null;
  masterLegalAgreementType: MasterLegalAgreementType | null;
  negotiationInstruction: NegotiationInstruction;
  nestedLegs: NestedLegs | null;
  notes: { [index: string]: string } | null;
  noticePeriodDays: number | null;
  notional: number | null;
  orderId: string;
  originalOrderId: string | null;
  parentOrderId: string | null;
  positionId: string | null;
  preAgreed: boolean;
  primeBrokerId: string | null;
  productId: string | null;
  productSectorId: string | null;
  rate: number | null;
  referenceRate: string | null;
  repoClassification: RepoClassification | null;
  repoInterest: number | null;
  repoType: RepoType | null;
  rfqEndTime: DateLike | null;
  rfqStartTime: DateLike | null;
  rfqType: RfqType;
  rightOfExit: number;
  rightOfSubstitution: number;
  secondaryBasketId: string | null;
  secondaryCollateralId: string | null;
  servicingTrigger: ServicingTrigger | null;
  source: OrderAttributesCreateSource;
  startConsideration: number | null;
  startConsiderationOverride: boolean;
  startDate: DateLike | null;
  status: OrderStatus;
  stockBorrowingLendingType: StockBorrowingLendingType | null;
  strategy: string | null;
  targetFillNotional: number | null;
  targetFillStartConsideration: number | null;
  term: OrderTerm | null;
  tradeClassification: TradeClassification;
  tradingType: TradingType;
  uploadRowIndex: string | null;
  version: number;
}

export interface OrderAttributes {
  collectionId: string | null;
  collectionName: string | null;
  collectionType: CollectionType | null;
  negotiationInstruction: NegotiationInstruction | null;
  orderCounterparties: OrderCounterpartyAttributes[];
  orderUserEnteredAttributes: OrderUserEnteredAttributes;
  source: OrderAttributesCreateSource;
}

export interface OrderCounterpartyAttributes {
  axeId: string | null;
  axed: string | null;
  counterpartyName: string | null;
  counterpartyOrganisationShortCode: string | null;
  internalAxeId: string | null;
  masterLegalAgreementType: MasterLegalAgreementType | null;
  primeBrokerId: string | null;
  repoType: RepoType | null;
  source: string | null;
  tradeConditionFlag: TradeConditionFlag;
}

export interface OrderCounterpartyInstructions {
  counterpartyInstructions: CounterpartyInstruction[];
  orderId: string;
}

export interface OrderEventAddPreAssignment extends StateMachineOrderEvent {
  eventType: 'OrderEventAddPreAssignment';
  fundId: string;
  orderId: string;
}

export interface OrderEventCancel extends StateMachineOrderEvent {
  eventType: 'OrderEventCancel';
  orderId: string;
}

export interface OrderEventClone extends StateMachineOrderEvent {
  eventType: 'OrderEventClone';
  originalOrderId: string | null;
  uploadRowIndex: string | null;
}

export interface OrderEventCloneBulk extends ClientMessageEventPayload {
  eventType: 'OrderEventCloneBulk';
  orderIds: string[];
}

export interface OrderEventCreate extends StateMachineOrderEvent {
  allowPartial: boolean | null;
  backToStagingTradingType: TradingType | null;
  basketId: string | null;
  blockTrade: boolean | null;
  bondPrice: number | null;
  book: string | null;
  bundle: string | null;
  callablePeriodDays: number | null;
  callableType: CallableType | null;
  clearingHouseId: string | null;
  collateralId: string | null;
  collectionId: string | null;
  containerCollectionId: string | null;
  csd: DealSettlement | null;
  currency: Currency | null;
  dateSource: DateSource | null;
  direction: Direction | null;
  endDate: DateLike | null;
  endDateTerm: DurationTerm | null;
  endDateTerms: DurationTerm[] | null;
  endDates: DateLike[] | null;
  eventType: 'OrderEventCreate';
  evergreenNoticePeriod: EvergreenNoticePeriod | null;
  executionDecisionMaker: string | null;
  externalOrderId: string | null;
  fee: number | null;
  fundId: string | null;
  fundShortName: string | null;
  haircut: number | null;
  haircutType: HaircutType | null;
  investmentDecisionMaker: string | null;
  masterLegalAgreementType: MasterLegalAgreementType | null;
  notes: { [index: string]: string } | null;
  noticePeriodDays: number | null;
  notional: number | null;
  orderCounterparties: OrderCounterpartyAttributes[];
  parentOrderId: string | null;
  performUserValidations: boolean | null;
  positionId: string | null;
  preAgreed: boolean | null;
  primeBrokerId: string | null;
  rate: number | null;
  referenceRate: string | null;
  repoType: RepoType | null;
  rfqType: RfqType | null;
  rightOfExit: number | null;
  rightOfSubstitution: number | null;
  secondaryBasketId: string | null;
  secondaryCollateralId: string | null;
  servicingTrigger: ServicingTrigger | null;
  settlementVenue: SettlementVenue | null;
  source: OrderAttributesCreateSource;
  sponsorId: string | null;
  startConsideration: number | null;
  startConsiderationOverride: boolean | null;
  startDate: DateLike | null;
  startDateTerm: DateTerm | null;
  stockBorrowingLendingType: StockBorrowingLendingType | null;
  strategy: string | null;
  targetFillNotional: number | null;
  targetFillStartConsideration: number | null;
  term: OrderTerm | null;
  toBeSubmitted: boolean | null;
  uploadRowIndex: string | null;
}

export interface OrderEventCreateBulk extends ClientMessageEventPayload {
  deleteCurrentOrdersInStaging: boolean;
  eventType: 'OrderEventCreateBulk';
  ignoreAxeId: boolean | null;
  ignoreClearAxeId: boolean;
  orderAttributes: OrderAttributes[];
  performUserValidations: boolean;
  spreadsheetContent: string | null;
}

export interface OrderEventCreateSingle extends ClientMessageEventPayload {
  deleteCurrentOrdersInStaging: boolean;
  eventType: 'OrderEventCreateSingle';
  ignoreAxeId: boolean | null;
  ignoreClearAxeId: boolean;
  orderAttributes: OrderAttributes;
  source: string;
  spreadsheetContent: string | null;
}

export interface OrderEventDelete extends StateMachineOrderEvent {
  eventType: 'OrderEventDelete';
  executionDecisionMaker: string | null;
  externalOrderCancelId: string | null;
  investmentDecisionMaker: string | null;
  orderId: string;
}

export interface OrderEventDeletePreAssignment extends StateMachineOrderEvent {
  assignmentIndex: number;
  eventType: 'OrderEventDeletePreAssignment';
  orderId: string;
}

export interface OrderEventDone extends StateMachineOrderEvent {
  eventType: 'OrderEventDone';
  orderId: string;
}

export interface OrderEventEnabledCollateral extends StateMachineOrderEvent {
  eventType: 'OrderEventEnabledCollateral';
  orderId: string;
}

export interface OrderEventExpired extends StateMachineOrderEvent {
  eventType: 'OrderEventExpired';
  orderId: string;
}

export interface OrderEventModify extends StateMachineOrderEvent {
  allowPartial: boolean | null;
  backToStagingTradingType: TradingType | null;
  basketId: string | null;
  blockTrade: boolean | null;
  bondPrice: number | null;
  book: string | null;
  bundle: string | null;
  callablePeriodDays: number | null;
  callableType: CallableType | null;
  clearingHouseId: string | null;
  collateralId: string | null;
  csd: DealSettlement | null;
  currency: Currency | null;
  dateSource: DateSource | null;
  direction: Direction | null;
  endDate: DateLike | null;
  endDateTerm: DurationTerm | null;
  endDateTerms: DurationTerm[] | null;
  endDates: DateLike[] | null;
  eventType: 'OrderEventModify';
  evergreenNoticePeriod: EvergreenNoticePeriod | null;
  executionDecisionMaker: string | null;
  externalOrderId: string | null;
  fee: number | null;
  fundId: string | null;
  fundShortName: string | null;
  haircut: number | null;
  haircutType: HaircutType | null;
  ignoreClearAxeId: boolean;
  investmentDecisionMaker: string | null;
  masterLegalAgreementType: MasterLegalAgreementType | null;
  negotiationInstruction: NegotiationInstruction;
  notes: { [index: string]: string } | null;
  noticePeriodDays: number | null;
  notional: number | null;
  orderId: string;
  parentOrderId: string | null;
  positionId: string | null;
  preAgreed: boolean | null;
  primeBrokerId: string | null;
  rate: number | null;
  referenceRate: string | null;
  repoType: RepoType | null;
  rfqType: RfqType | null;
  rightOfExit: number | null;
  rightOfSubstitution: number | null;
  secondaryBasketId: string | null;
  secondaryCollateralId: string | null;
  settlementVenue: SettlementVenue | null;
  sponsorId: string | null;
  startConsideration: number | null;
  startConsiderationOverride: boolean | null;
  startDate: DateLike | null;
  startDateTerm: DateTerm | null;
  stockBorrowingLendingType: StockBorrowingLendingType | null;
  strategy: string | null;
  targetFillNotional: number | null;
  targetFillStartConsideration: number | null;
  term: OrderTerm | null;
  uploadRowIndex: string | null;
}

export interface OrderEventModifyCollection extends StateMachineOrderEvent {
  collectionId: string | null;
  eventType: 'OrderEventModifyCollection';
  orderId: string;
  triggerPartialCollectionSubmit: boolean | null;
}

export interface OrderEventNegotiationAccepted extends StateMachineOrderEvent {
  acceptedNegotiationDetail: any;
  eventType: 'OrderEventNegotiationAccepted';
  orderId: string;
}

export interface OrderEventNegotiationCancelled extends StateMachineOrderEvent {
  eventType: 'OrderEventNegotiationCancelled';
  negotiationId: string;
  orderId: string;
}

export interface OrderEventNegotiationRejected extends StateMachineOrderEvent {
  eventType: 'OrderEventNegotiationRejected';
  negotiationId: string;
  orderId: string;
}

export interface OrderEventPrepare extends StateMachineOrderEvent {
  eventType: 'OrderEventPrepare';
  negotiationInstruction: NegotiationInstruction;
  orderId: string;
}

export interface OrderEventPrepareBulk extends ClientMessageEventPayload {
  eventType: 'OrderEventPrepareBulk';
  negotiationInstruction: NegotiationInstruction;
  orderCounterpartyInstructions: OrderCounterpartyInstructions[];
  submitOrder: boolean;
}

export interface OrderEventPrepareCollection extends StateMachineOrderEvent {
  collectionId: string;
  eventType: 'OrderEventPrepareCollection';
  forcedIsCurve: boolean | null;
  negotiationInstruction: NegotiationInstruction;
  orderCounterpartyInstructions: OrderCounterpartyInstructions[];
  submitOrder: boolean;
}

export interface OrderEventRefresh extends StateMachineOrderEvent {
  eventType: 'OrderEventRefresh';
  orderId: string;
}

export interface OrderEventSubmit extends StateMachineOrderEvent {
  batchId: string | null;
  eventType: 'OrderEventSubmit';
  forcedIsCurve: boolean | null;
  negotiationInstruction: NegotiationInstruction;
  orderId: string;
}

export interface OrderEventSubmitBulk extends ClientMessageEventPayload {
  collectionItems: CollectionItem[];
  eventType: 'OrderEventSubmitBulk';
  orderIds: string[];
}

export interface OrderEventUpdateAttributes extends StateMachineOrderEvent {
  eventType: 'OrderEventUpdateAttributes';
  orderId: string;
  updatedValues: { [P in NegotiationFieldMeta]?: any };
}

export interface OrderEventUpdatePreAssignmentAttributes extends StateMachineOrderEvent {
  assignmentIndex: number;
  eventType: 'OrderEventUpdatePreAssignmentAttributes';
  field: NegotiationFieldMeta;
  orderId: string;
  updatedValue: any;
}

export interface OrderEventUpdateValidateIssues extends StateMachineOrderEvent {
  action: Action;
  eventType: 'OrderEventUpdateValidateIssues';
  orderId: string;
  validationIssues: ValidationIssue[];
}

export interface OrderRecordKeeping {
  acceptedRfq: AcceptedValues | null;
  auditMarket: OrkMarket;
  basketTradeId: string | null;
  blockTradeId: string | null;
  bui: string | null;
  collectionType: CollectionTypeValues | null;
  csd: string | null;
  currency: Currency;
  cusip: string | null;
  dateOfReceipt: DateLike;
  direction: Direction;
  endDate: DateLike | null;
  endState: EndStateValues;
  event: EventValues;
  fee: number | null;
  haircut: number | null;
  haircutType: HaircutType | null;
  initialNotional: number;
  initialStartConsideration: number;
  initiatorOrResponderRecord: InitiatorOrResponderValues;
  instrumentType: InstrumentTypeValues;
  isAllocation: boolean;
  isAmended: boolean | null;
  isBlockAssignment: boolean;
  isBlockNegotiation: boolean;
  isBlockParent: boolean;
  isCleared: boolean;
  isGcParent: boolean;
  isLastLook: boolean | null;
  isin: string | null;
  lastUpdatedBy: string;
  masterLegalAgreement: MasterLegalAgreementType | null;
  mic: MicValues;
  negotiationId: string;
  negotiationTradingType: TradingType;
  notional: number;
  packageId: string | null;
  preAgreed: boolean | null;
  rate: number | null;
  receiverOrgLei: string;
  receiverTradingEntityLei: string;
  recordIdentifier: RecordIdentifier;
  referenceRate: string;
  repoType: RepoType | null;
  rfqId: string;
  rfqType: RfqTypeValues | null;
  sblType: StockBorrowingLendingType | null;
  secondaryCollateral: string | null;
  sendingEdm: string;
  sendingIdm: string;
  sendingOrgLei: string;
  sendingTime: DateLike;
  sendingTrader: string;
  sendingTradingEntityLei: string;
  servicingType: ServicingTypeValues | null;
  settlement: SettlementVenue;
  sponsor: string | null;
  startConsideration: number;
  startDate: DateLike;
  tradeId: string | null;
  validTime: DateLike | null;
}

export interface OrderRecordKeepingUpdate extends EntityUpdate {
  entity: OrderRecordKeeping;
  entityType: 'ORDER_RECORD_KEEPING';
}

export interface OrderUpdate extends EntityUpdate {
  availableEvents: OrderEvent[];
  entity: Order;
  entityType: 'ORDER';
  validationIssues: ValidationIssue[];
}

export interface OrderUserEnteredAttributes {
  allowPartial: boolean | null;
  backToStagingTradingType: TradingType | null;
  basketId: string | null;
  blockTrade: boolean | null;
  bondPrice: number | null;
  book: string | null;
  bundle: string | null;
  callablePeriodDays: number | null;
  callableType: CallableType | null;
  clearingHouseId: string | null;
  collateralId: string | null;
  csd: DealSettlement | null;
  currency: Currency | null;
  dateSource: DateSource | null;
  direction: Direction | null;
  endDate: DateLike | null;
  endDateTerm: DurationTerm | null;
  endDateTerms: DurationTerm[] | null;
  endDates: DateLike[] | null;
  evergreenNoticePeriod: EvergreenNoticePeriod | null;
  executionDecisionMaker: string | null;
  externalOrderId: string | null;
  fee: number | null;
  fundId: string | null;
  fundShortName: string | null;
  haircut: number | null;
  haircutType: HaircutType | null;
  investmentDecisionMaker: string | null;
  masterLegalAgreementType: MasterLegalAgreementType | null;
  notes: { [index: string]: string } | null;
  noticePeriodDays: number | null;
  notional: number | null;
  parentOrderId: string | null;
  positionId: string | null;
  preAgreed: boolean | null;
  primeBrokerId: string | null;
  rate: number | null;
  referenceRate: string | null;
  repoType: RepoType | null;
  rfqType: RfqType | null;
  rightOfExit: number | null;
  rightOfSubstitution: number | null;
  secondaryBasketId: string | null;
  secondaryCollateralId: string | null;
  settlementVenue: SettlementVenue | null;
  sponsorId: string | null;
  startConsideration: number | null;
  startConsiderationOverride: boolean | null;
  startDate: DateLike | null;
  startDateTerm: DateTerm | null;
  stockBorrowingLendingType: StockBorrowingLendingType | null;
  strategy: string | null;
  targetFillNotional: number | null;
  targetFillStartConsideration: number | null;
  term: OrderTerm | null;
  uploadRowIndex: string | null;
}

export interface OrderWithoutStatus {
  accruedInterest: number | null;
  accruedInterestAmount: number | null;
  allInPrice: number | null;
  allowPartial: boolean;
  assetType: AssetType | null;
  basisConvention: BasisConvention | null;
  basketAllocation: BasketAllocation | null;
  blockTrade: boolean | null;
  bondPrice: number | null;
  book: string | null;
  bundle: string | null;
  calculatedStartConsideration: number | null;
  callablePeriodDays: number | null;
  callableType: CallableType;
  cleanPrice: number | null;
  collateralId: string | null;
  collateralIssue: Issue | null;
  collectionId: string | null;
  complianceStatus: ComplianceStatus;
  containerCollectionId: string | null;
  currency: Currency | null;
  dateSource: DateSource | null;
  direction: Direction | null;
  dirtyPrice: number | null;
  endConsideration: number | null;
  endDate: DateLike | null;
  evergreenNoticePeriod: EvergreenNoticePeriod | null;
  executionDecisionMaker: string | null;
  externalOrderId: string | null;
  factor: number | null;
  fee: number | null;
  floatCrystallisationDays: number | null;
  fundId: string | null;
  haircut: number | null;
  haircutType: HaircutType | null;
  indexRatio: number | null;
  initiatorOrganisationId: string;
  investmentDecisionMaker: string | null;
  market: Market | null;
  marketIdentifierCode: MicCode | null;
  marketTradeDate: DateLike | null;
  marketType: MarketType | null;
  masterLegalAgreementType: MasterLegalAgreementType | null;
  negotiationInstruction: NegotiationInstruction;
  nestedLegs: NestedLegs | null;
  notes: { [index: string]: string } | null;
  noticePeriodDays: number | null;
  notional: number | null;
  orderId: string;
  originalOrderId: string | null;
  parentOrderId: string | null;
  positionId: string | null;
  preAgreed: boolean;
  primeBrokerId: string | null;
  productId: string | null;
  productSectorId: string | null;
  rate: number | null;
  referenceRate: string | null;
  repoClassification: RepoClassification | null;
  repoInterest: number | null;
  repoType: RepoType | null;
  rfqEndTime: DateLike | null;
  rfqStartTime: DateLike | null;
  rfqType: RfqType;
  rightOfExit: number;
  rightOfSubstitution: number;
  secondaryBasketId: string | null;
  secondaryCollateralId: string | null;
  servicingTrigger: ServicingTrigger | null;
  startConsideration: number | null;
  startConsiderationOverride: boolean;
  startDate: DateLike | null;
  stockBorrowingLendingType: StockBorrowingLendingType | null;
  strategy: string | null;
  targetFillNotional: number | null;
  targetFillStartConsideration: number | null;
  term: OrderTerm | null;
  tradeClassification: TradeClassification;
  tradingType: TradingType;
  uploadRowIndex: string | null;
}

export interface Organisation {
  enabledFeatures: PlatformFeature[];
  enabledOrganisationFeatures: OrganisationFeature[];
  gfidCodes: string[];
  id: string;
  legalName: string;
  lei: string;
  organisationTypes: OrganisationType[];
  shortCode: string;
  status: EntityStatus;
}

export interface OrganisationAggregate {
  enabledFeatures: PlatformFeature[];
  enabledOrganisationFeatures: OrganisationFeature[];
  gfidCodes: string[];
  id: string;
  ignorePrimeBrokersToDelete: boolean | null;
  legalName: string;
  lei: string;
  organisationTypes: OrganisationType[];
  primeBrokers: PrimeBroker[];
  shortCode: string;
  status: EntityStatus;
}

export interface OrganisationMeta {
  id: string;
  ownerOrganisationId: string;
  ownerTradingEntityId: string | null;
  primaryCoverUserId: string | null;
  productId: string | null;
  productSectorId: string | null;
  secondaryCoverUserId: string | null;
  splitTradeAmount: number | null;
  splitTrades: boolean;
  subjectOrganisationId: string | null;
  subjectTradingEntityId: string | null;
}

export interface OrganisationMetaUpdate extends EntityUpdate {
  availableEvents: CrudEventEnum[];
  entity: OrganisationMeta;
  entityType: 'ORGANISATION_META';
}

export interface OrganisationUpdate extends EntityUpdate {
  availableEvents: CrudEventEnum[];
  entity: Organisation;
  entityType: 'ORGANISATION';
}

export interface OrganisationUpsertEvent extends CrudUpsertEvent {
  entity: OrganisationAggregate;
  eventType: 'OrganisationUpsertEvent';
}

export interface PermissionReportRequest {
  correlationId: string;
  organisationId: string;
}

export interface PermissionReportRequestUpdate extends EntityUpdate {
  entity: PermissionReportRequest;
  entityType: 'PERMISSION_REPORT_REQUEST';
}

export interface Platform {
  applicationVersion: string | null;
  clientLogConfig: ClientLogConfig;
  clusterTimeOffset: string | null;
  enabledFeatures: PlatformFeature[];
  id: string;
  marketDetails: { [P in Market]?: MarketDetails };
  preMaturityDays: number;
  status: EntityStatus;
  version: string | null;
}

export interface PlatformUpdate extends EntityUpdate {
  availableEvents: CrudEventEnum[];
  entity: Platform;
  entityType: 'PLATFORM';
}

export interface PlatformUpsertEvent extends CrudUpsertEvent {
  entity: Platform;
  eventType: 'PlatformUpsertEvent';
}

export interface PrepareDealEvent extends StateMachineDealEvent {
  dealDetail: any;
  eventType: 'PrepareDealEvent';
}

export interface PrimeBroker {
  id: string;
  name: string;
  organisationId: string;
  status: EntityStatus;
}

export interface PrimeBrokerUpdate extends EntityUpdate {
  availableEvents: CrudEventEnum[];
  entity: PrimeBroker;
  entityType: 'PRIME_BROKER';
}

export interface Product {
  billingCurrency: Currency | null;
  id: string;
  market: Market;
  micCode: MicCode;
  name: string;
  secondaryBillingCurrency: Currency | null;
  secondaryMarket: Market;
  secondaryMicCode: MicCode;
  secondaryStartTime: DateAsString;
  secondaryStartTimeZone: StartTimeZone;
  secondaryTradingStatus: TradingStatus;
  startTime: DateAsString;
  startTimeZone: StartTimeZone;
  system: boolean;
  tradingStatus: TradingStatus;
}

export interface ProductRight {
  marketType: MarketType;
  permission: ProductPermission;
  productSectorId: string;
}

export interface ProductSector {
  basisConvention: BasisConvention | null;
  basketCurrency: Currency | null;
  basketType: CollateralBasketType | null;
  defaultFloatCrystallisationDaysByReferenceRateId: { [index: string]: number };
  defaultNoticePeriod: number | null;
  defaultNotificationDeadlineTime: DateAsString | null;
  defaultRepoType: RepoType;
  exDividendDayConvention: ExDividendDayConvention | null;
  exDividendDays: number | null;
  id: string;
  name: string;
  productId: string;
  startConsiderationOverrideThreshold: number;
  status: SectorStatus;
  supportedRepoTypes: RepoType[];
  system: boolean;
}

export interface ProductSectorMapping {
  assetSubType: string | null;
  assetType: AssetType | null;
  baseIndexNull: boolean | null;
  countryOfIncorporation: string | null;
  countryOfIssuance: string | null;
  currency: Currency | null;
  debtType: string | null;
  domicile: string | null;
  governmentBondType: string | null;
  hasCUSIP: boolean | null;
  id: string | null;
  issuerLei: string | null;
  notCountryOfIncorporation: string | null;
  notCountryOfIssuance: string | null;
  notCurrency: Currency | null;
  notDomicile: string | null;
  notGovernmentBondType: string | null;
  notIssuerLei: string | null;
  pendingFailed: boolean | null;
  productSectorId: string | null;
  refinitivAssetType: RefinitivAssetType | null;
  ruleName: string | null;
}

export interface ProductSectorMappingDetail {
  id: string;
  lastUpdatedAt: DateLike;
  lastUpdatedBy: string;
  productSectorMapping: ProductSectorMapping;
}

export interface ProductSectorMappingDraftDetail {
  id: string;
  lastUpdatedAt: DateLike;
  lastUpdatedBy: string;
  productSectorMapping: ProductSectorMapping;
  status: ProductSectorMappingStatus;
}

export interface ProductSectorMappingDraftUpdate extends EntityUpdate {
  availableEvents: CrudEventEnum[];
  entity: ProductSectorMappingDraftDetail;
  entityType: 'PRODUCT_SECTOR_MAPPING_DRAFT';
}

export interface ProductSectorMappingProductStateEventGenerateReport extends ProductStateMachineSectorMappingEvent {
  eventType: 'ProductSectorMappingProductStateEventGenerateReport';
  id: string;
}

export interface ProductSectorMappingProductStateEventRollback extends ProductStateMachineSectorMappingEvent {
  eventType: 'ProductSectorMappingProductStateEventRollback';
  id: string;
}

export interface ProductSectorMappingProductStateEventRuleChanged extends ProductStateMachineSectorMappingEvent {
  eventType: 'ProductSectorMappingProductStateEventRuleChanged';
  id: string;
  ruleId: string;
}

export interface ProductSectorMappingProductStateEventSubmit extends ProductStateMachineSectorMappingEvent {
  eventType: 'ProductSectorMappingProductStateEventSubmit';
  id: string;
}

export interface ProductSectorMappingState {
  id: string;
}

export interface ProductSectorMappingStateUpdate extends EntityUpdate {
  availableEvents: ProductSectorMappingStateEvent[];
  createdAt: DateLike | null;
  createdBy: string | null;
  entity: ProductSectorMappingState;
  entityType: 'PRODUCT_SECTOR_MAPPING_STATE';
  lastUpdatedAt: DateLike | null;
  lastUpdatedBy: string | null;
  status: ProductSectorMappingStateStatus;
}

export interface ProductSectorMappingUpdate extends EntityUpdate {
  availableEvents: CrudEventEnum[];
  entity: ProductSectorMappingDetail;
  entityType: 'PRODUCT_SECTOR_MAPPING';
}

export interface ProductSectorUpdate extends EntityUpdate {
  availableEvents: CrudEventEnum[];
  entity: ProductSector;
  entityType: 'PRODUCT_SECTOR';
}

export interface ProductSectorUpsertEvent extends CrudUpsertEvent {
  entity: ProductSector;
  eventType: 'ProductSectorUpsertEvent';
}

export interface ProductStateMachineSectorMappingEvent extends StateMachineEvent {
  eventType: 'ProductSectorMappingProductStateEventGenerateReport' | 'ProductSectorMappingProductStateEventRollback' | 'ProductSectorMappingProductStateEventRuleChanged' | 'ProductSectorMappingProductStateEventSubmit';
}

export interface ProductUpdate extends EntityUpdate {
  availableEvents: CrudEventEnum[];
  entity: Product;
  entityType: 'PRODUCT';
}

export interface ProductUpsertEvent extends CrudUpsertEvent {
  entity: Product;
  eventType: 'ProductUpsertEvent';
}

export interface QueryRequest {
  clusterClientSessionId: number | null;
  clusterRequest: boolean | null;
  historyServiceClientName: string | null;
  originatedFromCluster: boolean | null;
  originatedFromFix: boolean | null;
  queryGroupId: string | null;
  sessionId: number | null;
  subscribeMessage: SubscribeMessage | null;
}

export interface QueryRequestUpdate extends EntityUpdate {
  entity: QueryRequest;
  entityType: 'QUERY_REQUEST';
}

export interface Rate {
  rate: number | null;
  rateFromDate: DateLike | null;
}

export interface ReferenceDataManagementData {
  entitiesToDelete: EntityIdAndType[];
  internal: boolean;
  referenceDataEntities: EntityWrapper[];
  workflowId: string;
}

export interface ReferenceDataManagementDataUpdate extends EntityUpdate {
  availableEvents: ReferenceDataManagementEvent[];
  creatingCorrelationId: string;
  entity: ReferenceDataManagementData;
  entityType: 'REFERENCE_DATA_MANAGEMENT_DATA';
  lastCorrelationId: string;
  status: ReferenceDataManagementStatus;
  version: number;
}

export interface ReferenceDataManagementEventCancel extends StateMachineReferenceDataEvent {
  eventType: 'ReferenceDataManagementEventCancel';
  workflowId: string;
}

export interface ReferenceDataManagementEventCancelBulk extends ClientMessageEventPayload {
  eventType: 'ReferenceDataManagementEventCancelBulk';
  workflowIds: string[];
}

export interface ReferenceDataManagementEventCreateAndSubmit extends StateMachineReferenceDataEvent {
  eventType: 'ReferenceDataManagementEventCreateAndSubmit';
  force: boolean;
  referenceDataManagementData: ReferenceDataManagementData;
  throwErrorOnFailure: boolean;
}

export interface ReferenceDataManagementEventCreateClone extends StateMachineReferenceDataEvent {
  entityIdAndTypes: EntityIdAndType[];
  eventType: 'ReferenceDataManagementEventCreateClone';
}

export interface ReferenceDataManagementEventCreateEdit extends StateMachineReferenceDataEvent {
  entityIdAndTypes: EntityIdAndType[];
  eventType: 'ReferenceDataManagementEventCreateEdit';
}

export interface ReferenceDataManagementEventCreateNew extends StateMachineReferenceDataEvent {
  entityTypes: EntityTypeEnum[];
  eventType: 'ReferenceDataManagementEventCreateNew';
}

export interface ReferenceDataManagementEventDeleteAllOfType extends StateMachineReferenceDataEvent {
  entityType: EntityTypeEnum | null;
  eventType: 'ReferenceDataManagementEventDeleteAllOfType';
}

export interface ReferenceDataManagementEventEntityAdd extends StateMachineReferenceDataEvent {
  entityTypes: EntityTypeEnum[];
  eventType: 'ReferenceDataManagementEventEntityAdd';
  workflowId: string;
}

export interface ReferenceDataManagementEventEntityDelete extends StateMachineReferenceDataEvent {
  entitiesToDelete: EntityIdAndType[];
  eventType: 'ReferenceDataManagementEventEntityDelete';
  workflowId: string;
}

export interface ReferenceDataManagementEventEntityRevert extends StateMachineReferenceDataEvent {
  entitiesToRevert: EntityIdAndType[];
  eventType: 'ReferenceDataManagementEventEntityRevert';
  workflowId: string;
}

export interface ReferenceDataManagementEventEntityUpdate extends StateMachineReferenceDataEvent {
  entityIdAndType: EntityIdAndType;
  eventType: 'ReferenceDataManagementEventEntityUpdate';
  updatedEntity: any;
  workflowId: string;
}

export interface ReferenceDataManagementEventSubmit extends StateMachineReferenceDataEvent {
  eventType: 'ReferenceDataManagementEventSubmit';
  workflowId: string;
}

export interface ReferenceDataManagementEventSubmitBulk extends ClientMessageEventPayload {
  eventType: 'ReferenceDataManagementEventSubmitBulk';
  workflowIds: string[];
}

export interface ReferenceRate {
  currency: Currency;
  id: string;
  name: string;
}

export interface ReferenceRateUpdate extends EntityUpdate {
  availableEvents: CrudEventEnum[];
  entity: ReferenceRate;
  entityType: 'REFERENCE_RATE';
}

export interface ReferenceRateUpsertEvent extends CrudUpsertEvent {
  entity: ReferenceRate;
  eventType: 'ReferenceRateUpsertEvent';
}

export interface RefinitivCollateralImportResult {
  failedReason: string | null;
  imported: TermsAndConditionsResponseContents | null;
}

export interface RefinitivCollateralImportResultUpdate extends EntityUpdate {
  availableEvents: CrudEventEnum[];
  entity: RefinitivCollateralImportResult;
  entityType: 'COLLATERAL_REFINITIV_IMPORT_RESULT';
}

export interface Rerate extends ServicingTypeDetail {
  type: 'RERATE';
}

export interface ReturnRecall extends ServicingTypeDetail {
  type: 'RETURN_RECALL';
}

export interface Roll extends ServicingTypeDetail {
  type: 'ROLL';
}

export interface RollInPlace extends ServicingTypeDetail {
  type: 'ROLL_IN_PLACE';
}

export interface Security {
  name: string | null;
  productSectorId: string | null;
}

export interface ServerMessage {
  type: 'ENTITY_SNAPSHOT_BEGIN' | 'ENTITY_SNAPSHOT_END' | 'ENTITY_UPDATE' | 'ENTITY_DELETE' | 'ENTITY_REMOVE_SUBSCRIPTION_IDENTIFIERS' | 'EVENT_RESPONSE' | 'HISTORICAL_QUERY_RESPONSE' | 'ENTITY_SUBSCRIPTION_META' | 'HEARTBEAT' | 'ENTITY_STREAM_SNAPSHOTS_BEGIN' | 'ENTITY_STREAM_SNAPSHOTS_END';
}

export interface ServicingAdminCancel extends ServicingTypeDetail {
  type: 'SERVICING_ADMIN_CANCEL';
}

export interface ServicingAmend extends ServicingTypeDetail {
  type: 'SERVICING_AMEND';
}

export interface ServicingDetails {
  createdAt: DateLike;
  isActive: boolean;
  servicingInitiator: InitiatingActor;
  servicingInitiatorSideDetails: ServicingSideDetails;
  servicingResponderSideDetails: ServicingSideDetails;
  servicingType: ServicingTypeDetailUnion;
}

export interface ServicingSideDetails {
  lastUser: string | null;
  negotiationFields: { [P in NegotiationFieldMeta]?: NegotiationField };
  sideFields: { [P in NegotiationFieldMeta]?: NegotiationField };
  userWithLock: string | null;
  validationIssues: ValidationIssue[] | null;
}

export interface ServicingTraderAmend extends ServicingTypeDetail {
  type: 'SERVICING_TRADER_AMEND';
}

export interface ServicingTraderCancel extends ServicingTypeDetail {
  type: 'SERVICING_TRADER_CANCEL';
}

export interface ServicingTrigger {
  linked: NegotiableFieldRequired<boolean>;
  originalDealId: string | null;
  previousCounterpartyId: string | null;
  previousDealId: string | null;
  previousRate: number;
  servicingType: ServicingType | null;
}

export interface ServicingTypeDetail {
  servicingType: ServicingType | null;
  type: 'CLOSE_OPEN' | 'SUBSTITUTION' | 'TERMINATION' | 'RETURN_RECALL' | 'SERVICING_AMEND' | 'SERVICING_ADMIN_CANCEL' | 'SERVICING_TRADER_AMEND' | 'SERVICING_TRADER_CANCEL' | 'RERATE' | 'ROLL_IN_PLACE' | 'ROLL';
}

export interface SotwPosition {
  id: string | null;
  position: number | null;
  user: string | null;
}

export interface SotwReset {
  id: string | null;
}

export interface SotwResetUpdate extends EntityUpdate {
  entity: SotwReset;
  entityType: 'RESET_SOTW';
}

export interface SotwSentUpdate extends EntityUpdate {
  entity: SotwPosition;
  entityType: 'SOTW_SENT';
}

export interface Sponsor {
  clearingHouseIds: string[];
  id: string;
  lei: string;
  longName: string;
  publishedName: string;
}

export interface SponsorUpdate extends EntityUpdate {
  availableEvents: CrudEventEnum[];
  entity: Sponsor;
  entityType: 'SPONSOR';
}

export interface SpreadsheetMapping {
  accrualDate: ColumnMappingLocalDate;
  action: ColumnMappingEnum<MidTradeAction>;
  allInPrice: ColumnMappingDecimal;
  axe: ColumnMappingString;
  baseIndexValue: ColumnMappingString;
  basisConvention: ColumnMappingEnum<BasisConvention>;
  bic: ColumnMappingString;
  bondPrice: ColumnMappingString;
  book: ColumnMappingString;
  bundle: ColumnMappingString;
  buyOrganisation: ColumnMappingString;
  buyTradingEntity: ColumnMappingString;
  callablePeriodDays: ColumnMappingString;
  callableType: ColumnMappingEnum<CallableType>;
  centralBank: ColumnMappingString;
  cfi: ColumnMappingString;
  cleanPrice: ColumnMappingString;
  clearingHouse: ColumnMappingEnum<ClearingHouseType>;
  collateralType: ColumnMappingEnum<CollateralType>;
  collectionId: ColumnMappingString;
  collectionName: ColumnMappingString;
  collectionType: ColumnMappingEnum<CollectionType>;
  counterparty: ColumnMappingString;
  counterpartyName: ColumnMappingString;
  country: ColumnMappingString;
  coupon: ColumnMappingString;
  couponType: ColumnMappingEnum<RateType>;
  csd: ColumnMappingEnum<SpreadsheetCsd>;
  currency: ColumnMappingEnum<Currency>;
  cusip: ColumnMappingString;
  defaultAgreement: ColumnMappingEnum<MasterLegalAgreementType>;
  direction: ColumnMappingEnum<Direction>;
  effectiveDate: ColumnMappingLocalDate;
  eligible15a6: ColumnMappingString;
  eligibleCombinedRecFile: ColumnMappingString;
  endDate: ColumnMappingLocalDate;
  euGfidCode: ColumnMappingString;
  evergreenNoticePeriod: ColumnMappingString;
  externalId: ColumnMappingString;
  fee: ColumnMappingString;
  ficcShortName: ColumnMappingString;
  firstCouponDate: ColumnMappingLocalDate;
  firstSettlementDate: ColumnMappingLocalDate;
  fisn: ColumnMappingString;
  frequency: ColumnMappingEnum<CouponFrequency>;
  fund: ColumnMappingString;
  haircut: ColumnMappingString;
  haircutType: ColumnMappingEnum<HaircutType>;
  id: string;
  index: ColumnMappingString;
  isin: ColumnMappingString;
  issuer: ColumnMappingString;
  lag: ColumnMappingEnum<IndexLag>;
  lastSetAsDefault: DateLike | null;
  lei: ColumnMappingString;
  masterLegalAgreements: ColumnMappingEnum<MasterLegalAgreementType>;
  maturityDate: ColumnMappingLocalDate;
  name: string;
  negotiationHistoryFile: ColumnMappingString;
  negotiationId: ColumnMappingString;
  notes: { [index: string]: ColumnMappingString };
  notional: ColumnMappingDecimal;
  operatingCountry: ColumnMappingString;
  organisation: ColumnMappingString;
  permission: ColumnMappingEnum<TradingEntityProductPermission>;
  platform: ColumnMappingEnum<CollateralIdentifierType>;
  preAgreed: ColumnMappingString;
  primeBroker: ColumnMappingString;
  product: ColumnMappingString;
  publishedName: ColumnMappingString;
  rate: ColumnMappingString;
  referenceRate: ColumnMappingString;
  relationshipAction: ColumnMappingEnum<RelationshipAction>;
  repoType: ColumnMappingEnum<RepoType>;
  response: ColumnMappingEnum<MidTradeResponse>;
  rfqId: ColumnMappingString;
  rightOfExit: ColumnMappingString;
  rightOfSubstitution: ColumnMappingString;
  roll: ColumnMappingString;
  secondaryIsin: ColumnMappingString;
  sector: ColumnMappingString;
  sellOrganisation: ColumnMappingString;
  sellTradingEntity: ColumnMappingString;
  settlementVenue: ColumnMappingEnum<SpreadsheetSettlementVenue>;
  shortId: ColumnMappingString;
  shortName: ColumnMappingString;
  sponsor: ColumnMappingEnum<SponsorType>;
  startConsideration: ColumnMappingString;
  startDate: ColumnMappingLocalDate;
  strategy: ColumnMappingString;
  sub: ColumnMappingString;
  term: ColumnMappingString;
  ticketSize: ColumnMappingString;
  tradeId: ColumnMappingString;
  tradingEntityType: ColumnMappingEnum<ExtendedTradingEntityType>;
  tradingStatus: ColumnMappingEnum<EntityStatus>;
  tradingType: ColumnMappingEnum<TradingType>;
  type: SpreadsheetMappingType;
  unwind: ColumnMappingString;
  usGfidCode: ColumnMappingString;
  usSponsoredClearing: ColumnMappingString;
  usSponsoredClearingDefault: ColumnMappingString;
  userId: string;
  version: string;
}

export interface SpreadsheetMappingUpdate extends EntityUpdate {
  availableEvents: CrudEventEnum[];
  entity: SpreadsheetMapping;
  entityType: 'SPREADSHEET_MAPPING';
}

export interface SpreadsheetMappingUpsertEvent extends CrudUpsertEvent {
  entity: SpreadsheetMapping;
  eventType: 'SpreadsheetMappingUpsertEvent';
}

export interface StateMachineAxeEvent extends StateMachineEvent {
  eventType: 'AxeEventCancel' | 'AxeEventClone' | 'AxeEventCreate' | 'AxeEventCreateFromClone' | 'AxeEventStage' | 'AxeEventSend' | 'AxeEventUpdateAttributes';
}

export interface StateMachineCollectionEvent extends StateMachineEvent {
  eventType: 'CollectionEventAssignOrders' | 'CollectionEventCreate' | 'CollectionEventDelete' | 'CollectionEventModifyOrders' | 'CollectionEventModifyType' | 'CollectionEventRefresh' | 'CollectionEventSubmit';
}

export interface StateMachineDealEvent extends StateMachineEvent {
  eventType: 'BookPreparedDealEvent' | 'DealAllocateEvent' | 'DealAllocateLateWarningEvent' | 'DealAmendEvent' | 'DealAssignFundEvent' | 'DealCancelAllocationsEvent' | 'DealCancelAssignmentEvent' | 'DealCancelEvent' | 'DealClearingCancelResponseReceivedEvent' | 'DealClearingCancelSentEvent' | 'DealClearingCreateResponseReceivedEvent' | 'DealClearingCreateSentEvent' | 'DealClearingFailedEvent' | 'DealClearingFailedManuallyAcceptedEvent' | 'DealClearingFailedManuallyRejectedEvent' | 'DealClearingFailedRetryEvent' | 'DealCollateralAssignmentStartSaveAllocateEvent' | 'DealCompleteEvent' | 'DealExpireAllocationEvent' | 'DealExtendAllocationTimeEvent' | 'DealFundAssignmentStartSaveAssignmentEvent' | 'DealRecalculateEvent' | 'DealRefreshEvent' | 'DealSaveAllocationsEvent' | 'DealSaveFundAssignmentEvent' | 'DealServicingAcceptEvent' | 'DealServicingCancelEvent' | 'DealServicingRejectEvent' | 'DealServicingResponderLockEvent' | 'DealServicingResponderUnlockEvent' | 'DealServicingRevertAttributesEvent' | 'DealServicingSendEvent' | 'DealServicingUpdateAttributesEvent' | 'DealServicingUpdateRegulatoryAttributesEvent' | 'DealServicingWithdrawEvent' | 'DealServicingWorkflowCompleteEvent' | 'DealServicingWorkflowCancelEvent' | 'DealSettledEvent' | 'DealStartAllocationsEvent' | 'DealStartFundAssignmentEvent' | 'DealTradeEvent' | 'DealUpdateAllocationCollateralsEvent' | 'DealUpdateAvailableEventsEvent' | 'DealUpdateFundAssignmentReminderEvent' | 'PrepareDealEvent' | 'DealServicingStartAmendEvent' | 'DealServicingStartCloseOpenEvent' | 'DealServicingStartRerateEvent' | 'DealServicingStartReturnRecallEvent' | 'DealServicingStartRollInPlaceEvent' | 'DealServicingStartSubstituteEvent' | 'DealServicingStartTerminateEvent' | 'DealServicingStartAdminCancelEvent' | 'DealServicingStartAdminCloseOpenEvent' | 'DealServicingStartAdminRerateEvent' | 'DealServicingStartAdminTerminateEvent' | 'DealServicingStartAdminSubstitutionEvent' | 'DealServicingStartTraderAmendEvent' | 'DealServicingStartTraderCancelEvent' | 'DealServicingStartRollEvent';
}

export interface StateMachineEvent extends ClientMessageEventPayload {
  eventType: 'CompositeEvent' | 'AxeEventCancel' | 'AxeEventClone' | 'AxeEventCreate' | 'AxeEventCreateFromClone' | 'AxeEventStage' | 'AxeEventSend' | 'AxeEventUpdateAttributes' | 'BookPreparedDealEvent' | 'DealAllocateEvent' | 'DealAllocateLateWarningEvent' | 'DealAmendEvent' | 'DealAssignFundEvent' | 'DealCancelAllocationsEvent' | 'DealCancelAssignmentEvent' | 'DealCancelEvent' | 'DealClearingCancelResponseReceivedEvent' | 'DealClearingCancelSentEvent' | 'DealClearingCreateResponseReceivedEvent' | 'DealClearingCreateSentEvent' | 'DealClearingFailedEvent' | 'DealClearingFailedManuallyAcceptedEvent' | 'DealClearingFailedManuallyRejectedEvent' | 'DealClearingFailedRetryEvent' | 'DealCollateralAssignmentStartSaveAllocateEvent' | 'DealCompleteEvent' | 'DealExpireAllocationEvent' | 'DealExtendAllocationTimeEvent' | 'DealFundAssignmentStartSaveAssignmentEvent' | 'DealRecalculateEvent' | 'DealRefreshEvent' | 'DealSaveAllocationsEvent' | 'DealSaveFundAssignmentEvent' | 'DealServicingAcceptEvent' | 'DealServicingCancelEvent' | 'DealServicingRejectEvent' | 'DealServicingResponderLockEvent' | 'DealServicingResponderUnlockEvent' | 'DealServicingRevertAttributesEvent' | 'DealServicingSendEvent' | 'DealServicingUpdateAttributesEvent' | 'DealServicingUpdateRegulatoryAttributesEvent' | 'DealServicingWithdrawEvent' | 'DealServicingWorkflowCompleteEvent' | 'DealServicingWorkflowCancelEvent' | 'DealSettledEvent' | 'DealStartAllocationsEvent' | 'DealStartFundAssignmentEvent' | 'DealTradeEvent' | 'DealUpdateAllocationCollateralsEvent' | 'DealUpdateAvailableEventsEvent' | 'DealUpdateFundAssignmentReminderEvent' | 'PrepareDealEvent' | 'DealServicingStartAmendEvent' | 'DealServicingStartCloseOpenEvent' | 'DealServicingStartRerateEvent' | 'DealServicingStartReturnRecallEvent' | 'DealServicingStartRollInPlaceEvent' | 'DealServicingStartSubstituteEvent' | 'DealServicingStartTerminateEvent' | 'DealServicingStartAdminCancelEvent' | 'DealServicingStartAdminCloseOpenEvent' | 'DealServicingStartAdminRerateEvent' | 'DealServicingStartAdminTerminateEvent' | 'DealServicingStartAdminSubstitutionEvent' | 'DealServicingStartTraderAmendEvent' | 'DealServicingStartTraderCancelEvent' | 'DealServicingStartRollEvent' | 'CollectionEventAssignOrders' | 'CollectionEventCreate' | 'CollectionEventDelete' | 'CollectionEventModifyOrders' | 'CollectionEventModifyType' | 'CollectionEventRefresh' | 'CollectionEventSubmit' | 'NegotiationEventAccept' | 'NegotiationEventBackToStaging' | 'NegotiationEventCancel' | 'NegotiationEventCounterOffer' | 'NegotiationEventCounterOfferWithAttributes' | 'NegotiationEventExpire' | 'NegotiationEventInitiatorRevertAttributes' | 'NegotiationEventLock' | 'NegotiationEventLockQuote' | 'NegotiationEventMiss' | 'NegotiationEventOrderCancelled' | 'NegotiationEventQuote' | 'NegotiationEventQuoteWithAttributes' | 'NegotiationEventRefresh' | 'NegotiationEventReject' | 'NegotiationEventRequest' | 'NegotiationEventResponderRevertAttributes' | 'NegotiationEventTagAndInvoke' | 'NegotiationEventTagAndInvokeMany' | 'NegotiationEventUnlock' | 'NegotiationEventUpdateBestPrice' | 'NegotiationEventUpdateAttributes' | 'NegotiationEventUpdatePreAssignmentAttributes' | 'NegotiationEventUpdateRegulatoryAttributes' | 'NegotiationEventUpdateSummary' | 'NegotiationEventUpdateContainerSummary' | 'NegotiationEventUpdateTotalFill' | 'NegotiationEventWithdraw' | 'NegotiationEventValidate' | 'OrderEventAddPreAssignment' | 'OrderEventCancel' | 'OrderEventClone' | 'OrderEventCreate' | 'OrderEventDelete' | 'OrderEventDeletePreAssignment' | 'OrderEventDone' | 'OrderEventEnabledCollateral' | 'OrderEventExpired' | 'OrderEventModify' | 'OrderEventModifyCollection' | 'OrderEventNegotiationAccepted' | 'OrderEventNegotiationCancelled' | 'OrderEventNegotiationRejected' | 'OrderEventPrepare' | 'OrderEventPrepareCollection' | 'OrderEventRefresh' | 'OrderEventSubmit' | 'OrderEventUpdateAttributes' | 'OrderEventUpdatePreAssignmentAttributes' | 'OrderEventUpdateValidateIssues' | 'ReferenceDataManagementEventCancel' | 'ReferenceDataManagementEventCreateAndSubmit' | 'ReferenceDataManagementEventCreateEdit' | 'ReferenceDataManagementEventCreateClone' | 'ReferenceDataManagementEventCreateNew' | 'ReferenceDataManagementEventEntityAdd' | 'ReferenceDataManagementEventEntityDelete' | 'ReferenceDataManagementEventEntityRevert' | 'ReferenceDataManagementEventEntityUpdate' | 'ReferenceDataManagementEventSubmit' | 'ReferenceDataManagementEventDeleteAllOfType' | 'ProductSectorMappingProductStateEventGenerateReport' | 'ProductSectorMappingProductStateEventRollback' | 'ProductSectorMappingProductStateEventRuleChanged' | 'ProductSectorMappingProductStateEventSubmit';
}

export interface StateMachineNegotiationEvent extends StateMachineEvent {
  eventType: 'NegotiationEventAccept' | 'NegotiationEventBackToStaging' | 'NegotiationEventCancel' | 'NegotiationEventCounterOffer' | 'NegotiationEventCounterOfferWithAttributes' | 'NegotiationEventExpire' | 'NegotiationEventInitiatorRevertAttributes' | 'NegotiationEventLock' | 'NegotiationEventLockQuote' | 'NegotiationEventMiss' | 'NegotiationEventOrderCancelled' | 'NegotiationEventQuote' | 'NegotiationEventQuoteWithAttributes' | 'NegotiationEventRefresh' | 'NegotiationEventReject' | 'NegotiationEventRequest' | 'NegotiationEventResponderRevertAttributes' | 'NegotiationEventTagAndInvoke' | 'NegotiationEventTagAndInvokeMany' | 'NegotiationEventUnlock' | 'NegotiationEventUpdateBestPrice' | 'NegotiationEventUpdateAttributes' | 'NegotiationEventUpdatePreAssignmentAttributes' | 'NegotiationEventUpdateRegulatoryAttributes' | 'NegotiationEventUpdateSummary' | 'NegotiationEventUpdateContainerSummary' | 'NegotiationEventUpdateTotalFill' | 'NegotiationEventWithdraw' | 'NegotiationEventValidate';
  negotiationId: string | null;
  negotiationSide: NegotiationSide;
}

export interface StateMachineOrderEvent extends StateMachineEvent {
  eventType: 'OrderEventAddPreAssignment' | 'OrderEventCancel' | 'OrderEventClone' | 'OrderEventCreate' | 'OrderEventDelete' | 'OrderEventDeletePreAssignment' | 'OrderEventDone' | 'OrderEventEnabledCollateral' | 'OrderEventExpired' | 'OrderEventModify' | 'OrderEventModifyCollection' | 'OrderEventNegotiationAccepted' | 'OrderEventNegotiationCancelled' | 'OrderEventNegotiationRejected' | 'OrderEventPrepare' | 'OrderEventPrepareCollection' | 'OrderEventRefresh' | 'OrderEventSubmit' | 'OrderEventUpdateAttributes' | 'OrderEventUpdatePreAssignmentAttributes' | 'OrderEventUpdateValidateIssues';
}

export interface StateMachineReferenceDataEvent extends StateMachineEvent {
  eventType: 'ReferenceDataManagementEventCancel' | 'ReferenceDataManagementEventCreateAndSubmit' | 'ReferenceDataManagementEventCreateEdit' | 'ReferenceDataManagementEventCreateClone' | 'ReferenceDataManagementEventCreateNew' | 'ReferenceDataManagementEventEntityAdd' | 'ReferenceDataManagementEventEntityDelete' | 'ReferenceDataManagementEventEntityRevert' | 'ReferenceDataManagementEventEntityUpdate' | 'ReferenceDataManagementEventSubmit' | 'ReferenceDataManagementEventDeleteAllOfType';
}

export interface StringValue extends TypeSpecificField {
  type: 'string';
  value: string | null;
}

export interface SubscribeBatchMessage {
  clearExistingSubscriptions: boolean;
  correlationId: string;
  payload: SubscribeMessage[];
}

export interface SubscribeMessage {
  correlationId: string;
  entityTypeEnum: EntityTypeEnum;
  filters: SubscriptionFilter[];
  limitResults: boolean;
  messageWhenStateCorrupt: boolean;
  pageNumber: number | null;
  pageSize: number | null;
  postTransformFilters: SubscriptionFilter[];
  preDefinedFilter: PreDefinedFilter | null;
  subscribeType: SubscribeType;
  subscriptionIdentifier: string;
  summaryRequest: SummaryRequest | null;
}

export interface SubscriptionFilter {
  andFilter: SubscriptionFilter | null;
  calculatedValueType: CalculatedValueType | null;
  fieldName: string;
  filterOperation: FilterOperation;
  orFilter: SubscriptionFilter | null;
  value: any;
}

export interface Substitution extends ServicingTypeDetail {
  type: 'SUBSTITUTION';
}

export interface SummaryRequest {
  groupFields: string[] | null;
  sumFields: string[] | null;
}

export interface SummaryResponse {
  items: SummaryResponseItem[] | null;
}

export interface SummaryResponseItem {
  count: number | null;
  groupResults: { [index: string]: string } | null;
  sumResults: { [index: string]: number } | null;
}

export interface TaggedNegotiationEvent {
  event: StateMachineNegotiationEvent;
  negotiationTags: NegotiationTags;
}

export interface Termination extends ServicingTypeDetail {
  type: 'TERMINATION';
}

export interface TermsAndConditionsResponseContents extends IExtractionResponseContents {
  'Accrual Date': DateLike | null;
  'Asset Status': string | null;
  'Asset Status Effective Date': DateLike | null;
  'Asset SubType': string | null;
  'Asset Type': string | null;
  'Base Index': string | null;
  'Bond Type': string | null;
  'CFI Code': string | null;
  CUSIP: string | null;
  'Callable Flag': string | null;
  'Country of Incorporation': string | null;
  'Country of Issuance': string | null;
  'Country of Issuance Description': string | null;
  'Country of Risk - All': string | null;
  'Country of Risk - Primary': string | null;
  'Country of Risk - Ultimate Parent - All': string | null;
  'Country of Risk - Ultimate Parent - Primary': string | null;
  'Coupon Frequency': number | null;
  'Coupon Rate': number | null;
  'Coupon Rate Effective Date': DateLike | null;
  'Coupon Type': string | null;
  'Currency Code': string | null;
  'Day Count Code': number | null;
  'Debt Type': string | null;
  'Denomination Increment': number | null;
  Domicile: string | null;
  'Domicile Description': string | null;
  'ECB Indicator': string | null;
  'EEA Venue Eligible Flag': string | null;
  'End Of Month Payment Flag': string | null;
  'Ex-dividend Calendar': string | null;
  'Ex-dividend Rule': string | null;
  'Ex-dividend Value': string | null;
  Factor: string | null;
  'Factor Date': DateLike | null;
  'Financial Instrument Short Name': string | null;
  'First Coupon Date': DateLike | null;
  'Fitch Rating': string | null;
  'Float Index Type': string | null;
  'Float Multiplier': number | null;
  'Float Offset': number | null;
  'Floater Flag': string | null;
  'Floating Rate Fixing Date': DateLike | null;
  'Government Bond Type Code': string | null;
  ISIN: string | null;
  Identifier: string | null;
  IdentifierType: InstrumentIdentifierType | null;
  'In ESMA FIRDS': string | null;
  'Index Linked Bond Base Index': number | null;
  'Industry Sector Code': string | null;
  'Industry Sub-Sector Code': string | null;
  'Instrument Full Name - ESMA': string | null;
  'Is Secured Flag': string | null;
  'Issue Date': DateLike | null;
  'Issuer LEI': string | null;
  'Issuer OrgID': string | null;
  'Issuer Short Name': string | null;
  Lookback: number | null;
  'Maturity Date': DateLike | null;
  'MiFID Index Identifier': string | null;
  'MiFID Index Name': string | null;
  'MiFID Index Term': string | null;
  'Minimum Denomination': number | null;
  'Moodys Rating': string | null;
  'Multi-Step Float Flag': string | null;
  'Offering Type': number | null;
  'Par Value': number | null;
  'Perpetual Security Flag': string | null;
  'Private Placement Flag': string | null;
  'Prospectus Date': DateLike | null;
  RIC: string | null;
  'Refinitiv Classification Scheme': string | null;
  'Regulation S Indicator': string | null;
  'Rule 144a Indicator': string | null;
  'S&P Rating': string | null;
  'Security Description': string | null;
  'Seniority Code': string | null;
  'Seniority Type Code': string | null;
  Series: string | null;
  'Settlement Date Convention': string | null;
  'Sinkable Flag': string | null;
  Ticker: string | null;
  'Total Amount Issued': number | null;
  'Total Amount Outstanding': number | null;
  'UK Venue Eligible Flag': string | null;
  accrualDate: DateLike | null;
  assetStatus: string | null;
  assetStatusEffectiveDate: DateLike | null;
  assetSubType: string | null;
  assetType: string | null;
  baseIndex: string | null;
  bondType: string | null;
  callableFlag: string | null;
  cfiCode: string | null;
  countryOfIncorporation: string | null;
  countryOfIssuance: string | null;
  countryOfIssuanceDescription: string | null;
  countryOfRiskAll: string | null;
  countryOfRiskPrimary: string | null;
  countryOfRiskUltimateParentAll: string | null;
  countryOfRiskUltimateParentPrimary: string | null;
  couponFrequency: number | null;
  couponRate: number | null;
  couponRateEffectiveDate: DateLike | null;
  couponType: string | null;
  currencyCode: string | null;
  cusip: string | null;
  dayCountCode: number | null;
  debtType: string | null;
  denominationIncrement: number | null;
  domicile: string | null;
  domicileDescription: string | null;
  ecbIndicator: string | null;
  eeaVenueEligible: string | null;
  endOfMonthPaymentFlag: string | null;
  exDividendCalendar: string | null;
  exDividendRule: string | null;
  exDividendValue: string | null;
  factor: string | null;
  factorDate: DateLike | null;
  financialInstrumentShortName: string | null;
  firstCouponDate: DateLike | null;
  fitchRating: string | null;
  floatIndexType: string | null;
  floatMultiplier: number | null;
  floatOffset: number | null;
  floaterFlag: string | null;
  floatingRateFixingDate: DateLike | null;
  governmentBondType: string | null;
  inEsmaFirds: string | null;
  indexLinkedBondBaseIndex: number | null;
  industrySectorCode: string | null;
  industrySubSectorCode: string | null;
  instrumentFullNameEsma: string | null;
  isSecuredFlag: string | null;
  isin: string | null;
  issueDate: DateLike | null;
  issuerLEI: string | null;
  issuerOrgID: string | null;
  issuerShortName: string | null;
  lookBack: number | null;
  maturityDate: DateLike | null;
  mifidIndexIdentifier: string | null;
  mifidIndexName: string | null;
  mifidIndexTerm: string | null;
  minimumDenomination: number | null;
  moodysRating: string | null;
  multiStepFloatFlag: string | null;
  offeringType: number | null;
  parValue: number | null;
  perpetualSecurityFlag: string | null;
  privatePlacementFlag: string | null;
  prospectusDate: DateLike | null;
  refinitivClassificationScheme: string | null;
  regulationIndicator: string | null;
  ric: string | null;
  rule144aIndicator: string | null;
  sandPRating: string | null;
  securedFlag: string | null;
  securityDescription: string | null;
  seniorityCode: string | null;
  seniorityTypeCode: string | null;
  series: string | null;
  settlementDateConvention: string | null;
  sinkableFlag: string | null;
  ticker: string | null;
  totalAmountIssued: number | null;
  totalAmountOutstanding: number | null;
  ukVenueEligible: string | null;
}

export interface TradingControlLimit {
  available: number | null;
  displayAvailable: number | null;
  id: string;
  limit: number;
  organisationId: string;
  status: EntityStatus;
  utilisation: number;
}

export interface TradingControlLimitUpdate extends EntityUpdate {
  availableEvents: CrudEventEnum[];
  entity: TradingControlLimit;
  entityType: 'TRADING_CONTROL_LIMIT';
}

export interface TradingControlLimitUpsertEvent extends CrudUpsertEvent {
  entity: TradingControlLimit;
  eventType: 'TradingControlLimitUpsertEvent';
}

export interface TradingControlLimitUser {
  limitsByCurrency: { [P in Currency]?: TradingControlLimitUserCurrency };
  tradingLimitType: TradingLimitType;
}

export interface TradingControlLimitUserCurrency {
  limit: number;
}

export interface TradingControlLimitUserUtilisation {
  lastUpdateEntityId: string | null;
  lastUpdateTrigger: Trigger | null;
  userId: string;
  utilisationByCurrency: { [P in Currency]?: TradingControlLimitUserUtilisationCurrency };
  version: number | null;
}

export interface TradingControlLimitUserUtilisationCurrency {
  market: Market;
  utilisationDate: DateLike;
  utilisationGross: number;
  utilisationNet: number | null;
  utilisationNetBuy: number;
  utilisationNetSell: number;
}

export interface TradingControlLimitUserUtilisationUpdate extends EntityUpdate {
  availableEvents: CrudEventEnum[];
  entity: TradingControlLimitUserUtilisation;
  entityType: 'TRADING_CONTROL_LIMIT_USER_UTILISATION';
}

export interface TradingEntity {
  bic: string;
  centralBank: boolean;
  eligible15a6: boolean;
  eligibleCombinedRecFile: boolean;
  euGfidCode: string | null;
  ficcShortName: string | null;
  group: string;
  id: string;
  lei: string;
  negotiationHistoryFile: boolean;
  operatingCountry: string | null;
  organisationId: string;
  productRights: ProductRight[];
  publishedName: string;
  roundPriceToOneEighth: boolean;
  settlementVenue: string[];
  shortName: string;
  splitUsTrades: boolean;
  status: EntityStatus;
  tradingEntityTypes: TradingEntityType[];
  usGfidCode: string | null;
}

export interface TradingEntityClearing {
  clearingHouseId: string;
  id: string;
  productSectorIds: string[];
  sponsorId: string | null;
  status: EntityStatus;
  swiftMessageId: string;
  tradingEntityId: string;
  type: TradingEntityClearingType;
}

export interface TradingEntityClearingUpdate extends EntityUpdate {
  availableEvents: CrudEventEnum[];
  entity: TradingEntityClearing;
  entityType: 'TRADING_ENTITY_CLEARING';
}

export interface TradingEntityClearingUpsertEvent extends CrudUpsertEvent {
  entity: TradingEntityClearing;
  eventType: 'TradingEntityClearingUpsertEvent';
}

export interface TradingEntityMeta {
  aliasTypeAndValues: AliasTypeAndValue[];
  id: string;
  ownerTradingEntityId: string;
  primaryCoverUserId: string | null;
  primeBrokerId: string | null;
  secondaryCoverUserId: string | null;
  subjectTradingEntityId: string;
}

export interface TradingEntityMetaUpdate extends EntityUpdate {
  availableEvents: CrudEventEnum[];
  entity: TradingEntityMeta;
  entityType: 'TRADING_ENTITY_META';
}

export interface TradingEntityMetaUpsertEvent extends CrudUpsertEvent {
  entity: TradingEntityMeta;
  eventType: 'TradingEntityMetaUpsertEvent';
}

export interface TradingEntityUpdate extends EntityUpdate {
  availableEvents: CrudEventEnum[];
  entity: TradingEntity;
  entityType: 'TRADING_ENTITY';
}

export interface TradingEntityUpsertEvent extends CrudUpsertEvent {
  entity: TradingEntity;
  eventType: 'TradingEntityUpsertEvent';
}

export interface TradingRelationship {
  buyTradingEntity: string;
  defaultAgreement: MasterLegalAgreementType | null;
  id: string;
  initiatorFiccToInitiatorFix: boolean;
  initiatorFiccToResponderFix: boolean;
  masterLegalAgreements: MasterLegalAgreementType[];
  responderFiccToInitiatorFix: boolean;
  responderFiccToResponderFix: boolean;
  sellTradingEntity: string;
  status: Status;
  usSponsoredClearing: boolean;
  usSponsoredClearingByDefault: boolean;
}

export interface TradingRelationshipUpdate extends EntityUpdate {
  availableEvents: CrudEventEnum[];
  entity: TradingRelationship;
  entityType: 'TRADING_RELATIONSHIP';
}

export interface TradingRelationshipUpsertEvent extends CrudUpsertEvent {
  entity: TradingRelationship;
  eventType: 'TradingRelationshipUpsertEvent';
}

export interface TypeSpecificField {
  type: 'string' | 'long' | 'boolean';
}

export interface UnsubscribeMessage {
  correlationId: string;
  entityTypeEnum: EntityTypeEnum;
  subscriptionIdentifier: string;
}

export interface UpdateCollateralsClientRequest extends ClusterRoutedCommand {
  collateralIds: string[];
  correlationId: string;
  excludeFieldNames: string[];
  includeFieldNames: string[];
}

export interface UpdateIssuerOrganisationClientRequest extends ClusterRoutedCommand {
  correlationId: string;
  issuerOrganisationId: string;
}

export interface UpdateUserGroup {
  groups: string[] | null;
  username: string | null;
}

export interface User {
  algoName: string | null;
  allTradingEntities: boolean;
  canTrade: boolean;
  featureToggle: { [index: string]: boolean } | null;
  firstName: string;
  id: string;
  lastGroups: Group[];
  lastLoggedOutInstant: DateLike | null;
  lastLogoutReason: LogoutReason | null;
  lastName: string;
  loginStatus: LoginStatus;
  notes: string | null;
  oktaDetailsLastUpdated: DateLike | null;
  oktaLastLogin: DateLike | null;
  oktaUserStatus: OktaUserStatus;
  organisationId: string;
  phoneNumber: string | null;
  productRights: ProductRight[];
  rateLimiterLogoutInstants: DateLike[];
  shortId: string | null;
  status: UserStatus;
  tradingControlLimit: TradingControlLimitUser;
  tradingEntityDetails: UserTradingEntityDetails[];
  username: string;
}

export interface UserAttributes {
  algoName: string | null;
  allTradingEntities: boolean;
  canTrade: boolean;
  featureToggle: { [index: string]: boolean } | null;
  firstName: string;
  lastGroups: Group[];
  lastLoggedOutInstant: DateLike | null;
  lastName: string;
  notes: string | null;
  oktaDetailsLastUpdated: DateLike | null;
  oktaLastLogin: DateLike | null;
  oktaUserStatus: OktaUserStatus;
  organisationId: string;
  phoneNumber: string | null;
  productRights: ProductRight[];
  rateLimiterLogoutInstants: DateLike[];
  shortId: string | null;
  status: UserStatus;
  tradingEntityDetails: UserTradingEntityDetails[];
}

export interface UserPreference {
  amountFormat: AmountFormat;
  basketAllocationDurationMinsDefault: number;
  callablePeriodDaysDefault: number | null;
  copyPasteFormat: CopyPasteFormat;
  counterPartyGridView: boolean;
  counterpartyGroups: CounterpartyGroup[];
  dateFormat: DateFormat;
  defaultCallableType: CallableType;
  defaultCollateralIdentifierType: DefaultCollateralIdentifierType;
  defaultExecutionDecisionMaker: string;
  defaultHaircutType: HaircutType;
  defaultInvestmentDecisionMaker: string;
  defaultRfqType: RfqType;
  directionFormat: DirectionFormat;
  haircutSign: HaircutSign;
  id: string;
  notificationsEnabled: boolean;
  otwDurationMinsDefault: number | null;
  pinnedCollaterals: string[];
  playAudibleAlertOnNotification: boolean;
  rfqDurationMinsDefault: number;
  settlementVenueDefault: SettlementVenue;
  showCompetitionDefault: boolean;
}

export interface UserPreferenceUpdate extends EntityUpdate {
  availableEvents: CrudEventEnum[];
  entity: UserPreference;
  entityType: 'USER_PREFERENCE';
}

export interface UserPreferenceUpsertEvent extends CrudUpsertEvent {
  entity: UserPreference;
  eventType: 'UserPreferenceUpsertEvent';
}

export interface UserSettings {
  id: string;
  payload: string;
  version: string;
}

export interface UserSettingsUpdate extends EntityUpdate {
  availableEvents: CrudEventEnum[];
  entity: UserSettings;
  entityType: 'USER_SETTINGS';
}

export interface UserSettingsUpsertEvent extends CrudUpsertEvent {
  entity: UserSettings;
  eventType: 'UserSettingsUpsertEvent';
}

export interface UserTradingEntityDetails {
  euPassport: boolean | null;
  niUk: boolean | null;
  tradingEntityId: string;
  ukPassport: boolean | null;
}

export interface UserUpdate extends EntityUpdate {
  availableEvents: CrudEventEnum[];
  entity: User;
  entityType: 'USER';
}

export interface UserUpsertEvent extends CrudUpsertEvent {
  entity: User;
  eventType: 'UserUpsertEvent';
}

export interface ValidationIssue {
  errorMessage: string | null;
  fieldName: string;
  severity: Severity | null;
  type: ValidationType;
}

export interface WorkingDays {
  friday: boolean;
  id: string;
  monday: boolean;
  saturday: boolean;
  sunday: boolean;
  thursday: boolean;
  tuesday: boolean;
  wednesday: boolean;
}

export interface WorkingDaysUpdate extends EntityUpdate {
  entity: WorkingDays;
  entityType: 'WORKING_DAYS';
}

export type ClientMessageEventPayloadUnion = AxeEventCancel | AxeEventClone | AxeEventCreate | AxeEventCreateFromClone | AxeEventStage | AxeEventSend | AxeEventUpdateAttributes | BookPreparedDealEvent | CollectionEventAssignOrders | CollectionEventCreate | CollectionEventDelete | CollectionEventModifyOrders | CollectionEventModifyType | CollectionEventRefresh | CollectionEventSubmit | CompositeEvent | DealAllocateEvent | DealAllocateLateWarningEvent | DealAmendEvent | DealAssignFundEvent | DealCancelAllocationsEvent | DealCancelAssignmentEvent | DealCancelEvent | DealClearingCancelResponseReceivedEvent | DealClearingCancelSentEvent | DealClearingCreateResponseReceivedEvent | DealClearingCreateSentEvent | DealClearingFailedEvent | DealClearingFailedManuallyAcceptedEvent | DealClearingFailedManuallyRejectedEvent | DealClearingFailedRetryEvent | DealCollateralAssignmentStartSaveAllocateEvent | DealCompleteEvent | DealExpireAllocationEvent | DealExtendAllocationTimeEvent | DealFundAssignmentStartSaveAssignmentEvent | DealRecalculateEvent | DealRefreshEvent | DealSaveAllocationsEvent | DealSaveFundAssignmentEvent | DealServicingAcceptEvent | DealServicingCancelEvent | DealServicingRejectEvent | DealServicingResponderLockEvent | DealServicingResponderUnlockEvent | DealServicingRespondEventBulk | DealServicingRevertAttributesEvent | DealServicingSendEvent | DealServicingStartAmendEvent | DealServicingStartEventBulk | DealServicingStartCloseOpenEvent | DealServicingStartRerateEvent | DealServicingStartCloseOpenEventBulk | DealServicingStartRerateEventBulk | DealServicingStartReturnRecallEvent | DealServicingStartRollInPlaceEvent | DealServicingStartRollInPlaceEventBulk | DealServicingStartSubstituteEvent | DealServicingStartTerminateEvent | DealServicingStartAdminCancelEvent | DealServicingStartAdminCloseOpenEvent | DealServicingStartAdminRerateEvent | DealServicingStartAdminCloseOpenEventBulk | DealServicingStartAdminRerateEventBulk | DealServicingStartAdminTerminateEvent | DealServicingStartAdminSubstitutionEvent | DealServicingStartAdminSubstitutionEventBulk | DealServicingStartTerminateEventBulk | DealServicingStartAdminTerminateEventBulk | DealServicingStartTraderAmendEvent | DealServicingStartTraderCancelEvent | DealServicingStartRollEvent | DealServicingUpdateAttributesEvent | DealServicingUpdateRegulatoryAttributesEvent | DealServicingWithdrawEvent | DealServicingWorkflowCompleteEvent | DealServicingWorkflowCancelEvent | DealSettledEvent | DealStartAllocationsEvent | DealStartFundAssignmentEvent | DealTradeEvent | DealUpdateAllocationCollateralsEvent | DealUpdateAvailableEventsEvent | DealUpdateFundAssignmentReminderEvent | NegotiationEventAccept | NegotiationEventAcceptBulk | NegotiationEventBackToStaging | NegotiationEventCancel | NegotiationEventCancelBulk | NegotiationEventCounterOffer | NegotiationEventCounterOfferWithAttributes | NegotiationEventExpire | NegotiationEventInitiatorRevertAttributes | NegotiationEventLock | NegotiationEventLockQuote | NegotiationEventMiss | NegotiationEventOrderCancelled | NegotiationEventQuote | NegotiationEventQuoteWithAttributes | NegotiationEventRefresh | NegotiationEventReject | NegotiationEventRequest | NegotiationEventResponderRevertAttributes | NegotiationEventTagAndInvoke | NegotiationEventTagAndInvokeMany | NegotiationEventUnlock | NegotiationEventUpdateBestPrice | NegotiationEventUpdateAttributes | NegotiationEventUpdatePreAssignmentAttributes | NegotiationEventUpdateAttributesBulk | NegotiationEventUpdateRegulatoryAttributes | NegotiationEventUpdateSummary | NegotiationEventUpdateContainerSummary | NegotiationEventUpdateTotalFill | NegotiationEventWithdraw | NegotiationEventValidate | OrderEventAddPreAssignment | OrderEventCancel | OrderEventClone | OrderEventCloneBulk | OrderEventCreate | OrderEventCreateBulk | OrderEventCreateSingle | OrderEventDelete | OrderEventDeletePreAssignment | OrderEventDone | OrderEventEnabledCollateral | OrderEventExpired | OrderEventModify | OrderEventModifyCollection | OrderEventNegotiationAccepted | OrderEventNegotiationCancelled | OrderEventNegotiationRejected | OrderEventPrepare | OrderEventPrepareBulk | OrderEventPrepareCollection | OrderEventRefresh | OrderEventSubmit | OrderEventSubmitBulk | OrderEventUpdateAttributes | OrderEventUpdatePreAssignmentAttributes | OrderEventUpdateValidateIssues | PrepareDealEvent | ReferenceDataManagementEventCancel | ReferenceDataManagementEventCancelBulk | ReferenceDataManagementEventCreateAndSubmit | ReferenceDataManagementEventCreateEdit | ReferenceDataManagementEventCreateClone | ReferenceDataManagementEventCreateNew | ReferenceDataManagementEventEntityAdd | ReferenceDataManagementEventEntityDelete | ReferenceDataManagementEventEntityRevert | ReferenceDataManagementEventEntityUpdate | ReferenceDataManagementEventSubmit | ReferenceDataManagementEventSubmitBulk | ReferenceDataManagementEventDeleteAllOfType | ProductSectorMappingProductStateEventGenerateReport | ProductSectorMappingProductStateEventRollback | ProductSectorMappingProductStateEventRuleChanged | ProductSectorMappingProductStateEventSubmit;

export type ClientMessageUnion = ClientMessagePlatformChange | ClientMessageAdminLogsOutUser | ClientMessageLogin | ClientMessageLogout | ClientMessageEvent | ClientMessageSubscribe | ClientMessageDatabaseReportRequest | ClientMessageSubscribeBatch | ClientMessageUnsubscribe | ClientMessageLog | ClientMessageDevSignal | ClientMessagePermissionsReportRequest | ClientMessageAlert | ClientMessageUpdateCollaterals | ClientMessageUpdateIssuerOrganisation | ClientMessageCrudEvent | ClientMessageTogglePlatformFeature | ClientMessageCollateralUpload | ClientMessageUpdateLogLevels | ClientMessageClusterRoutedCommand;

export type CrudEventUnion = ClearingHouseUpsertEvent | CollateralUpsertEvent | CollateralBasketUpsertEvent | CrudDeleteEvent | FatFingerProductSectorLimitUpsertEvent | HaircutEntryUpsertEvent | HolidayUpsertEvent | IndexUpsertEvent | OrganisationUpsertEvent | PlatformUpsertEvent | ProductUpsertEvent | ProductSectorUpsertEvent | ReferenceRateUpsertEvent | SpreadsheetMappingUpsertEvent | TradingControlLimitUpsertEvent | TradingEntityClearingUpsertEvent | TradingEntityUpsertEvent | TradingEntityMetaUpsertEvent | TradingRelationshipUpsertEvent | UserUpsertEvent | UserPreferenceUpsertEvent | UserSettingsUpsertEvent;

export type DateAsString = string;

export type EntityUpdateUnion = ApplicationAlertUpdate | AuditEntryUpdate | AuthorisedUserUpdate | AxeUpdate | BestPriceUpdate | BatchDataUpdate | ClearingHouseInteractionUpdate | CountryUpdate | ClearingHouseUpdate | ClusterClientStateUpdate | ClusterPongUpdate | ClusterSessionUpdate | CollateralBasketUpdate | CollateralPriceUpdate | CollateralUpdate | CollateralDetailsUpdate | CollateralUploadUpdate | CollectionUpdate | ComplianceCollateralUpdate | CounterUpdate | DatabaseReportGatewayRequestUpdate | DealExportEntryUpdate | DealHistoryUpdate | DealInterestPeriodUpdate | DealUpdate | DevSignalUpdate | EgressPositionUpdate | EoniaRateUpdate | EstrRateUpdate | ExportFailedRetryDataUpdate | FatFingerProductSectorLimitUpdate | FatFingerUserLimitUpdate | FxRateUpdate | HaircutBandUpdate | HaircutCollateralBandUpdate | HaircutEntryUpdate | HolidayUpdate | ImportCompleteUpdate | IndexUpdate | InFlightOrganisationUpdate | InFlightTargetOrganisationUpdate | NegotiationAggregateUpdate | NegotiationHistoryUpdate | NegotiationOverrideUpdate | NegotiationUpdate | NotesUpdate | OrderRecordKeepingUpdate | OrderUpdate | OrganisationMetaUpdate | OrganisationUpdate | IssuerOrganisationUpdate | PermissionReportRequestUpdate | PlatformUpdate | PrimeBrokerUpdate | ProductSectorUpdate | ProductSectorMappingUpdate | ProductSectorMappingDraftUpdate | ProductUpdate | QueryRequestUpdate | ReferenceDataManagementDataUpdate | ReferenceRateUpdate | SotwResetUpdate | SotwSentUpdate | SponsorUpdate | SpreadsheetMappingUpdate | TradingControlLimitUpdate | TradingControlLimitUserUtilisationUpdate | TradingEntityClearingUpdate | TradingEntityMetaUpdate | TradingEntityUpdate | TradingRelationshipUpdate | UserPreferenceUpdate | UserSettingsUpdate | UserUpdate | WorkingDaysUpdate | RefinitivCollateralImportResultUpdate | ProductSectorMappingStateUpdate;

export type ServerMessageUnion = EntitySnapshotBeginMessage | EntitySnapshotEndMessage | EntityUpdateMessage | EntityDeleteMessage | EntityRemoveSubscriptionIdsMessage | EventResponseMessage | HistoricalQueryResponseMessage | EntitySubscriptionMetaMessage | HeartbeatMessage | EntityStreamSnapshotsBeginMessage | EntityStreamSnapshotsEndMessage;

export type ServicingTypeDetailUnion = CloseOpen | Substitution | Termination | ReturnRecall | ServicingAmend | ServicingAdminCancel | ServicingTraderAmend | ServicingTraderCancel | Rerate | RollInPlace | Roll;

export type StateMachineEventUnion = AxeEventCancel | AxeEventClone | AxeEventCreate | AxeEventCreateFromClone | AxeEventStage | AxeEventSend | AxeEventUpdateAttributes | BookPreparedDealEvent | CollectionEventAssignOrders | CollectionEventCreate | CollectionEventDelete | CollectionEventModifyOrders | CollectionEventModifyType | CollectionEventRefresh | CollectionEventSubmit | CompositeEvent | DealAllocateEvent | DealAllocateLateWarningEvent | DealAmendEvent | DealAssignFundEvent | DealCancelAllocationsEvent | DealCancelAssignmentEvent | DealCancelEvent | DealClearingCancelResponseReceivedEvent | DealClearingCancelSentEvent | DealClearingCreateResponseReceivedEvent | DealClearingCreateSentEvent | DealClearingFailedEvent | DealClearingFailedManuallyAcceptedEvent | DealClearingFailedManuallyRejectedEvent | DealClearingFailedRetryEvent | DealCollateralAssignmentStartSaveAllocateEvent | DealCompleteEvent | DealExpireAllocationEvent | DealExtendAllocationTimeEvent | DealFundAssignmentStartSaveAssignmentEvent | DealRecalculateEvent | DealRefreshEvent | DealSaveAllocationsEvent | DealSaveFundAssignmentEvent | DealServicingAcceptEvent | DealServicingCancelEvent | DealServicingRejectEvent | DealServicingResponderLockEvent | DealServicingResponderUnlockEvent | DealServicingRevertAttributesEvent | DealServicingSendEvent | DealServicingStartAmendEvent | DealServicingStartCloseOpenEvent | DealServicingStartRerateEvent | DealServicingStartReturnRecallEvent | DealServicingStartRollInPlaceEvent | DealServicingStartSubstituteEvent | DealServicingStartTerminateEvent | DealServicingStartAdminCancelEvent | DealServicingStartAdminCloseOpenEvent | DealServicingStartAdminRerateEvent | DealServicingStartAdminTerminateEvent | DealServicingStartAdminSubstitutionEvent | DealServicingStartTraderAmendEvent | DealServicingStartTraderCancelEvent | DealServicingStartRollEvent | DealServicingUpdateAttributesEvent | DealServicingUpdateRegulatoryAttributesEvent | DealServicingWithdrawEvent | DealServicingWorkflowCompleteEvent | DealServicingWorkflowCancelEvent | DealSettledEvent | DealStartAllocationsEvent | DealStartFundAssignmentEvent | DealTradeEvent | DealUpdateAllocationCollateralsEvent | DealUpdateAvailableEventsEvent | DealUpdateFundAssignmentReminderEvent | NegotiationEventAccept | NegotiationEventBackToStaging | NegotiationEventCancel | NegotiationEventCounterOffer | NegotiationEventCounterOfferWithAttributes | NegotiationEventExpire | NegotiationEventInitiatorRevertAttributes | NegotiationEventLock | NegotiationEventLockQuote | NegotiationEventMiss | NegotiationEventOrderCancelled | NegotiationEventQuote | NegotiationEventQuoteWithAttributes | NegotiationEventRefresh | NegotiationEventReject | NegotiationEventRequest | NegotiationEventResponderRevertAttributes | NegotiationEventTagAndInvoke | NegotiationEventTagAndInvokeMany | NegotiationEventUnlock | NegotiationEventUpdateBestPrice | NegotiationEventUpdateAttributes | NegotiationEventUpdatePreAssignmentAttributes | NegotiationEventUpdateRegulatoryAttributes | NegotiationEventUpdateSummary | NegotiationEventUpdateContainerSummary | NegotiationEventUpdateTotalFill | NegotiationEventWithdraw | NegotiationEventValidate | OrderEventAddPreAssignment | OrderEventCancel | OrderEventClone | OrderEventCreate | OrderEventDelete | OrderEventDeletePreAssignment | OrderEventDone | OrderEventEnabledCollateral | OrderEventExpired | OrderEventModify | OrderEventModifyCollection | OrderEventNegotiationAccepted | OrderEventNegotiationCancelled | OrderEventNegotiationRejected | OrderEventPrepare | OrderEventPrepareCollection | OrderEventRefresh | OrderEventSubmit | OrderEventUpdateAttributes | OrderEventUpdatePreAssignmentAttributes | OrderEventUpdateValidateIssues | PrepareDealEvent | ReferenceDataManagementEventCancel | ReferenceDataManagementEventCreateAndSubmit | ReferenceDataManagementEventCreateEdit | ReferenceDataManagementEventCreateClone | ReferenceDataManagementEventCreateNew | ReferenceDataManagementEventEntityAdd | ReferenceDataManagementEventEntityDelete | ReferenceDataManagementEventEntityRevert | ReferenceDataManagementEventEntityUpdate | ReferenceDataManagementEventSubmit | ReferenceDataManagementEventDeleteAllOfType | ProductSectorMappingProductStateEventGenerateReport | ProductSectorMappingProductStateEventRollback | ProductSectorMappingProductStateEventRuleChanged | ProductSectorMappingProductStateEventSubmit;

export type TypeSpecificFieldUnion = StringValue | LongValue | BooleanValue;

export enum AcceptedActor {
  INITIATOR = 'INITIATOR',
  RESPONDER = 'RESPONDER',
}

export enum AcceptedValues {
  Y = 'Y',
}

export enum Action {
  ADD = 'ADD',
  REMOVE = 'REMOVE',
}

export enum ActualClearingType {
  SYSTEM = 'SYSTEM',
  MANUALLY_CLEARED = 'MANUALLY_CLEARED',
}

export enum AlertType {
  WEB_USER_DISCONNECTED = 'WEB_USER_DISCONNECTED',
  WEB_USER_CONNECTED = 'WEB_USER_CONNECTED',
  FIX_USER_DISCONNECTED = 'FIX_USER_DISCONNECTED',
  FIX_USER_CONNECTED = 'FIX_USER_CONNECTED',
  FIX_QUOTE_REJECTED = 'FIX_QUOTE_REJECTED',
  FIX_QUOTE_STATUS_REPORT_REJECTED = 'FIX_QUOTE_STATUS_REPORT_REJECTED',
  FIX_SESSION_REJECTION = 'FIX_SESSION_REJECTION',
  FIX_RATE_LIMIT_BREACH = 'FIX_RATE_LIMIT_BREACH',
  FIX_GATEWAY_DISCONNECTED = 'FIX_GATEWAY_DISCONNECTED',
  COLLATERAL_UPLOAD_SANCTIONS_FAILED = 'COLLATERAL_UPLOAD_SANCTIONS_FAILED',
  COLLATERAL_PRICE_FAILED = 'COLLATERAL_PRICE_FAILED',
}

export enum AmountFormat {
  ACTUAL = 'ACTUAL',
  THOUSANDS = 'THOUSANDS',
  MILLIONS = 'MILLIONS',
}

export enum Application {
  TRADING_GATEWAY = 'TRADING_GATEWAY',
  ADMIN_GATEWAY = 'ADMIN_GATEWAY',
  REFERENCE_DATA_GATEWAY = 'REFERENCE_DATA_GATEWAY',
  DATA_EXPORT_GATEWAY = 'DATA_EXPORT_GATEWAY',
  DATA_IMPORT_GATEWAY = 'DATA_IMPORT_GATEWAY',
  HISTORICAL_SERVICE = 'HISTORICAL_SERVICE',
  CLUSTER = 'CLUSTER',
  FIX_GATEWAY = 'FIX_GATEWAY',
  CLEARING_GATEWAY = 'CLEARING_GATEWAY',
  MOCK_CLEARING_HOUSE = 'MOCK_CLEARING_HOUSE',
}

export enum AssetType {
  GOVT = 'GOVT',
  CORP = 'CORP',
  MORT_NON_AGENCY = 'MORT_NON_AGENCY',
  MORT_AGENCY = 'MORT_AGENCY',
}

export enum AuditDirection {
  INCOMING = 'INCOMING',
  OUTGOING = 'OUTGOING',
}

export enum AuditMarket {
  EUROPE = 'EUROPE',
  US = 'US',
  ALL = 'ALL',
  NONE = 'NONE',
}

export enum AxeEvent {
  CREATE = 'CREATE',
  CREATE_FROM_CLONE = 'CREATE_FROM_CLONE',
  CLONE = 'CLONE',
  UPDATE_ATTRIBUTES = 'UPDATE_ATTRIBUTES',
  SEND = 'SEND',
  STAGE = 'STAGE',
  CANCEL = 'CANCEL',
  PURGE = 'PURGE',
}

export enum AxeStatus {
  DRAFT = 'DRAFT',
  READY = 'READY',
  SUBMITTED = 'SUBMITTED',
  CANCELLED = 'CANCELLED',
}

export enum BasisConvention {
  DEF_30_360 = '30/360',
  ACTUAL_ACTUAL = 'ACT/ACT',
  ACTUAL_ACTUAL_NON_EOM = 'ACT/ACT (Non EOM)',
  ACTUAL_AFB = 'ACT/AFB',
  ACTUAL_360 = 'ACT/360',
  ACTUAL_365 = 'ACT/365',
  NL_365 = 'NL/365',
}

export enum BatchEvent {
  STARTED = 'STARTED',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
}

export enum BestPriceAssociationType {
  ORDER = 'ORDER',
  COLLECTION = 'COLLECTION',
}

export enum BestPriceStrategy {
  REPO_INTEREST = 'REPO_INTEREST',
  WEIGHTED_AVERAGE_RATE = 'WEIGHTED_AVERAGE_RATE',
}

export enum CalculatedValueType {
  MARKET_TRADE_DATE_US = 'MARKET_TRADE_DATE_US',
  MARKET_TRADE_DATE_EUROPE = 'MARKET_TRADE_DATE_EUROPE',
}

export enum CallableType {
  NONE = 'NONE',
  BILATERAL = 'BILATERAL',
  UNILATERAL = 'UNILATERAL',
}

export enum ClearingHouseType {
  LCH = 'LCH',
  LCHSA = 'LCHSA',
  FICC = 'FICC',
}

export enum ClearingInstance {
  LCH = 'LCH',
  SA = 'SA',
}

export enum ClearingInteractionState {
  MESSAGE_GENERATION_FAILED = 'MESSAGE_GENERATION_FAILED',
  TIMEOUT_NOT_SENT = 'TIMEOUT_NOT_SENT',
  CANNOT_SEND_TO_MQ = 'CANNOT_SEND_TO_MQ',
  SENT = 'SENT',
  TIMEOUT_SENT = 'TIMEOUT_SENT',
  SENT_PENDING_RECEIVED = 'SENT_PENDING_RECEIVED',
  RESPONSE_RECEIVED_CANNOT_PARSED = 'RESPONSE_RECEIVED_CANNOT_PARSED',
  REJECTED = 'REJECTED',
  ACCEPTED = 'ACCEPTED',
  RETRYING = 'RETRYING',
}

export enum ClearingMessageType {
  TRADE_MESSAGE = 'TRADE_MESSAGE',
  TRADE_CANCEL_MESSAGE = 'TRADE_CANCEL_MESSAGE',
}

export enum ClearingResponseResult {
  SUCCESS = 'SUCCESS',
  REJECTED = 'REJECTED',
  PENDING = 'PENDING',
}

export enum ClearingTradeType {
  REPO = 'REPO',
  DBV = 'DBV',
  GC_PLUS = 'GC_PLUS',
}

export enum ClientLogStream {
  GLOBAL = 'GLOBAL',
  ENTITY_UPDATES = 'ENTITY_UPDATES',
}

export enum ClientMessageType {
  EVENT_MESSAGE = 'EVENT_MESSAGE',
  CRUD_EVENT_MESSAGE = 'CRUD_EVENT_MESSAGE',
  LOGIN_MESSAGE = 'LOGIN_MESSAGE',
  LOGOUT_MESSAGE = 'LOGOUT_MESSAGE',
  SUBSCRIBE_MESSAGE = 'SUBSCRIBE_MESSAGE',
  DATABASE_REPORT_MESSAGE = 'DATABASE_REPORT_MESSAGE',
  SUBSCRIBE_BATCH_MESSAGE = 'SUBSCRIBE_BATCH_MESSAGE',
  UNSUBSCRIBE_MESSAGE = 'UNSUBSCRIBE_MESSAGE',
  LOG_MESSAGE = 'LOG_MESSAGE',
  PLATFORM_CHANGE_MESSAGE = 'PLATFORM_CHANGE_MESSAGE',
  ADMIN_LOGS_OUT_USER_MESSAGE = 'ADMIN_LOGS_OUT_USER_MESSAGE',
  DEV_SIGNAL = 'DEV_SIGNAL',
  TRADING_ENTITY_UPDATE_TRADE_SPLIT_MESSAGE = 'TRADING_ENTITY_UPDATE_TRADE_SPLIT_MESSAGE',
  TOGGLE_PLATFORM_FEATURE_MESSAGE = 'TOGGLE_PLATFORM_FEATURE_MESSAGE',
  CLIENT_UPDATE_LOG_LEVELS_MESSAGE = 'CLIENT_UPDATE_LOG_LEVELS_MESSAGE',
  PERMISSIONS_REPORT = 'PERMISSIONS_REPORT',
  UPDATE_COLLATERALS = 'UPDATE_COLLATERALS',
  UPDATE_ISSUER_ORGANISATION = 'UPDATE_ISSUER_ORGANISATION',
}

export enum ClientPlatformChangeActionEnum {
  PRE_START_DAY_EUROPE = 'PRE_START_DAY_EUROPE',
  START_DAY_EUROPE = 'START_DAY_EUROPE',
  PRE_START_DAY_EUROPE_TOMORROW = 'PRE_START_DAY_EUROPE_TOMORROW',
  START_DAY_EUROPE_TOMORROW = 'START_DAY_EUROPE_TOMORROW',
  END_DAY_EUROPE = 'END_DAY_EUROPE',
  PRE_START_DAY_US = 'PRE_START_DAY_US',
  START_DAY_US = 'START_DAY_US',
  END_DAY_US = 'END_DAY_US',
  ACTIVATE_TRADING_EUROPE = 'ACTIVATE_TRADING_EUROPE',
  SUSPEND_TRADING_EUROPE = 'SUSPEND_TRADING_EUROPE',
  ACTIVATE_TRADING_US = 'ACTIVATE_TRADING_US',
  SUSPEND_TRADING_US = 'SUSPEND_TRADING_US',
}

export enum CollateralBasketStatus {
  ACTIVE = 'ACTIVE',
  SUSPENDED = 'SUSPENDED',
  EXPIRED = 'EXPIRED',
  DELETED = 'DELETED',
}

export enum CollateralBasketType {
  GC_BESPOKE = 'GC_BESPOKE',
  DBV = 'DBV',
  EUROCLEAR = 'EUROCLEAR',
  BONY = 'BONY',
  GC_PLUS = 'GC_PLUS',
  CLEAR_STREAM = 'CLEAR_STREAM',
  CASH = 'CASH',
  COLLATERAL = 'COLLATERAL',
}

export enum CollateralIdentifierType {
  ISIN = 'ISIN',
  CUSIP = 'CUSIP',
  FISN = 'FISN',
  CFI = 'CFI',
  EXCHANGE = 'EXCHANGE',
  MARKET = 'MARKET',
}

export enum CollateralStatus {
  ENABLED = 'ENABLED',
  DISABLED = 'DISABLED',
  READY_FOR_ACTIVATION = 'READY_FOR_ACTIVATION',
  PENDING = 'PENDING',
  PENDING_FAILED = 'PENDING_FAILED',
  PENDING_AWAITING_PRICE = 'PENDING_AWAITING_PRICE',
}

export enum CollateralType {
  STANDARD = 'STANDARD',
  INDEX_LINKED = 'INDEX_LINKED',
}

export enum CollectionEvent {
  CREATE = 'CREATE',
  ASSIGN = 'ASSIGN',
  MODIFY_TYPE = 'MODIFY_TYPE',
  REFRESH = 'REFRESH',
  SUBMIT = 'SUBMIT',
  DELETE = 'DELETE',
  MODIFY_ORDERS = 'MODIFY_ORDERS',
}

export enum CollectionStatus {
  OPEN = 'OPEN',
  SUBMITTED = 'SUBMITTED',
  DELETED = 'DELETED',
}

export enum CollectionType {
  ALL_OR_NOTHING = 'ALL_OR_NOTHING',
  ONE_CANCELS_OTHERS = 'ONE_CANCELS_OTHERS',
  NON_CONTINGENT = 'NON_CONTINGENT',
  SMART_GROUPING = 'SMART_GROUPING',
  SEND_INDIVIDUALLY = 'SEND_INDIVIDUALLY',
  ALL_OR_NOTHING_X = 'ALL_OR_NOTHING_X',
}

export enum CollectionTypeValues {
  AON = 'AON',
  OCO = 'OCO',
  Std = 'Std',
  SG = 'SG',
  AON_X = 'AON_X',
}

export enum ComplianceStatus {
  NOT_CALCULATED = 'NOT_CALCULATED',
  CLEARED = 'CLEARED',
  NOT_CLEARED = 'NOT_CLEARED',
}

export enum CopyPasteFormat {
  TABULAR = 'TABULAR',
  PLAIN_TEXT = 'PLAIN_TEXT',
}

export enum CounterType {
  FIRDS_REPORT = 'FIRDS_REPORT',
  FIRDS_REPORT_FILE_FORMAT_2 = 'FIRDS_REPORT_FILE_FORMAT_2',
}

export enum CouponFrequency {
  ANNUAL = 'ANNUAL',
  BI_ANNUAL = 'BI_ANNUAL',
  QUARTERLY = 'QUARTERLY',
  MONTHLY = 'MONTHLY',
}

export enum CrudEventEnum {
  UPSERT = 'UPSERT',
  DELETE = 'DELETE',
}

export enum Currency {
  AUD = 'AUD',
  CAD = 'CAD',
  DKK = 'DKK',
  EUR = 'EUR',
  GBP = 'GBP',
  JPY = 'JPY',
  SEK = 'SEK',
  USD = 'USD',
  NZD = 'NZD',
  CHF = 'CHF',
  NOK = 'NOK',
  HUF = 'HUF',
  PLN = 'PLN',
  ZAR = 'ZAR',
  SGD = 'SGD',
  ARS = 'ARS',
  BRL = 'BRL',
  CLP = 'CLP',
  COP = 'COP',
  MXN = 'MXN',
  VEF = 'VEF',
  CZK = 'CZK',
  AED = 'AED',
  TRY = 'TRY',
  KRW = 'KRW',
  ILS = 'ILS',
  BGN = 'BGN',
  ISK = 'ISK',
  RON = 'RON',
  QAR = 'QAR',
  HKD = 'HKD',
  KWD = 'KWD',
  BHD = 'BHD',
  CNH = 'CNH',
  CNY = 'CNY',
  EGP = 'EGP',
  IDR = 'IDR',
  INR = 'INR',
  JOD = 'JOD',
  KZT = 'KZT',
  MAD = 'MAD',
  MYR = 'MYR',
  PEN = 'PEN',
  PHP = 'PHP',
  PYG = 'PYG',
  RUB = 'RUB',
  SAR = 'SAR',
  SDG = 'SDG',
  THB = 'THB',
  TND = 'TND',
  TWD = 'TWD',
  UAH = 'UAH',
  UYU = 'UYU',
  VND = 'VND',
}

export enum DateFormat {
  EU = 'EU',
  US = 'US',
  DD_MMM_YYYY = 'DD_MMM_YYYY',
}

export enum DateSource {
  TERM = 'TERM',
  DATES = 'DATES',
}

export enum DateTerm {
  O = 'O',
  T = 'T',
  T_PLUS_0 = 'T_PLUS_0',
  T_PLUS_1 = 'T_PLUS_1',
  T_PLUS_2 = 'T_PLUS_2',
  T_PLUS_3 = 'T_PLUS_3',
  S = 'S',
  C = 'C',
}

/**
 * Values:
 * - `TRADE`
 * - `AMEND`
 * - `REFRESH` - @deprecated
 * - `CANCEL`
 * - `COMPLETE`
 * - `CLEARING_CREATE_SENT`
 * - `CLEARING_CREATE_RESPONSE_RECEIVED`
 * - `CLEARING_CANCEL_SENT`
 * - `CLEARING_CANCEL_RESPONSE_RECEIVED`
 * - `CLEARING_FAILURE_OCCURRED`
 * - `CLEARING_FAILURE_MANUALLY_REJECTED`
 * - `CLEARING_FAILURE_MANUALLY_ACCEPTED`
 * - `CLEARING_FAILURE_RETRY`
 * - `PREPARE_DEAL`
 * - `BOOK_PREPARED_DEAL`
 * - `PURGE`
 * - `RESURRECTED`
 * - `START_ALLOCATIONS`
 * - `CANCEL_ALLOCATIONS`
 * - `SAVE_ALLOCATIONS`
 * - `EXTEND_ALLOCATION_TIME`
 * - `EXPIRE_ALLOCATION_TIME`
 * - `ALLOCATE`
 * - `START_ASSIGNMENT`
 * - `CANCEL_ASSIGNMENT`
 * - `SAVE_ASSIGNMENT`
 * - `ASSIGN`
 * - `SERVICING_START_TRADER_AMEND`
 * - `SERVICING_START_TRADER_CANCEL`
 * - `SERVICING_START_AMEND`
 * - `SERVICING_START_RERATE`
 * - `SERVICING_START_SUBSTITUTE`
 * - `SERVICING_START_TERMINATE`
 * - `SERVICING_START_ADMIN_TERMINATE`
 * - `SERVICING_START_ADMIN_RERATE`
 * - `SERVICING_START_ADMIN_SUBSTITUTION`
 * - `SERVICING_START_RETURN_RECALL`
 * - `SERVICING_START_ROLL_IN_PLACE`
 * - `SERVICING_START_ROLL`
 * - `SERVICING_START_CLOSE_OPEN`
 * - `SERVICING_START_ADMIN_CLOSE_OPEN`
 * - `SERVICING_START_ADMIN_CANCEL`
 * - `SERVICING_WORKFLOW_COMPLETE`
 * - `SERVICING_WORKFLOW_CANCEL`
 * - `SERVICING_RESPONDER_LOCK`
 * - `SERVICING_RESPONDER_UNLOCK`
 * - `SERVICING_UPDATE_ATTRIBUTES`
 * - `SERVICING_REVERT_ATTRIBUTES`
 * - `SERVICING_UPDATE_REGULATORY_ATTRIBUTES`
 * - `SERVICING_SEND`
 * - `SERVICING_ACCEPT`
 * - `SERVICING_REJECT`
 * - `SERVICING_WITHDRAW`
 * - `SERVICING_CANCEL`
 * - `RECALCULATE`
 * - `ALLOCATION_LATE_WARNING`
 * - `UPDATE_FUND_ASSIGNMENT_REMINDER`
 * - `UPDATE_ALLOCATION_COLLATERALS`
 * - `UPDATE_AVAILABLE_EVENTS`
 * - `SETTLE` - @deprecated
 */
export enum DealEvent {
  TRADE = 'TRADE',
  AMEND = 'AMEND',
  /**
   * @deprecated
   */
  REFRESH = 'REFRESH',
  CANCEL = 'CANCEL',
  COMPLETE = 'COMPLETE',
  CLEARING_CREATE_SENT = 'CLEARING_CREATE_SENT',
  CLEARING_CREATE_RESPONSE_RECEIVED = 'CLEARING_CREATE_RESPONSE_RECEIVED',
  CLEARING_CANCEL_SENT = 'CLEARING_CANCEL_SENT',
  CLEARING_CANCEL_RESPONSE_RECEIVED = 'CLEARING_CANCEL_RESPONSE_RECEIVED',
  CLEARING_FAILURE_OCCURRED = 'CLEARING_FAILURE_OCCURRED',
  CLEARING_FAILURE_MANUALLY_REJECTED = 'CLEARING_FAILURE_MANUALLY_REJECTED',
  CLEARING_FAILURE_MANUALLY_ACCEPTED = 'CLEARING_FAILURE_MANUALLY_ACCEPTED',
  CLEARING_FAILURE_RETRY = 'CLEARING_FAILURE_RETRY',
  PREPARE_DEAL = 'PREPARE_DEAL',
  BOOK_PREPARED_DEAL = 'BOOK_PREPARED_DEAL',
  PURGE = 'PURGE',
  RESURRECTED = 'RESURRECTED',
  START_ALLOCATIONS = 'START_ALLOCATIONS',
  CANCEL_ALLOCATIONS = 'CANCEL_ALLOCATIONS',
  SAVE_ALLOCATIONS = 'SAVE_ALLOCATIONS',
  EXTEND_ALLOCATION_TIME = 'EXTEND_ALLOCATION_TIME',
  EXPIRE_ALLOCATION_TIME = 'EXPIRE_ALLOCATION_TIME',
  ALLOCATE = 'ALLOCATE',
  START_ASSIGNMENT = 'START_ASSIGNMENT',
  CANCEL_ASSIGNMENT = 'CANCEL_ASSIGNMENT',
  SAVE_ASSIGNMENT = 'SAVE_ASSIGNMENT',
  ASSIGN = 'ASSIGN',
  SERVICING_START_TRADER_AMEND = 'SERVICING_START_TRADER_AMEND',
  SERVICING_START_TRADER_CANCEL = 'SERVICING_START_TRADER_CANCEL',
  SERVICING_START_AMEND = 'SERVICING_START_AMEND',
  SERVICING_START_RERATE = 'SERVICING_START_RERATE',
  SERVICING_START_SUBSTITUTE = 'SERVICING_START_SUBSTITUTE',
  SERVICING_START_TERMINATE = 'SERVICING_START_TERMINATE',
  SERVICING_START_ADMIN_TERMINATE = 'SERVICING_START_ADMIN_TERMINATE',
  SERVICING_START_ADMIN_RERATE = 'SERVICING_START_ADMIN_RERATE',
  SERVICING_START_ADMIN_SUBSTITUTION = 'SERVICING_START_ADMIN_SUBSTITUTION',
  SERVICING_START_RETURN_RECALL = 'SERVICING_START_RETURN_RECALL',
  SERVICING_START_ROLL_IN_PLACE = 'SERVICING_START_ROLL_IN_PLACE',
  SERVICING_START_ROLL = 'SERVICING_START_ROLL',
  SERVICING_START_CLOSE_OPEN = 'SERVICING_START_CLOSE_OPEN',
  SERVICING_START_ADMIN_CLOSE_OPEN = 'SERVICING_START_ADMIN_CLOSE_OPEN',
  SERVICING_START_ADMIN_CANCEL = 'SERVICING_START_ADMIN_CANCEL',
  SERVICING_WORKFLOW_COMPLETE = 'SERVICING_WORKFLOW_COMPLETE',
  SERVICING_WORKFLOW_CANCEL = 'SERVICING_WORKFLOW_CANCEL',
  SERVICING_RESPONDER_LOCK = 'SERVICING_RESPONDER_LOCK',
  SERVICING_RESPONDER_UNLOCK = 'SERVICING_RESPONDER_UNLOCK',
  SERVICING_UPDATE_ATTRIBUTES = 'SERVICING_UPDATE_ATTRIBUTES',
  SERVICING_REVERT_ATTRIBUTES = 'SERVICING_REVERT_ATTRIBUTES',
  SERVICING_UPDATE_REGULATORY_ATTRIBUTES = 'SERVICING_UPDATE_REGULATORY_ATTRIBUTES',
  SERVICING_SEND = 'SERVICING_SEND',
  SERVICING_ACCEPT = 'SERVICING_ACCEPT',
  SERVICING_REJECT = 'SERVICING_REJECT',
  SERVICING_WITHDRAW = 'SERVICING_WITHDRAW',
  SERVICING_CANCEL = 'SERVICING_CANCEL',
  RECALCULATE = 'RECALCULATE',
  ALLOCATION_LATE_WARNING = 'ALLOCATION_LATE_WARNING',
  UPDATE_FUND_ASSIGNMENT_REMINDER = 'UPDATE_FUND_ASSIGNMENT_REMINDER',
  UPDATE_ALLOCATION_COLLATERALS = 'UPDATE_ALLOCATION_COLLATERALS',
  UPDATE_AVAILABLE_EVENTS = 'UPDATE_AVAILABLE_EVENTS',
  /**
   * @deprecated
   */
  SETTLE = 'SETTLE',
}

export enum DealExportFile {
  BILLING = 'BILLING',
  RECONCILIATION = 'RECONCILIATION',
  FIFTEEN_A_SIX = 'FIFTEEN_A_SIX',
}

export enum DealSettlement {
  EUROCLEAR = 'EUROCLEAR',
  BILATERAL = 'BILATERAL',
  CCP = 'CCP',
  SPONSORED_CCP = 'SPONSORED_CCP',
  BONY = 'BONY',
  DTCC = 'DTCC',
  CLEARSTREAM = 'CLEARSTREAM',
  AUSTRACLEAR = 'AUSTRACLEAR',
  JASDEC = 'JASDEC',
  FED = 'FED',
}

/**
 * Values:
 * - `DONE`
 * - `CANCELLED`
 * - `REJECTED`
 * - `COMPLETED`
 * - `CLEARING_NOT_SENT`
 * - `CLEARING_SENT`
 * - `CLEARING_ACKNOWLEDGED`
 * - `CLEARING_FAILED`
 * - `UNASSIGNED`
 * - `ASSIGNED`
 * - `UNALLOCATED`
 * - `EXPIRED_ALLOCATION`
 * - `ALLOCATED`
 * - `PREPARED`
 * - `SERVICING_WITH_RESPONDER`
 * - `SERVICING_WITH_INITIATOR`
 * - `SERVICING_WITH_WORKFLOW`
 * - `SETTLED` - @deprecated
 */
export enum DealStatus {
  DONE = 'DONE',
  CANCELLED = 'CANCELLED',
  REJECTED = 'REJECTED',
  COMPLETED = 'COMPLETED',
  CLEARING_NOT_SENT = 'CLEARING_NOT_SENT',
  CLEARING_SENT = 'CLEARING_SENT',
  CLEARING_ACKNOWLEDGED = 'CLEARING_ACKNOWLEDGED',
  CLEARING_FAILED = 'CLEARING_FAILED',
  UNASSIGNED = 'UNASSIGNED',
  ASSIGNED = 'ASSIGNED',
  UNALLOCATED = 'UNALLOCATED',
  EXPIRED_ALLOCATION = 'EXPIRED_ALLOCATION',
  ALLOCATED = 'ALLOCATED',
  PREPARED = 'PREPARED',
  SERVICING_WITH_RESPONDER = 'SERVICING_WITH_RESPONDER',
  SERVICING_WITH_INITIATOR = 'SERVICING_WITH_INITIATOR',
  SERVICING_WITH_WORKFLOW = 'SERVICING_WITH_WORKFLOW',
  /**
   * @deprecated
   */
  SETTLED = 'SETTLED',
}

export enum DealTrigger {
  NEGOTIATION_ACCEPT = 'NEGOTIATION_ACCEPT',
  DEAL_SPLIT = 'DEAL_SPLIT',
  DEAL_SERVICING = 'DEAL_SERVICING',
  DEAL_ALLOCATION = 'DEAL_ALLOCATION',
  DEAL_ASSIGNMENT = 'DEAL_ASSIGNMENT',
}

export enum DefaultCollateralIdentifierType {
  ISIN = 'ISIN',
  CUSIP = 'CUSIP',
  BOTH = 'BOTH',
}

export enum Direction {
  BUY = 'BUY',
  SELL = 'SELL',
}

export enum DirectionFormat {
  BUY_SELL = 'BUY_SELL',
  RVP_RP = 'RVP_RP',
  BID_OFFER = 'BID_OFFER',
  BORROW_LEND = 'BORROW_LEND',
}

export enum DurationTerm {
  N = 'N',
  W = 'W',
  W_2 = 'W_2',
  W_3 = 'W_3',
  M = 'M',
  M_2 = 'M_2',
  M_3 = 'M_3',
  M_4 = 'M_4',
  M_5 = 'M_5',
  M_6 = 'M_6',
  M_7 = 'M_7',
  M_8 = 'M_8',
  M_9 = 'M_9',
  M_10 = 'M_10',
  M_11 = 'M_11',
  Y = 'Y',
  Y_2 = 'Y_2',
  BD = 'BD',
  BD_1 = 'BD_1',
  BD_2 = 'BD_2',
  BD_3 = 'BD_3',
  BD_4 = 'BD_4',
  BD_5 = 'BD_5',
  BD_6 = 'BD_6',
  O = 'O',
}

export enum EgressPositionIdentifier {
  DEAL_EXPORT_EOD_US = 'DEAL_EXPORT_EOD_US',
  DEAL_EXPORT_INTRADAY_US = 'DEAL_EXPORT_INTRADAY_US',
  DEAL_EXPORT_INTRADAY_EUROPE = 'DEAL_EXPORT_INTRADAY_EUROPE',
  DEAL_EXPORT_EOD_EUROPE = 'DEAL_EXPORT_EOD_EUROPE',
  NEGOTIATION_EXPORT_EOD_US = 'NEGOTIATION_EXPORT_EOD_US',
  FIRDS_REPORT = 'FIRDS_REPORT',
  HISTORY_SERVICE_WRITE_POSITION_AXE = 'HISTORY_SERVICE_WRITE_POSITION_AXE',
  HISTORY_SERVICE_WRITE_POSITION_DEAL = 'HISTORY_SERVICE_WRITE_POSITION_DEAL',
  HISTORY_SERVICE_WRITE_POSITION_DEAL_INTEREST = 'HISTORY_SERVICE_WRITE_POSITION_DEAL_INTEREST',
  HISTORY_SERVICE_WRITE_POSITION_NEGOTIATION = 'HISTORY_SERVICE_WRITE_POSITION_NEGOTIATION',
  HISTORY_SERVICE_WRITE_POSITION_COLLATERAL_DETAILS = 'HISTORY_SERVICE_WRITE_POSITION_COLLATERAL_DETAILS',
  CLEARING = 'CLEARING',
  ORDER_RECORD_KEEPING_US = 'ORDER_RECORD_KEEPING_US',
  ORDER_RECORD_KEEPING_EUROPE = 'ORDER_RECORD_KEEPING_EUROPE',
  AUDITING = 'AUDITING',
  TRANSACTION_REPORTING_US = 'TRANSACTION_REPORTING_US',
  TRANSACTION_REPORTING_EUROPE = 'TRANSACTION_REPORTING_EUROPE',
  ALERT = 'ALERT',
  RETRY_EXPORT = 'RETRY_EXPORT',
  PUBLIC_EGRESS_STATE_OF_THE_WORLD = 'PUBLIC_EGRESS_STATE_OF_THE_WORLD',
  COLLATERAL_EXPORT = 'COLLATERAL_EXPORT',
  COLLATERAL_IMPORT = 'COLLATERAL_IMPORT',
  COLLATERAL_REFINITIV_REPORT = 'COLLATERAL_REFINITIV_REPORT',
  BONDS_NOTE_COLLATERAL_UPDATE_ADHOC = 'BONDS_NOTE_COLLATERAL_UPDATE_ADHOC',
  BONDS_NOTE_COLLATERAL_UPDATE_SCHEDULED = 'BONDS_NOTE_COLLATERAL_UPDATE_SCHEDULED',
  UPDATE_MORTGAGES_FACTORS = 'UPDATE_MORTGAGES_FACTORS',
  UPDATE_COLLATERAL_FIELDS = 'UPDATE_COLLATERAL_FIELDS',
  UPDATE_COLLATERALS_REQUEST_POSITION = 'UPDATE_COLLATERALS_REQUEST_POSITION',
  COLLATERAL_UPLOAD = 'COLLATERAL_UPLOAD',
  UPDATE_ISSUER_ORGANISATION_REQUEST_POSITION = 'UPDATE_ISSUER_ORGANISATION_REQUEST_POSITION',
  PERMISSIONS_REPORT = 'PERMISSIONS_REPORT',
  FIRDS_REPORT_ADHOC = 'FIRDS_REPORT_ADHOC',
  GENERATE_SCHEDULE_REPORT_ADHOC = 'GENERATE_SCHEDULE_REPORT_ADHOC',
  GENERATE_SCHEDULE_REPORT = 'GENERATE_SCHEDULE_REPORT',
  FIRDS_REPORT_SCHEDULED = 'FIRDS_REPORT_SCHEDULED',
  OKTA_USERS_REPORT_ADHOC = 'OKTA_USERS_REPORT_ADHOC',
  OKTA_USERS_REPORT_SCHEDULED = 'OKTA_USERS_REPORT_SCHEDULED',
  DEALS_EXPORT_INTRADAY_EUROPE = 'DEALS_EXPORT_INTRADAY_EUROPE',
  DEALS_EXPORT_INTRADAY_US = 'DEALS_EXPORT_INTRADAY_US',
  DEALS_EXPORT_EOD_EUROPE = 'DEALS_EXPORT_EOD_EUROPE',
  DEALS_EXPORT_EOD_US = 'DEALS_EXPORT_EOD_US',
  NEGOTIATIONS_EXPORT_EOD_ALL = 'NEGOTIATIONS_EXPORT_EOD_ALL',
  NEGOTIATIONS_EXPORT_EOD_GFID_EU = 'NEGOTIATIONS_EXPORT_EOD_GFID_EU',
  NEGOTIATIONS_EXPORT_EOD_GFID_US = 'NEGOTIATIONS_EXPORT_EOD_GFID_US',
  SECTOR_MAPPINGS_REPORT = 'SECTOR_MAPPINGS_REPORT',
  REFINITIV_ISSUER_ORG_REFRESH = 'REFINITIV_ISSUER_ORG_REFRESH',
  LATEST_PUBLIC_EGRESS_STATE_OF_THE_WORLD = 'LATEST_PUBLIC_EGRESS_STATE_OF_THE_WORLD',
  FIX_EU_MARKET_OPEN_ARCHIVER_POSITION = 'FIX_EU_MARKET_OPEN_ARCHIVER_POSITION',
  FIX_US_MARKET_OPEN_ARCHIVER_POSITION = 'FIX_US_MARKET_OPEN_ARCHIVER_POSITION',
}

export enum EndStateValues {
  NEGOTIATION_ACCEPTED = 'NEGOTIATION_ACCEPTED',
  NEGOTIATION_CANCELLED = 'NEGOTIATION_CANCELLED',
  NEGOTIATION_MISSED = 'NEGOTIATION_MISSED',
  NEGOTIATION_EXPIRED = 'NEGOTIATION_EXPIRED',
  NEGOTIATION_QUOTED = 'NEGOTIATION_QUOTED',
  NEGOTIATION_QUOTED_SUBJECT = 'NEGOTIATION_QUOTED_SUBJECT',
  NEGOTIATION_REQUESTED_WITH_PRICE = 'NEGOTIATION_REQUESTED_WITH_PRICE',
  NEGOTIATION_REQUESTED_WITHOUT_PRICE = 'NEGOTIATION_REQUESTED_WITHOUT_PRICE',
  NEGOTIATION_REFRESHED = 'NEGOTIATION_REFRESHED',
  NEGOTIATION_WITHDRAWN = 'NEGOTIATION_WITHDRAWN',
  NEGOTIATION_REJECTED = 'NEGOTIATION_REJECTED',
  DEAL_CLEARING_ACKNOWLEDGED = 'DEAL_CLEARING_ACKNOWLEDGED',
  DEAL_CLEARING_NOT_SENT = 'DEAL_CLEARING_NOT_SENT',
  DEAL_CLEARING_SENT = 'DEAL_CLEARING_SENT',
  DEAL_CLEARING_FAILED = 'DEAL_CLEARING_FAILED',
  DEAL_ASSIGNED = 'DEAL_ASSIGNED',
  DEAL_UNASSIGNED = 'DEAL_UNASSIGNED',
  DEAL_UNALLOCATED = 'DEAL_UNALLOCATED',
  DEAL_ALLOCATED = 'DEAL_ALLOCATED',
  DEAL_DONE = 'DEAL_DONE',
  DEAL_CANCELLED = 'DEAL_CANCELLED',
  DEAL_REJECTED = 'DEAL_REJECTED',
  DEAL_SERVICING_WITH_RESPONDER = 'DEAL_SERVICING_WITH_RESPONDER',
}

export enum EntityState {
  NEW = 'NEW',
  EXISTING = 'EXISTING',
  EXISTING_FROM_JSON = 'EXISTING_FROM_JSON',
}

export enum EntityStatus {
  ENABLED = 'ENABLED',
  DISABLED = 'DISABLED',
}

export enum EntityTypeEnum {
  APPLICATION_ALERT = 'APPLICATION_ALERT',
  AUDIT_ENTRY = 'AUDIT_ENTRY',
  AUTHORISED_USER = 'AUTHORISED_USER',
  AXE = 'AXE',
  BATCH_DATA = 'BATCH_DATA',
  BEST_PRICE = 'BEST_PRICE',
  CLEARING_HOUSE = 'CLEARING_HOUSE',
  CLEARING_HOUSE_INTERACTION = 'CLEARING_HOUSE_INTERACTION',
  CLUSTER_CLIENT_STATE = 'CLUSTER_CLIENT_STATE',
  CLUSTER_PONG = 'CLUSTER_PONG',
  CLUSTER_SESSION = 'CLUSTER_SESSION',
  COLLATERAL = 'COLLATERAL',
  COLLATERAL_BASKET = 'COLLATERAL_BASKET',
  COLLATERAL_DETAILS = 'COLLATERAL_DETAILS',
  COLLATERAL_PRICE = 'COLLATERAL_PRICE',
  COLLATERAL_REFINITIV_IMPORT_RESULT = 'COLLATERAL_REFINITIV_IMPORT_RESULT',
  COLLATERAL_UPLOAD = 'COLLATERAL_UPLOAD',
  COLLECTION = 'COLLECTION',
  COMMAND = 'COMMAND',
  COMPLIANCE_COLLATERAL = 'COMPLIANCE_COLLATERAL',
  COUNTER = 'COUNTER',
  COUNTRY = 'COUNTRY',
  DATABASE_REPORT_REQUEST = 'DATABASE_REPORT_REQUEST',
  DEAL = 'DEAL',
  DEAL_EXPORT = 'DEAL_EXPORT',
  DEAL_HISTORY = 'DEAL_HISTORY',
  DEAL_INTEREST_PERIOD = 'DEAL_INTEREST_PERIOD',
  DEV_SIGNAL = 'DEV_SIGNAL',
  EGRESS_POSITION = 'EGRESS_POSITION',
  EGRESS_SOTW_PUBLISH = 'EGRESS_SOTW_PUBLISH',
  EONIA_RATE = 'EONIA_RATE',
  ESTR_RATE = 'ESTR_RATE',
  FAT_FINGER_PRODUCT_SECTOR = 'FAT_FINGER_PRODUCT_SECTOR',
  FAT_FINGER_USER = 'FAT_FINGER_USER',
  FX_RATE = 'FX_RATE',
  HAIRCUT_BAND = 'HAIRCUT_BAND',
  HAIRCUT_COLLATERAL_BAND = 'HAIRCUT_COLLATERAL_BAND',
  HAIRCUT_ENTRY = 'HAIRCUT_ENTRY',
  HOLIDAY = 'HOLIDAY',
  IMPORT_COMPLETE = 'IMPORT_COMPLETE',
  INDEX = 'INDEX',
  IN_FLIGHT_TARGET_ORGANISATION = 'IN_FLIGHT_TARGET_ORGANISATION',
  IN_FLIGHT_ORGANISATION = 'IN_FLIGHT_ORGANISATION',
  ISSUER_ORGANISATION = 'ISSUER_ORGANISATION',
  NEGOTIATION = 'NEGOTIATION',
  NEGOTIATION_AGGREGATE = 'NEGOTIATION_AGGREGATE',
  NEGOTIATION_HISTORY = 'NEGOTIATION_HISTORY',
  NEGOTIATION_OVERRIDE = 'NEGOTIATION_OVERRIDE',
  NOTES = 'NOTES',
  ORDER = 'ORDER',
  ORDER_RECORD_KEEPING = 'ORDER_RECORD_KEEPING',
  ORGANISATION = 'ORGANISATION',
  ORGANISATION_META = 'ORGANISATION_META',
  PERMISSION_REPORT_REQUEST = 'PERMISSION_REPORT_REQUEST',
  PLATFORM = 'PLATFORM',
  PRIME_BROKER = 'PRIME_BROKER',
  PRODUCT = 'PRODUCT',
  PRODUCT_SECTOR = 'PRODUCT_SECTOR',
  PRODUCT_SECTOR_MAPPING_STATE = 'PRODUCT_SECTOR_MAPPING_STATE',
  PRODUCT_SECTOR_MAPPING = 'PRODUCT_SECTOR_MAPPING',
  PRODUCT_SECTOR_MAPPING_DRAFT = 'PRODUCT_SECTOR_MAPPING_DRAFT',
  QUERY_REQUEST = 'QUERY_REQUEST',
  REFERENCE_DATA_MANAGEMENT_DATA = 'REFERENCE_DATA_MANAGEMENT_DATA',
  REFERENCE_RATE = 'REFERENCE_RATE',
  RESET_SOTW = 'RESET_SOTW',
  RESPONSE_CLUSTER_MESSAGE = 'RESPONSE_CLUSTER_MESSAGE',
  RETRY_FAILED_EXPORT = 'RETRY_FAILED_EXPORT',
  SCHEDULED_TASK_STATE = 'SCHEDULED_TASK_STATE',
  SOTW_SENT = 'SOTW_SENT',
  SOTW_START_FROM_CHECKPOINT = 'SOTW_START_FROM_CHECKPOINT',
  SPONSOR = 'SPONSOR',
  SPREADSHEET_MAPPING = 'SPREADSHEET_MAPPING',
  TRADING_CONTROL_LIMIT = 'TRADING_CONTROL_LIMIT',
  TRADING_CONTROL_LIMIT_USER_UTILISATION = 'TRADING_CONTROL_LIMIT_USER_UTILISATION',
  TRADING_ENTITY = 'TRADING_ENTITY',
  TRADING_ENTITY_CLEARING = 'TRADING_ENTITY_CLEARING',
  TRADING_ENTITY_META = 'TRADING_ENTITY_META',
  TRADING_RELATIONSHIP = 'TRADING_RELATIONSHIP',
  TRANSACTION_META = 'TRANSACTION_META',
  USER = 'USER',
  USER_PREFERENCE = 'USER_PREFERENCE',
  USER_SETTINGS = 'USER_SETTINGS',
  WORKING_DAYS = 'WORKING_DAYS',
}

export enum EventResponseStatus {
  FAILED = 'FAILED',
  SUCCEEDED = 'SUCCEEDED',
  VALIDATION_ISSUES = 'VALIDATION_ISSUES',
}

export enum EventValues {
  NEGOTIATION_INITIATOR_ACCEPT = 'NEGOTIATION_INITIATOR_ACCEPT',
  NEGOTIATION_INITIATOR_CANCEL = 'NEGOTIATION_INITIATOR_CANCEL',
  NEGOTIATION_INITIATOR_REQUEST = 'NEGOTIATION_INITIATOR_REQUEST',
  NEGOTIATION_INITIATOR_COUNTER_OFFER = 'NEGOTIATION_INITIATOR_COUNTER_OFFER',
  NEGOTIATION_INITIATOR_REFRESH = 'NEGOTIATION_INITIATOR_REFRESH',
  NEGOTIATION_INITIATOR_WITHDRAW = 'NEGOTIATION_INITIATOR_WITHDRAW',
  NEGOTIATION_INITIATOR_REJECT = 'NEGOTIATION_INITIATOR_REJECT',
  NEGOTIATION_RESPONDER_ACCEPT = 'NEGOTIATION_RESPONDER_ACCEPT',
  NEGOTIATION_RESPONDER_QUOTE = 'NEGOTIATION_RESPONDER_QUOTE',
  NEGOTIATION_RESPONDER_WITHDRAW = 'NEGOTIATION_RESPONDER_WITHDRAW',
  NEGOTIATION_RESPONDER_REJECT = 'NEGOTIATION_RESPONDER_REJECT',
  NEGOTIATION_RESPONDER_COUNTER_OFFER = 'NEGOTIATION_RESPONDER_COUNTER_OFFER',
  NEGOTIATION_SYSTEM_CANCEL = 'NEGOTIATION_SYSTEM_CANCEL',
  NEGOTIATION_SYSTEM_EXPIRE = 'NEGOTIATION_SYSTEM_EXPIRE',
  NEGOTIATION_SYSTEM_WITHDRAW = 'NEGOTIATION_SYSTEM_WITHDRAW',
  NEGOTIATION_SYSTEM_MISS = 'NEGOTIATION_SYSTEM_MISS',
  DEAL_INITIATOR_TRADE = 'DEAL_INITIATOR_TRADE',
  DEAL_RESPONDER_TRADE = 'DEAL_RESPONDER_TRADE',
  DEAL_INITIATOR_CANCEL = 'DEAL_INITIATOR_CANCEL',
  DEAL_RESPONDER_CANCEL = 'DEAL_RESPONDER_CANCEL',
  DEAL_INITIATOR_CLEARING_FAILURE_MANUALLY_ACCEPTED = 'DEAL_INITIATOR_CLEARING_FAILURE_MANUALLY_ACCEPTED',
  DEAL_INITIATOR_CLEARING_FAILURE_MANUALLY_REJECTED = 'DEAL_INITIATOR_CLEARING_FAILURE_MANUALLY_REJECTED',
  DEAL_ADMIN_CLEARING_FAILURE_MANUALLY_ACCEPTED = 'DEAL_ADMIN_CLEARING_FAILURE_MANUALLY_ACCEPTED',
  DEAL_ADMIN_CLEARING_FAILURE_MANUALLY_REJECTED = 'DEAL_ADMIN_CLEARING_FAILURE_MANUALLY_REJECTED',
  DEAL_INITIATOR_ALLOCATE = 'DEAL_INITIATOR_ALLOCATE',
  DEAL_RESPONDER_ALLOCATE = 'DEAL_RESPONDER_ALLOCATE',
  DEAL_INITIATOR_ASSIGN = 'DEAL_INITIATOR_ASSIGN',
  DEAL_INITIATOR_SERVICING_START_TRADER_CANCEL = 'DEAL_INITIATOR_SERVICING_START_TRADER_CANCEL',
  DEAL_INITIATOR_SERVICING_SEND = 'DEAL_INITIATOR_SERVICING_SEND',
  DEAL_RESPONDER_SERVICING_ACCEPT = 'DEAL_RESPONDER_SERVICING_ACCEPT',
  DEAL_RESPONDER_SERVICING_REJECT = 'DEAL_RESPONDER_SERVICING_REJECT',
  DEAL_ADMIN_SERVICING_ACCEPT = 'DEAL_ADMIN_SERVICING_ACCEPT',
  DEAL_ADMIN_SERVICING_REJECT = 'DEAL_ADMIN_SERVICING_REJECT',
  DEAL_ADMIN_CANCEL = 'DEAL_ADMIN_CANCEL',
  DEAL_SYSTEM_CANCEL = 'DEAL_SYSTEM_CANCEL',
  DEAL_SYSTEM_CLEARING_CREATE_SENT = 'DEAL_SYSTEM_CLEARING_CREATE_SENT',
  DEAL_SYSTEM_CLEARING_CREATE_RESPONSE_RECEIVED = 'DEAL_SYSTEM_CLEARING_CREATE_RESPONSE_RECEIVED',
  DEAL_SYSTEM_CLEARING_FAILURE_OCCURRED = 'DEAL_SYSTEM_CLEARING_FAILURE_OCCURRED',
}

export enum EvergreenNoticePeriod {
  THIRTY = 'THIRTY',
  SIXTY = 'SIXTY',
  NINETY = 'NINETY',
}

export enum ExDividendDayConvention {
  CALENDAR_DAYS = 'CALENDAR_DAYS',
  BUSINESS_DAYS = 'BUSINESS_DAYS',
}

export enum ExportCallableType {
  FALSE = 'FALSE',
  BILATERAL = 'BILATERAL',
  UNILATERAL = 'UNILATERAL',
}

export enum ExtendedTradingEntityType {
  STANDARD = 'STANDARD',
  BLOCK = 'BLOCK',
  BOTH = 'BOTH',
}

export enum FilterOperation {
  EQUALS = 'EQUALS',
  CONTAINS = 'CONTAINS',
  NOT_CONTAINS = 'NOT_CONTAINS',
  VALUE_CONTAINS = 'VALUE_CONTAINS',
  NOT_EQUALS = 'NOT_EQUALS',
  BEFORE = 'BEFORE',
  BEFORE_OR_EQUAL = 'BEFORE_OR_EQUAL',
  AFTER = 'AFTER',
}

export enum Group {
  GROUP_INITIATOR = 'GROUP_INITIATOR',
  GROUP_RESPONDER = 'GROUP_RESPONDER',
  GROUP_ADMIN = 'GROUP_ADMIN',
  GROUP_BUY_SIDE_TRADER = 'GROUP_BUY_SIDE_TRADER',
  GROUP_BUY_SIDE_VIEW_ONLY = 'GROUP_BUY_SIDE_VIEW_ONLY',
  GROUP_SELL_SIDE_TRADER = 'GROUP_SELL_SIDE_TRADER',
  GROUP_SELL_SIDE_SALES = 'GROUP_SELL_SIDE_SALES',
  GROUP_SELL_SIDE_VIEW_ONLY = 'GROUP_SELL_SIDE_VIEW_ONLY',
  GROUP_SELL_SIDE_ADMIN = 'GROUP_SELL_SIDE_ADMIN',
  GROUP_ADAPTIVE_ADMIN = 'GROUP_ADAPTIVE_ADMIN',
  GROUP_BTEC_ADMIN_MARKET_CONTROL = 'GROUP_BTEC_ADMIN_MARKET_CONTROL',
  GROUP_BTEC_ADMIN_NO_MARKET_CONTROL = 'GROUP_BTEC_ADMIN_NO_MARKET_CONTROL',
  GROUP_BTEC_ADMIN_BUSINESS_SALES = 'GROUP_BTEC_ADMIN_BUSINESS_SALES',
  GROUP_DISABLE_CANCEL_ON_DISCONNECT = 'GROUP_DISABLE_CANCEL_ON_DISCONNECT',
  GROUP_BTEC_ADMIN_COMPLETE_TRADES = 'GROUP_BTEC_ADMIN_COMPLETE_TRADES',
  GROUP_FIX_POST_TRADE_PROFILE = 'GROUP_FIX_POST_TRADE_PROFILE',
  GROUP_NEGOTIATION_PROFILE = 'GROUP_NEGOTIATION_PROFILE',
  GROUP_FIX_INITIATOR_NEGOTIATION_PROFILE = 'GROUP_FIX_INITIATOR_NEGOTIATION_PROFILE',
  GROUP_FIX_RESPONDER_NEGOTIATION_PROFILE = 'GROUP_FIX_RESPONDER_NEGOTIATION_PROFILE',
  GROUP_FIX_STAGE_ORDER_PROFILE = 'GROUP_FIX_STAGE_ORDER_PROFILE',
  GROUP_FIX_RECEIVE_COLLATERAL_RESPONSE = 'GROUP_FIX_RECEIVE_COLLATERAL_RESPONSE',
  GROUP_FIX_TCR_QUOTES = 'GROUP_FIX_TCR_QUOTES',
  GROUP_BTEC_ADMIN_RISK = 'GROUP_BTEC_ADMIN_RISK',
  GROUP_BTEC_ADMIN_PLTC = 'GROUP_BTEC_ADMIN_PLTC',
  GROUP_BTEC_ADMIN_EASE = 'GROUP_BTEC_ADMIN_EASE',
  GROUP_BTEC_ADMIN_REFERENCE_DATA = 'GROUP_BTEC_ADMIN_REFERENCE_DATA',
  GROUP_BTEC_ADMIN_GAM = 'GROUP_BTEC_ADMIN_GAM',
  GROUP_ADAPTIVE_CLIENT_TESTER = 'GROUP_ADAPTIVE_CLIENT_TESTER',
  GROUP_AXE_BOARD_VIEW = 'GROUP_AXE_BOARD_VIEW',
  GROUP_AXE_BOARD_INITIATE = 'GROUP_AXE_BOARD_INITIATE',
  GROUP_AXE_BOARD_RESPOND = 'GROUP_AXE_BOARD_RESPOND',
  GROUP_FIX_ENABLE_AXE = 'GROUP_FIX_ENABLE_AXE',
  GROUP_FIX_ENABLE_NEW_SOTW_AI_REALTIME = 'GROUP_FIX_ENABLE_NEW_SOTW_AI_REALTIME',
  GROUP_FIX_PRODUCT_IDENTIFIER = 'GROUP_FIX_PRODUCT_IDENTIFIER',
  GROUP_FIX_ENABLE_CLEARING_FLAG = 'GROUP_FIX_ENABLE_CLEARING_FLAG',
  GROUP_FIX_ENABLE_PREASSIGNED_SINGLE_TCR = 'GROUP_FIX_ENABLE_PREASSIGNED_SINGLE_TCR',
  GROUP_BTEC_ADMIN_COMPLIANCE = 'GROUP_BTEC_ADMIN_COMPLIANCE',
  GROUP_FIX_ENABLE_REPO_DCC_TCR = 'GROUP_FIX_ENABLE_REPO_DCC_TCR',
  GROUP_FIX_TCR_CHANGE_FLAG = 'GROUP_FIX_TCR_CHANGE_FLAG',
  GROUP_FIX_ENABLE_TCR_AXE_ROLL_UNWIND_SUB_FLAGS = 'GROUP_FIX_ENABLE_TCR_AXE_ROLL_UNWIND_SUB_FLAGS',
  GROUP_BTEC_ADMIN_SECTOR_MAPPING = 'GROUP_BTEC_ADMIN_SECTOR_MAPPING',
  GROUP_FIX_ENABLE_ROE = 'GROUP_FIX_ENABLE_ROE',
  GROUP_FIX_ENABLE_ROS = 'GROUP_FIX_ENABLE_ROS',
  GROUP_FIX_ENABLE_EC_AIP = 'GROUP_FIX_ENABLE_EC_AIP',
  UNKNOWN = 'UNKNOWN',
}

export enum HaircutSign {
  DIRECTIONAL = 'DIRECTIONAL',
  ACTUAL_SELL_SIDE = 'ACTUAL_SELL_SIDE',
  ABSOLUTE = 'ABSOLUTE',
  NEGATIVE_REV_REPO = 'NEGATIVE_REV_REPO',
}

export enum HaircutType {
  HAIRCUT = 'HAIRCUT',
  COLLATERAL = 'COLLATERAL',
}

export enum HistoricalQueryResult {
  SUCCESS = 'SUCCESS',
  FAILED_ERROR = 'FAILED_ERROR',
  FAILED_NOT_READY = 'FAILED_NOT_READY',
  FAILED_RESULT_SET_TOO_LARGE = 'FAILED_RESULT_SET_TOO_LARGE',
  NO_REPLY_FROM_CLUSTER = 'NO_REPLY_FROM_CLUSTER',
}

export enum ImportType {
  EONIA = 'EONIA',
  ESTR = 'ESTR',
  COLLATERAL_PRICE_AUSTRALIAN = 'COLLATERAL_PRICE_AUSTRALIAN',
  COLLATERAL_PRICE_ISIN = 'COLLATERAL_PRICE_ISIN',
  COLLATERAL_PRICE_CUSIP = 'COLLATERAL_PRICE_CUSIP',
  COLLATERAL_IMPORT = 'COLLATERAL_IMPORT',
}

export enum IndexLag {
  THREE_MONTHS = 'THREE_MONTHS',
  EIGHT_MONTHS = 'EIGHT_MONTHS',
}

export enum InitiatingActor {
  ORDER_INITIATOR = 'ORDER_INITIATOR',
  ORDER_RESPONDER = 'ORDER_RESPONDER',
  ADMIN = 'ADMIN',
}

export enum InitiatorOrResponderValues {
  I = 'I',
  R = 'R',
}

export enum InstrumentIdentifierType {
  Cusip = 'Cusip',
  Ric = 'Ric',
  Isin = 'Isin',
  Sedol = 'Sedol',
  Pid = 'Pid',
}

export enum InstrumentTypeValues {
  Special = 'Special',
  Basket = 'Basket',
}

export enum Issue {
  ISSUE_OOO2S = 'ISSUE_OOO2S',
  ISSUE_OO2S = 'ISSUE_OO2S',
  ISSUE_O2S = 'ISSUE_O2S',
  ISSUE_2YR = 'ISSUE_2YR',
  ISSUE_WI2S = 'ISSUE_WI2S',
  ISSUE_OOO3S = 'ISSUE_OOO3S',
  ISSUE_OO3S = 'ISSUE_OO3S',
  ISSUE_O3S = 'ISSUE_O3S',
  ISSUE_3YR = 'ISSUE_3YR',
  ISSUE_WI3S = 'ISSUE_WI3S',
  ISSUE_OOO5S = 'ISSUE_OOO5S',
  ISSUE_OO5S = 'ISSUE_OO5S',
  ISSUE_O5S = 'ISSUE_O5S',
  ISSUE_5YR = 'ISSUE_5YR',
  ISSUE_WI5S = 'ISSUE_WI5S',
  ISSUE_OOO7S = 'ISSUE_OOO7S',
  ISSUE_OO7S = 'ISSUE_OO7S',
  ISSUE_O7S = 'ISSUE_O7S',
  ISSUE_7YR = 'ISSUE_7YR',
  ISSUE_WI7S = 'ISSUE_WI7S',
  ISSUE_OOO10S = 'ISSUE_OOO10S',
  ISSUE_OO10S = 'ISSUE_OO10S',
  ISSUE_O10S = 'ISSUE_O10S',
  ISSUE_10YR = 'ISSUE_10YR',
  ISSUE_WI10S = 'ISSUE_WI10S',
  ISSUE_OOO20S = 'ISSUE_OOO20S',
  ISSUE_OO20S = 'ISSUE_OO20S',
  ISSUE_O20S = 'ISSUE_O20S',
  ISSUE_20YR = 'ISSUE_20YR',
  ISSUE_WI20S = 'ISSUE_WI20S',
  ISSUE_OOO30S = 'ISSUE_OOO30S',
  ISSUE_OO30S = 'ISSUE_OO30S',
  ISSUE_O30S = 'ISSUE_O30S',
  ISSUE_30YR = 'ISSUE_30YR',
  ISSUE_WI30S = 'ISSUE_WI30S',
  ISSUE_1MO = 'ISSUE_1MO',
  ISSUE_2MO = 'ISSUE_2MO',
  ISSUE_3MO = 'ISSUE_3MO',
  ISSUE_4MO = 'ISSUE_4MO',
  ISSUE_6MO = 'ISSUE_6MO',
  ISSUE_1YR = 'ISSUE_1YR',
  ISSUE_CTD = 'ISSUE_CTD',
}

export enum LagType {
  DAYS = 'DAYS',
  WEEKS = 'WEEKS',
  MONTHS = 'MONTHS',
  YEARS = 'YEARS',
}

export enum LogSeverity {
  ERROR = 'ERROR',
  WARN = 'WARN',
  INFO = 'INFO',
}

export enum LoginStatus {
  LOGGED_IN = 'LOGGED_IN',
  NOT_LOGGED_IN = 'NOT_LOGGED_IN',
}

export enum LogoutReason {
  OTHER = 'OTHER',
  DIFFERENT_USER_SIGNED_USING_SAME_SESSION = 'DIFFERENT_USER_SIGNED_USING_SAME_SESSION',
  USER_SIGNED_ON_DIFFERENT_SESSION = 'USER_SIGNED_ON_DIFFERENT_SESSION',
  USER_LOGGED_OUT_BY_ADMIN = 'USER_LOGGED_OUT_BY_ADMIN',
  RATE_LIMITER = 'RATE_LIMITER',
  CAN_ONLY_LOGIN_IN_FIX_NOW = 'CAN_ONLY_LOGIN_IN_FIX_NOW',
}

export enum Market {
  EUROPE = 'EUROPE',
  US = 'US',
}

export enum MarketStatus {
  MARKET_PRE_OPEN = 'MARKET_PRE_OPEN',
  MARKET_OPEN = 'MARKET_OPEN',
  MARKET_CLOSED = 'MARKET_CLOSED',
}

export enum MarketType {
  PRIMARY = 'PRIMARY',
  SECONDARY = 'SECONDARY',
}

export enum MasterLegalAgreementType {
  GMRA = 'GMRA',
  GMSLA = 'GMSLA',
  MFA = 'MFA',
}

export enum MicCode {
  BTQE = 'BTQE',
  BTQG = 'BTQG',
  BTEQ = 'BTEQ',
}

export enum MicValues {
  BTQE = 'BTQE',
  BTQG = 'BTQG',
  BTEQ = 'BTEQ',
}

export enum MidTradeAction {
  CLOSE = 'CLOSE',
  RESIZE = 'RESIZE',
  REPRICE = 'REPRICE',
  ROLL = 'ROLL',
  RESIZE_WITH_RECALCULATED_COLLATERAL_PRICE = 'RESIZE_WITH_RECALCULATED_COLLATERAL_PRICE',
}

export enum MidTradeResponse {
  ACCEPT = 'ACCEPT',
  REJECT = 'REJECT',
  SEND = 'SEND',
}

export enum NegotiationEvent {
  ACCEPT = 'ACCEPT',
  EXPIRE = 'EXPIRE',
  CANCEL = 'CANCEL',
  WITHDRAW = 'WITHDRAW',
  MISS = 'MISS',
  QUOTE = 'QUOTE',
  QUOTE_WITH_ATTRIBUTES = 'QUOTE_WITH_ATTRIBUTES',
  COUNTER_OFFER = 'COUNTER_OFFER',
  COUNTER_OFFER_WITH_ATTRIBUTES = 'COUNTER_OFFER_WITH_ATTRIBUTES',
  REJECT = 'REJECT',
  REQUEST = 'REQUEST',
  LOCK_QUOTE = 'LOCK_QUOTE',
  ORDER_CANCELLED = 'ORDER_CANCELLED',
  BACK_TO_STAGING = 'BACK_TO_STAGING',
  LOCK = 'LOCK',
  UNLOCK = 'UNLOCK',
  RESPONDER_REVERT_ATTRIBUTES = 'RESPONDER_REVERT_ATTRIBUTES',
  INITIATOR_REVERT_ATTRIBUTES = 'INITIATOR_REVERT_ATTRIBUTES',
  PURGE = 'PURGE',
  UPDATE_SUMMARY = 'UPDATE_SUMMARY',
  UPDATE_CONTAINER_SUMMARY = 'UPDATE_CONTAINER_SUMMARY',
  UPDATE_BEST_PRICE = 'UPDATE_BEST_PRICE',
  TAG_AND_INVOKE = 'TAG_AND_INVOKE',
  UPDATE_REGULATORY_ATTRIBUTES = 'UPDATE_REGULATORY_ATTRIBUTES',
  UPDATE_ATTRIBUTES = 'UPDATE_ATTRIBUTES',
  UPDATE_PRE_ASSIGNMENT_ATTRIBUTES = 'UPDATE_PRE_ASSIGNMENT_ATTRIBUTES',
  UPDATE_TOTAL_FILL = 'UPDATE_TOTAL_FILL',
  REFRESH = 'REFRESH',
  VALIDATE = 'VALIDATE',
}

export enum NegotiationFieldMeta {
  OTW_END_TIME = 'OTW_END_TIME',
  LAST_LOOK = 'LAST_LOOK',
  BASKET = 'BASKET',
  ACCRUED_INTEREST = 'ACCRUED_INTEREST',
  ALL_IN_PRICE = 'ALL_IN_PRICE',
  INDEX_RATIO = 'INDEX_RATIO',
  FEE = 'FEE',
  STOCK_BORROWING_LENDING_TYPE = 'STOCK_BORROWING_LENDING_TYPE',
  SECONDARY_BASKET_ID = 'SECONDARY_BASKET_ID',
  RATE = 'RATE',
  REPO_INTEREST = 'REPO_INTEREST',
  NOTIONAL = 'NOTIONAL',
  HAIRCUT = 'HAIRCUT',
  CALLDAYS = 'CALLDAYS',
  HIDE_COMPETITION = 'HIDE_COMPETITION',
  MIC = 'MIC',
  REFERENCE_RATE = 'REFERENCE_RATE',
  START_DATE = 'START_DATE',
  RERATE_DATE = 'RERATE_DATE',
  START_CONSIDERATION_OVERRIDE = 'START_CONSIDERATION_OVERRIDE',
  ABSOLUTE_START_CONSIDERATION = 'ABSOLUTE_START_CONSIDERATION',
  DEAL_SETTLEMENT = 'DEAL_SETTLEMENT',
  START_CONSIDERATION = 'START_CONSIDERATION',
  CALCULATED_START_CONSIDERATION = 'CALCULATED_START_CONSIDERATION',
  END_CONSIDERATION = 'END_CONSIDERATION',
  END_DATE = 'END_DATE',
  TERM = 'TERM',
  BOND_PRICE = 'BOND_PRICE',
  DIRECTION = 'DIRECTION',
  DIRTY_PRICE = 'DIRTY_PRICE',
  CLEAN_PRICE = 'CLEAN_PRICE',
  COUNTERPARTY_ID = 'COUNTERPARTY_ID',
  CLEAR_COUNTERPARTY_DATA = 'CLEAR_COUNTERPARTY_DATA',
  RESPONDER_ORGANISATION_IDS = 'RESPONDER_ORGANISATION_IDS',
  CURRENCY = 'CURRENCY',
  MARKET = 'MARKET',
  MARKET_TYPE = 'MARKET_TYPE',
  MARKET_TRADE_DATE = 'MARKET_TRADE_DATE',
  MIC_CODE = 'MIC_CODE',
  CLEARING_HOUSE = 'CLEARING_HOUSE',
  CLEARING_TYPE = 'CLEARING_TYPE',
  REPO_TYPE = 'REPO_TYPE',
  REPO_CLASSIFICATION = 'REPO_CLASSIFICATION',
  TRADE_CONDITION_FLAG = 'TRADE_CONDITION_FLAG',
  IS_AXED = 'IS_AXED',
  RFQ_TYPE = 'RFQ_TYPE',
  CALLABLE_TYPE = 'CALLABLE_TYPE',
  CALLABLE_PERIOD_DAYS = 'CALLABLE_PERIOD_DAYS',
  COUNTERPARTY_SPONSOR_ID = 'COUNTERPARTY_SPONSOR_ID',
  INITIATOR_INVESTMENT_DECISION_MAKER = 'INITIATOR_INVESTMENT_DECISION_MAKER',
  INITIATOR_EXECUTION_DECISION_MAKER = 'INITIATOR_EXECUTION_DECISION_MAKER',
  RESPONDER_INVESTMENT_DECISION_MAKER = 'RESPONDER_INVESTMENT_DECISION_MAKER',
  RESPONDER_EXECUTION_DECISION_MAKER = 'RESPONDER_EXECUTION_DECISION_MAKER',
  COLLATERAL = 'COLLATERAL',
  COLLATERAL_ISSUE = 'COLLATERAL_ISSUE',
  EXTERNAL_QUOTE_ID = 'EXTERNAL_QUOTE_ID',
  EXTERNAL_QUOTE_RESPONSE_ID = 'EXTERNAL_QUOTE_RESPONSE_ID',
  FUND_ID = 'FUND_ID',
  INITIATOR_TRADING_ENTITY_ID = 'INITIATOR_TRADING_ENTITY_ID',
  SPONSOR_ID = 'SPONSOR_ID',
  BUNDLE = 'BUNDLE',
  PRE_AGREED = 'PRE_AGREED',
  INITIATOR_PRIME_BROKER_ID = 'INITIATOR_PRIME_BROKER_ID',
  TRADING_TYPE = 'TRADING_TYPE',
  PRODUCT = 'PRODUCT',
  PRODUCT_SECTOR = 'PRODUCT_SECTOR',
  HAIRCUT_TYPE = 'HAIRCUT_TYPE',
  INITIATOR_BOOK = 'INITIATOR_BOOK',
  INITIATOR_STRATEGY = 'INITIATOR_STRATEGY',
  RESPONDER_BOOK = 'RESPONDER_BOOK',
  BOOK = 'BOOK',
  STRATEGY = 'STRATEGY',
  RESPONDER_STRATEGY = 'RESPONDER_STRATEGY',
  SETTLEMENT_VENUE = 'SETTLEMENT_VENUE',
  ASSET_TYPE = 'ASSET_TYPE',
  FACTOR = 'FACTOR',
  EVERGREEN_NOTICE_PERIOD = 'EVERGREEN_NOTICE_PERIOD',
}

export enum NegotiationSide {
  INITIATOR = 'INITIATOR',
  RESPONDER = 'RESPONDER',
}

export enum NegotiationStatus {
  QUOTED = 'QUOTED',
  QUOTED_SUBJECT = 'QUOTED_SUBJECT',
  REQUESTED_WITH_PRICE = 'REQUESTED_WITH_PRICE',
  REQUESTED_WITHOUT_PRICE = 'REQUESTED_WITHOUT_PRICE',
  MISSED = 'MISSED',
  REJECTED = 'REJECTED',
  EXPIRED = 'EXPIRED',
  CANCELLED = 'CANCELLED',
  WITHDRAWN = 'WITHDRAWN',
  ACCEPTED = 'ACCEPTED',
  ITEMS_IN_DIFFERENT_STATES = 'ITEMS_IN_DIFFERENT_STATES',
  ITEMS_IN_DIFFERENT_STATES_TERMINATED = 'ITEMS_IN_DIFFERENT_STATES_TERMINATED',
  ITEMS_IN_DIFFERENT_STATES_REJECTED_EXPIRED = 'ITEMS_IN_DIFFERENT_STATES_REJECTED_EXPIRED',
}

export enum NegotiationType {
  CONTINGENT_SUMMARY = 'CONTINGENT_SUMMARY',
  CONTINGENT_ITEM = 'CONTINGENT_ITEM',
  ONE_CANCELS_OTHERS_SUMMARY = 'ONE_CANCELS_OTHERS_SUMMARY',
  ONE_CANCELS_OTHERS_ITEM = 'ONE_CANCELS_OTHERS_ITEM',
  SMART_GROUPING_SUMMARY = 'SMART_GROUPING_SUMMARY',
  SMART_GROUPING_ITEM = 'SMART_GROUPING_ITEM',
  INDIVIDUAL = 'INDIVIDUAL',
  AON_X_SUMMARY = 'AON_X_SUMMARY',
}

export enum OktaUserStatus {
  NOT_IN_OKTA = 'NOT_IN_OKTA',
  ACTIVE = 'ACTIVE',
  DEPROVISIONED = 'DEPROVISIONED',
  LOCKED_OUT = 'LOCKED_OUT',
  PASSWORD_EXPIRED = 'PASSWORD_EXPIRED',
  PROVISIONED = 'PROVISIONED',
  RECOVERY = 'RECOVERY',
  STAGED = 'STAGED',
  SUSPENDED = 'SUSPENDED',
  SDK_UNKNOWN = 'SDK_UNKNOWN',
}

export enum OrderAttributesCreateSource {
  ADD_ASSETS = 'ADD_ASSETS',
  STAGE_BASKET = 'STAGE_BASKET',
  SPREADSHEET_UPLOAD = 'SPREADSHEET_UPLOAD',
  FIX = 'FIX',
  CLONED = 'CLONED',
  CANCEL_BACK_TO_STAGING = 'CANCEL_BACK_TO_STAGING',
  REJECTED_BACK_TO_STAGING = 'REJECTED_BACK_TO_STAGING',
  EXPIRED_BACK_TO_STAGING = 'EXPIRED_BACK_TO_STAGING',
  ROLLED = 'ROLLED',
}

export enum OrderEvent {
  CREATE = 'CREATE',
  MODIFY = 'MODIFY',
  MODIFY_COLLECTION = 'MODIFY_COLLECTION',
  PREPARE = 'PREPARE',
  SUBMIT = 'SUBMIT',
  EXPIRE = 'EXPIRE',
  MOVE_TO_DONE = 'MOVE_TO_DONE',
  DELETE = 'DELETE',
  CANCEL = 'CANCEL',
  NEGOTIATION_ACCEPTED = 'NEGOTIATION_ACCEPTED',
  NEGOTIATION_CANCELLED = 'NEGOTIATION_CANCELLED',
  NEGOTIATION_REJECTED = 'NEGOTIATION_REJECTED',
  TRADE = 'TRADE',
  REFRESH = 'REFRESH',
  PURGE = 'PURGE',
  CLONE = 'CLONE',
  UPDATE_ATTRIBUTES = 'UPDATE_ATTRIBUTES',
  ADD_PRE_ASSIGNMENT = 'ADD_PRE_ASSIGNMENT',
  DELETE_PRE_ASSIGNMENT = 'DELETE_PRE_ASSIGNMENT',
  UPDATE_PRE_ASSIGNMENT_ATTRIBUTES = 'UPDATE_PRE_ASSIGNMENT_ATTRIBUTES',
  UPDATE_VALIDATION_ISSUES = 'UPDATE_VALIDATION_ISSUES',
  ENABLED_COLLATERAL = 'ENABLED_COLLATERAL',
}

export enum OrderStatus {
  INCOMPLETE = 'INCOMPLETE',
  READY = 'READY',
  PREPARED = 'PREPARED',
  SUBMITTED = 'SUBMITTED',
  DELETED = 'DELETED',
  DONE = 'DONE',
  DONE_WITHOUT_DEAL = 'DONE_WITHOUT_DEAL',
  EXPIRED = 'EXPIRED',
  CANCELLED = 'CANCELLED',
}

export enum OrderTerm {
  O_N = 'O_N',
  O_W = 'O_W',
  O_M = 'O_M',
  T_N = 'T_N',
  T_W = 'T_W',
  T_M = 'T_M',
  S_N = 'S_N',
  S_W = 'S_W',
  S_2W = 'S_2W',
  S_M = 'S_M',
  S_3M = 'S_3M',
  S_6M = 'S_6M',
  S_Y = 'S_Y',
  C_N = 'C_N',
  C_W = 'C_W',
  C_2W = 'C_2W',
  C_M = 'C_M',
  C_3M = 'C_3M',
  C_6M = 'C_6M',
  C_Y = 'C_Y',
  CUSTOM = 'CUSTOM',
  O_O = 'O_O',
  T_O = 'T_O',
  S_O = 'S_O',
  C_O = 'C_O',
  CUSTOM_O = 'CUSTOM_O',
  O_2W = 'O_2W',
  O_3W = 'O_3W',
  O_2M = 'O_2M',
  O_3M = 'O_3M',
  O_4M = 'O_4M',
  O_5M = 'O_5M',
  O_6M = 'O_6M',
  O_7M = 'O_7M',
  O_8M = 'O_8M',
  O_9M = 'O_9M',
  O_10M = 'O_10M',
  O_11M = 'O_11M',
  T_2W = 'T_2W',
  T_3W = 'T_3W',
  T_2M = 'T_2M',
  T_3M = 'T_3M',
  T_4M = 'T_4M',
  T_5M = 'T_5M',
  T_6M = 'T_6M',
  T_7M = 'T_7M',
  T_8M = 'T_8M',
  T_9M = 'T_9M',
  T_10M = 'T_10M',
  T_11M = 'T_11M',
  S_3W = 'S_3W',
  S_2M = 'S_2M',
  S_4M = 'S_4M',
  S_5M = 'S_5M',
  S_7M = 'S_7M',
  S_8M = 'S_8M',
  S_9M = 'S_9M',
  S_10M = 'S_10M',
  S_11M = 'S_11M',
  C_3W = 'C_3W',
  C_2M = 'C_2M',
  C_4M = 'C_4M',
  C_5M = 'C_5M',
  C_7M = 'C_7M',
  C_8M = 'C_8M',
  C_9M = 'C_9M',
  C_10M = 'C_10M',
  C_11M = 'C_11M',
}

export enum OrganisationFeature {
  ENABLE_GRID_VIEW = 'ENABLE_GRID_VIEW',
  ENABLE_ORDER_SMART_GROUPING = 'ENABLE_ORDER_SMART_GROUPING',
  ENABLE_PRE_ASSIGNMENT = 'ENABLE_PRE_ASSIGNMENT',
  ENABLE_FIX_LOCKING = 'ENABLE_FIX_LOCKING',
  ENABLE_CUMULATIVE_RECONCILIATION_FILE = 'ENABLE_CUMULATIVE_RECONCILIATION_FILE',
}

export enum OrganisationType {
  BUY_SIDE = 'BUY_SIDE',
  SELL_SIDE = 'SELL_SIDE',
}

export enum OriginalSource {
  REFINITIV = 'REFINITIV',
  FILE_UPLOAD = 'FILE_UPLOAD',
}

/**
 * Values:
 * - `EUROPE`
 * - `US`
 * - `ALL` - @deprecated
 */
export enum OrkMarket {
  EUROPE = 'EUROPE',
  US = 'US',
  /**
   * @deprecated
   */
  ALL = 'ALL',
}

export enum OverriddenNegotiationSide {
  INITIATOR = 'INITIATOR',
  RESPONDER = 'RESPONDER',
  BOTH = 'BOTH',
}

export enum Permission {
  NONE = 'NONE',
  CAN_TRADE = 'CAN_TRADE',
  AXE_BOARD_VIEW = 'AXE_BOARD_VIEW',
  AXE_BOARD_INITIATE = 'AXE_BOARD_INITIATE',
  AXE_BOARD_RESPOND = 'AXE_BOARD_RESPOND',
  FAT_FINGER_LIMITS_MANAGE = 'FAT_FINGER_LIMITS_MANAGE',
  HAIRCUT_MANAGE = 'HAIRCUT_MANAGE',
  HAIRCUT_VIEW = 'HAIRCUT_VIEW',
  INCOMING_RFQS_VIEW = 'INCOMING_RFQS_VIEW',
  INITIATED_RFQS_VIEW = 'INITIATED_RFQS_VIEW',
  POSITION_UPLOAD = 'POSITION_UPLOAD',
  STAGING_VIEW = 'STAGING_VIEW',
  STAGING_MANAGE = 'STAGING_MANAGE',
  RFQS_MANAGE = 'RFQS_MANAGE',
  TRADE_BLOTTER_VIEW = 'TRADE_BLOTTER_VIEW',
  TRADE_CANCEL = 'TRADE_CANCEL',
  TRADE_UPDATE = 'TRADE_UPDATE',
  TRADE_ASSIGN = 'TRADE_ASSIGN',
  TRADE_ALLOCATE = 'TRADE_ALLOCATE',
  TRADE_COMPLETE = 'TRADE_COMPLETE',
  TRADE_SERVICING = 'TRADE_SERVICING',
  USERS_MANAGE = 'USERS_MANAGE',
  USERS_VIEW = 'USERS_VIEW',
  ORGANISATION_MANAGE = 'ORGANISATION_MANAGE',
  ISSUER_ORGANISATION_VIEW = 'ISSUER_ORGANISATION_VIEW',
  ISSUER_ORGANISATION_MANAGE = 'ISSUER_ORGANISATION_MANAGE',
  ORGANISATION_META_MANAGE = 'ORGANISATION_META_MANAGE',
  ORGANISATION_META_VIEW = 'ORGANISATION_META_VIEW',
  PRIME_BROKER_MANAGE = 'PRIME_BROKER_MANAGE',
  TRADING_ENTITY_MANAGE = 'TRADING_ENTITY_MANAGE',
  TRADING_RELATIONSHIP_MANAGE = 'TRADING_RELATIONSHIP_MANAGE',
  TRADING_ENTITY_CLEARING_MANAGE = 'TRADING_ENTITY_CLEARING_MANAGE',
  TRADING_ENTITY_MAPPING_MANAGE = 'TRADING_ENTITY_MAPPING_MANAGE',
  TRADING_ENTITY_MAPPING_ADMIN_VIEW = 'TRADING_ENTITY_MAPPING_ADMIN_VIEW',
  TRADING_ENTITY_MAPPING_VIEW = 'TRADING_ENTITY_MAPPING_VIEW',
  COLLATERAL_VIEW = 'COLLATERAL_VIEW',
  COLLATERAL_MANAGE = 'COLLATERAL_MANAGE',
  PRODUCT_MANAGE = 'PRODUCT_MANAGE',
  SECTOR_MANAGE = 'SECTOR_MANAGE',
  SECTOR_MAPPING_VIEW = 'SECTOR_MAPPING_VIEW',
  SECTOR_MAPPING_MANAGE = 'SECTOR_MAPPING_MANAGE',
  CLEARING_HOUSE_MANAGE = 'CLEARING_HOUSE_MANAGE',
  HOLIDAY_MANAGE = 'HOLIDAY_MANAGE',
  USER_PREFERENCE_MANAGE = 'USER_PREFERENCE_MANAGE',
  PLATFORM_TRADING_SUSPEND = 'PLATFORM_TRADING_SUSPEND',
  SCRIPT_UPLOAD = 'SCRIPT_UPLOAD',
  SYSTEM = 'SYSTEM',
  POST_TRADE_PROFILE = 'POST_TRADE_PROFILE',
  NEGOTIATION_PROFILE = 'NEGOTIATION_PROFILE',
  INITIATOR_NEGOTIATION_PROFILE = 'INITIATOR_NEGOTIATION_PROFILE',
  RESPONDER_NEGOTIATION_PROFILE = 'RESPONDER_NEGOTIATION_PROFILE',
  STAGE_ORDER_PROFILE = 'STAGE_ORDER_PROFILE',
  SEND_RECEIVE_AXE_FIX = 'SEND_RECEIVE_AXE_FIX',
  USER_SETTINGS_MANAGE = 'USER_SETTINGS_MANAGE',
  COLLATERAL_BASKET_VIEW = 'COLLATERAL_BASKET_VIEW',
  COLLATERAL_BASKET_MANAGE = 'COLLATERAL_BASKET_MANAGE',
  INDEX_MANAGE = 'INDEX_MANAGE',
  TRADING_CONTROL_LIMIT_VIEW = 'TRADING_CONTROL_LIMIT_VIEW',
  TRADING_CONTROL_LIMIT_MANAGE = 'TRADING_CONTROL_LIMIT_MANAGE',
  FX_RATE_VIEW = 'FX_RATE_VIEW',
  FX_RATE_MANAGE = 'FX_RATE_MANAGE',
  DISABLE_CANCEL_ON_DISCONNECT = 'DISABLE_CANCEL_ON_DISCONNECT',
  FIX_RECEIVE_COLLATERAL_RESPONSE = 'FIX_RECEIVE_COLLATERAL_RESPONSE',
  SPREADSHEET_MAPPING_MANAGE = 'SPREADSHEET_MAPPING_MANAGE',
  FIX_TCR_QUOTES = 'FIX_TCR_QUOTES',
  TRIGGER_SUPPORT_ACTIONS = 'TRIGGER_SUPPORT_ACTIONS',
  UPDATE_CLIENT_LOGGING = 'UPDATE_CLIENT_LOGGING',
  START_CONSIDERATION_OVERRIDE = 'START_CONSIDERATION_OVERRIDE',
  FIX_PERCENTAGE_FORMAT = 'FIX_PERCENTAGE_FORMAT',
  STOCK_BORROWING_LENDING = 'STOCK_BORROWING_LENDING',
  TRADING_ENTITY_VIEW = 'TRADING_ENTITY_VIEW',
  ORGANISATION_VIEW = 'ORGANISATION_VIEW',
  PLATFORM_VIEW = 'PLATFORM_VIEW',
  CLEARING_HOUSE_VIEW = 'CLEARING_HOUSE_VIEW',
  FAT_FINGER_LIMITS_VIEW = 'FAT_FINGER_LIMITS_VIEW',
  HOLIDAY_VIEW = 'HOLIDAY_VIEW',
  SECTOR_VIEW = 'SECTOR_VIEW',
  INDEX_VIEW = 'INDEX_VIEW',
  PRODUCT_VIEW = 'PRODUCT_VIEW',
  REPORTING_TRIGGERS_MANAGE = 'REPORTING_TRIGGERS_MANAGE',
  ADMIN_MENU = 'ADMIN_MENU',
  SEND_ALERTS = 'SEND_ALERTS',
  SEND_APPLICATION_SIGNALS = 'SEND_APPLICATION_SIGNALS',
  BACKDATED_MID_TRADE = 'BACKDATED_MID_TRADE',
  FIX_ENABLE_AXE = 'FIX_ENABLE_AXE',
  FIX_ENABLE_NEW_SOTW_AI_REALTIME = 'FIX_ENABLE_NEW_SOTW_AI_REALTIME',
  FIX_PRODUCT_IDENTIFIER = 'FIX_PRODUCT_IDENTIFIER',
  REQUEST_PERMISSION_REPORT = 'REQUEST_PERMISSION_REPORT',
  FIX_ENABLE_CLEARING_FLAG = 'FIX_ENABLE_CLEARING_FLAG',
  FIX_ENABLE_PREASSIGNED_SINGLE_TCR = 'FIX_ENABLE_PREASSIGNED_SINGLE_TCR',
  FIX_ENABLE_REPO_DCC_TCR = 'FIX_ENABLE_REPO_DCC_TCR',
  FIX_TCR_CHANGE_FLAG = 'FIX_TCR_CHANGE_FLAG',
  FIX_ENABLE_TCR_AXE_ROLL_UNWIND_SUB_FLAGS = 'FIX_ENABLE_TCR_AXE_ROLL_UNWIND_SUB_FLAGS',
  FIX_ENABLE_ROE = 'FIX_ENABLE_ROE',
  FIX_ENABLE_ROS = 'FIX_ENABLE_ROS',
  FIX_ENABLE_EC_AIP = 'FIX_ENABLE_EC_AIP',
}

export enum PlatformFeature {
  ALLOW_PARTIAL = 'ALLOW_PARTIAL',
  FIX_TRADE_QUERIES = 'FIX_TRADE_QUERIES',
  ENABLE_CURVE_COLLECTIONS = 'ENABLE_CURVE_COLLECTIONS',
  ENABLE_START_CONSIDERATION_OVERRIDE = 'ENABLE_START_CONSIDERATION_OVERRIDE',
  ENABLE_STOCK_BORROWING_LENDING = 'ENABLE_STOCK_BORROWING_LENDING',
  ENABLE_FIX_PERCENTAGE_FORMAT = 'ENABLE_FIX_PERCENTAGE_FORMAT',
  ENABLE_CLUSTER_BATCHING = 'ENABLE_CLUSTER_BATCHING',
  ENABLE_LIGHT_WEIGHT_PRICE = 'ENABLE_LIGHT_WEIGHT_PRICE',
  ENABLE_LIGHT_WEIGHT_PRICE_WEB = 'ENABLE_LIGHT_WEIGHT_PRICE_WEB',
  ENABLE_SPREADSHEET_UNKNOWN_COLLATERAL = 'ENABLE_SPREADSHEET_UNKNOWN_COLLATERAL',
  ENABLE_FIRDS = 'ENABLE_FIRDS',
  ENABLE_CORPORATES = 'ENABLE_CORPORATES',
  ENABLE_MBS = 'ENABLE_MBS',
  ENABLE_EVERGREENS = 'ENABLE_EVERGREENS',
  ENABLE_REAL_TIME_PURGE = 'ENABLE_REAL_TIME_PURGE',
  ENABLE_NEGOTIATION_INFLIGHT = 'ENABLE_NEGOTIATION_INFLIGHT',
  DISABLE_PRODUCT_HOURS = 'DISABLE_PRODUCT_HOURS',
}

export enum PreDefinedFilter {
  NEGOTIATION_GRID_VIEW = 'NEGOTIATION_GRID_VIEW',
}

/**
 * Values:
 * - `TRADE`
 * - `WRITE`
 * - `VIEW`
 * - `NONE`
 * - `ALL` - @deprecated
 */
export enum ProductPermission {
  TRADE = 'TRADE',
  WRITE = 'WRITE',
  VIEW = 'VIEW',
  NONE = 'NONE',
  /**
   * @deprecated
   */
  ALL = 'ALL',
}

export enum ProductSectorMappingStateEvent {
  INITIALISE = 'INITIALISE',
  RULE_CHANGED = 'RULE_CHANGED',
  ROLLBACK_CHANGES = 'ROLLBACK_CHANGES',
  SUBMIT_CHANGES = 'SUBMIT_CHANGES',
  GENERATE_REPORT = 'GENERATE_REPORT',
}

export enum ProductSectorMappingStateStatus {
  RULES_UNCHANGED = 'RULES_UNCHANGED',
  RULES_MODIFIED = 'RULES_MODIFIED',
}

export enum ProductSectorMappingStatus {
  EXISTING = 'EXISTING',
  NEW = 'NEW',
  DELETED = 'DELETED',
  EDITED = 'EDITED',
}

export enum RateType {
  FIXED = 'FIXED',
  FLOATING = 'FLOATING',
}

export enum RecordIdentifier {
  NEGOTIATION = 'NEGOTIATION',
  DEAL = 'DEAL',
}

export enum ReferenceDataManagementEvent {
  CREATE_NEW = 'CREATE_NEW',
  CREATE_EDIT = 'CREATE_EDIT',
  CREATE_CLONE = 'CREATE_CLONE',
  CREATE_AND_SUBMIT = 'CREATE_AND_SUBMIT',
  ENTITY_ADD = 'ENTITY_ADD',
  ENTITY_DELETE = 'ENTITY_DELETE',
  ENTITY_UPDATE = 'ENTITY_UPDATE',
  ENTITY_REVERT = 'ENTITY_REVERT',
  SUBMIT = 'SUBMIT',
  CANCEL = 'CANCEL',
  PURGE = 'PURGE',
  DELETE_ALL_OF_TYPE = 'DELETE_ALL_OF_TYPE',
}

export enum ReferenceDataManagementStatus {
  IN_PROGRESS = 'IN_PROGRESS',
  CANCELLED = 'CANCELLED',
  SUBMITTED = 'SUBMITTED',
}

export enum RefinitivAssetType {
  GOVT = 'GOVT',
  AGNC = 'AGNC',
  OTHR = 'OTHR',
  CORP = 'CORP',
  ABSY = 'ABSY',
  CMBS = 'CMBS',
  CMO = 'CMO',
  MORT = 'MORT',
}

export enum RelationshipAction {
  ADD = 'ADD',
  DELETE = 'DELETE',
}

export enum RepoClassification {
  FIXED = 'FIXED',
  OPEN = 'OPEN',
  OPEN_EVERGREEN = 'OPEN_EVERGREEN',
}

export enum RepoType {
  BSB = 'BSB',
  CLASSIC = 'CLASSIC',
  STOCK_BORROWING_LENDING = 'STOCK_BORROWING_LENDING',
}

export enum ReportType {
  RECONCILIATION = 'RECONCILIATION',
  BILLING_EU = 'BILLING_EU',
  BILLING_US = 'BILLING_US',
  BILLING_EU_ADHOC = 'BILLING_EU_ADHOC',
  BILLING_US_ADHOC = 'BILLING_US_ADHOC',
}

export enum RfqType {
  INDICATIVE = 'INDICATIVE',
  FIRM = 'FIRM',
}

export enum RfqTypeValues {
  Firm = 'Firm',
  Indicative = 'Indicative',
}

export enum SaveAllocationsSource {
  USER_ENTERED = 'USER_ENTERED',
  SPREADSHEET_UPLOAD = 'SPREADSHEET_UPLOAD',
}

export enum SectorStatus {
  ENABLED = 'ENABLED',
  DISABLED = 'DISABLED',
  DELETED = 'DELETED',
}

export enum Seniority {
  SNDB = 'SNDB',
  MZZD = 'MZZD',
  SBOD = 'SBOD',
  JUND = 'JUND',
}

export enum ServerMessageType {
  ENTITY_SNAPSHOT_BEGIN = 'ENTITY_SNAPSHOT_BEGIN',
  ENTITY_SNAPSHOT_END = 'ENTITY_SNAPSHOT_END',
  ENTITY_UPDATE = 'ENTITY_UPDATE',
  ENTITY_DELETE = 'ENTITY_DELETE',
  EVENT_RESPONSE = 'EVENT_RESPONSE',
  HEARTBEAT = 'HEARTBEAT',
  ENTITY_STREAM_SNAPSHOTS_BEGIN = 'ENTITY_STREAM_SNAPSHOTS_BEGIN',
  ENTITY_STREAM_SNAPSHOTS_END = 'ENTITY_STREAM_SNAPSHOTS_END',
  ENTITY_TRANSACTION_BEGIN = 'ENTITY_TRANSACTION_BEGIN',
  ENTITY_TRANSACTION_END = 'ENTITY_TRANSACTION_END',
  HISTORICAL_QUERY_RESPONSE = 'HISTORICAL_QUERY_RESPONSE',
  ENTITY_REMOVE_SUBSCRIPTION_IDENTIFIERS = 'ENTITY_REMOVE_SUBSCRIPTION_IDENTIFIERS',
  ENTITY_SUBSCRIPTION_META = 'ENTITY_SUBSCRIPTION_META',
}

export enum ServicingType {
  CLOSE_OPEN = 'CLOSE_OPEN',
  SUBSTITUTION = 'SUBSTITUTION',
  TERMINATION = 'TERMINATION',
  RETURN_RECALL = 'RETURN_RECALL',
  SERVICING_AMEND = 'SERVICING_AMEND',
  SERVICING_ADMIN_CANCEL = 'SERVICING_ADMIN_CANCEL',
  SERVICING_TRADER_AMEND = 'SERVICING_TRADER_AMEND',
  SERVICING_TRADER_CANCEL = 'SERVICING_TRADER_CANCEL',
  RERATE = 'RERATE',
  ROLL_IN_PLACE = 'ROLL_IN_PLACE',
  ROLL = 'ROLL',
}

export enum ServicingTypeValues {
  SERVICING_TRADER_CANCEL = 'SERVICING_TRADER_CANCEL',
  SERVICING_TRADER_AMEND = 'SERVICING_TRADER_AMEND',
  RETURN_RECALL = 'RETURN_RECALL',
}

export enum SessionAttribute {
  SOFT_LOGOUT = 'SOFT_LOGOUT',
  HARD_LOGOUT = 'HARD_LOGOUT',
}

export enum SessionCloseCode {
  SESSION_EXPIRED = 4001,
  USER_SUSPENDED = 4002,
  CLUSTER_DISCONNECTED = 4003,
  USER_LOGOUT = 4004,
  DUPLICATE_SESSION = 4005,
  RATE_LIMIT_BREACHED = 4006,
}

export enum SettlementVenue {
  Bilateral = 'Bilateral',
  Direct = 'Direct',
  Sponsored = 'Sponsored',
  Triparty = 'Triparty',
}

export enum Severity {
  INFO = 'INFO',
  WARN = 'WARN',
  ERROR = 'ERROR',
}

export enum SignalType {
  TRIGGER_ORDER_RECORD_KEEPING_DUMP = 'TRIGGER_ORDER_RECORD_KEEPING_DUMP',
  TRIGGER_TRANSACTION_REPORTING_DUMP = 'TRIGGER_TRANSACTION_REPORTING_DUMP',
  TRIGGER_NEGOTIATION_EXPORT_DUMP = 'TRIGGER_NEGOTIATION_EXPORT_DUMP',
  TRIGGER_DEAL_EXPORT_DUMP = 'TRIGGER_DEAL_EXPORT_DUMP',
  SET_CURRENT_TRADE_DATE = 'SET_CURRENT_TRADE_DATE',
  EXPIRE_RFQS = 'EXPIRE_RFQS',
  PURGE_TRADE_DATA = 'PURGE_TRADE_DATA',
  PURGE_COLLATERAL_DETAIL_DATA = 'PURGE_COLLATERAL_DETAIL_DATA',
  PURGE_UNKNOWN_COLLATERALS_AND_ORDERS = 'PURGE_UNKNOWN_COLLATERALS_AND_ORDERS',
  PURGE_NEGOTIATION_DATA = 'PURGE_NEGOTIATION_DATA',
  DELETE_CLUSTER_SCHEDULED_EVENTS = 'DELETE_CLUSTER_SCHEDULED_EVENTS',
  DELETE_RATE_DATA = 'DELETE_RATE_DATA',
  RUN_DEAL_SCHEDULED_EVENTS = 'RUN_DEAL_SCHEDULED_EVENTS',
  ALLOW_BACK_DATED_TRADES = 'ALLOW_BACK_DATED_TRADES',
  DISABLE_BACK_DATED_TRADES = 'DISABLE_BACK_DATED_TRADES',
  REMOVE_TODAY_HOLIDAYS = 'REMOVE_TODAY_HOLIDAYS',
  COMPLETE_ALL_DEALS = 'COMPLETE_ALL_DEALS',
  SCHEDULED_DEAL_EVENTS_ARE_RUN = 'SCHEDULED_DEAL_EVENTS_ARE_RUN',
  RESEND_COLLATERALS = 'RESEND_COLLATERALS',
  RESEND_COLLATERAL_PRICES = 'RESEND_COLLATERAL_PRICES',
  SNAPSHOT_PUBLIC_EGRESS = 'SNAPSHOT_PUBLIC_EGRESS',
  FIX_TRADE_QUERIES_ENABLED = 'FIX_TRADE_QUERIES_ENABLED',
  FIX_TRADE_QUERIES_DISABLED = 'FIX_TRADE_QUERIES_DISABLED',
  START_CASH_OVERRIDE_ENABLED = 'START_CASH_OVERRIDE_ENABLED',
  START_CASH_OVERRIDE_DISABLED = 'START_CASH_OVERRIDE_DISABLED',
  RELOAD_AND_CALCULATE_END_CONSIDERATION = 'RELOAD_AND_CALCULATE_END_CONSIDERATION',
  RELOAD_AND_COMPLETE_DEALS = 'RELOAD_AND_COMPLETE_DEALS',
  DISABLE_BATCHING = 'DISABLE_BATCHING',
  ENABLE_BATCHING = 'ENABLE_BATCHING',
  UPDATE_USERS_GROUP = 'UPDATE_USERS_GROUP',
  ADD_USERS_GROUP = 'ADD_USERS_GROUP',
  REMOVE_USERS_GROUP = 'REMOVE_USERS_GROUP',
  FAILED_EXPORTS_RETRY = 'FAILED_EXPORTS_RETRY',
  FAILED_EXPORTS_DELETE = 'FAILED_EXPORTS_DELETE',
  REFRESH_DEALS = 'REFRESH_DEALS',
  RESET_FIX_PERSISTENT_SOTW = 'RESET_FIX_PERSISTENT_SOTW',
  TRIGGER_REFINITIV_PRICE_REQUEST = 'TRIGGER_REFINITIV_PRICE_REQUEST',
  TRIGGER_REFINITIV_COLLATERAL_REQUEST = 'TRIGGER_REFINITIV_COLLATERAL_REQUEST',
  TRIGGER_REFINITIV_COLLATERAL_RIC_LOAD = 'TRIGGER_REFINITIV_COLLATERAL_RIC_LOAD',
  TRIGGER_REFINITIV_ISSUER_ORG_REFRESH = 'TRIGGER_REFINITIV_ISSUER_ORG_REFRESH',
  TRIGGER_CLEAR_FIRST_TRADE_DATE = 'TRIGGER_CLEAR_FIRST_TRADE_DATE',
  TRIGGER_REFINITIV_COLLATERAL_CORPS_REFRESH = 'TRIGGER_REFINITIV_COLLATERAL_CORPS_REFRESH',
  RESET_FIX_PERSISTENT_SOTW_COMPLETE = 'RESET_FIX_PERSISTENT_SOTW_COMPLETE',
  FIRDS_REPORT_REQUEST = 'FIRDS_REPORT_REQUEST',
  FIRDS_REPORT_SCHEDULED_REQUEST = 'FIRDS_REPORT_SCHEDULED_REQUEST',
  OKTA_USERS_REPORT_REQUEST = 'OKTA_USERS_REPORT_REQUEST',
  OKTA_USERS_REPORT_SCHEDULED_REQUEST = 'OKTA_USERS_REPORT_SCHEDULED_REQUEST',
  BONDS_NOTE_COLLATERAL_UPDATE_REQUEST = 'BONDS_NOTE_COLLATERAL_UPDATE_REQUEST',
  BONDS_NOTE_COLLATERAL_UPDATE_SCHEDULED_REQUEST = 'BONDS_NOTE_COLLATERAL_UPDATE_SCHEDULED_REQUEST',
  COLLATERAL_REFINITIV_REPORT_REQUEST = 'COLLATERAL_REFINITIV_REPORT_REQUEST',
  COLLATERAL_REFINITIV_REPORT_REQUEST_ADHOC = 'COLLATERAL_REFINITIV_REPORT_REQUEST_ADHOC',
  PURGE_TRADABLE_ENTITY = 'PURGE_TRADABLE_ENTITY',
  LOG_MESSAGE = 'LOG_MESSAGE',
  SIMULATE_REFINITIV_COLLATERAL_RESPONSE = 'SIMULATE_REFINITIV_COLLATERAL_RESPONSE',
  SIMULATE_IMPORT_ENABLING_COLLATERAL = 'SIMULATE_IMPORT_ENABLING_COLLATERAL',
  GENERATE_SCHEDULE_REPORT = 'GENERATE_SCHEDULE_REPORT',
  MIGRATE_COLLATERAL_DETAILS = 'MIGRATE_COLLATERAL_DETAILS',
  UPDATE_MORTGAGES_FACTORS_REQUEST = 'UPDATE_MORTGAGES_FACTORS_REQUEST',
  UPDATE_COLLATERAL_FIELDS_REQUEST = 'UPDATE_COLLATERAL_FIELDS_REQUEST',
  RESET_TEST_DATA = 'RESET_TEST_DATA',
  EOD_PURGE = 'EOD_PURGE',
  INCREMENT_IDS = 'INCREMENT_IDS',
}

export enum SponsorType {
  NatWest = 'NatWest',
  SocGen = 'SocGen',
  HSBC = 'HSBC',
}

export enum SpreadsheetCsd {
  DTCC = 'DTCC',
  EUROCLEAR = 'EUROCLEAR',
  BONY = 'BONY',
  CLEARSTREAM = 'CLEARSTREAM',
  AUSTRACLEAR = 'AUSTRACLEAR',
  JASDEC = 'JASDEC',
  FED = 'FED',
}

export enum SpreadsheetMappingType {
  STAGING = 'STAGING',
  INCOMING = 'INCOMING',
  CURVE = 'CURVE',
  MID_TRADE = 'MID_TRADE',
  COLLATERAL = 'COLLATERAL',
  COLLATERAL_BASKET_ALLOCATION = 'COLLATERAL_BASKET_ALLOCATION',
  TRADING_ENTITY = 'TRADING_ENTITY',
  TRADING_ENTITY_PRODUCT = 'TRADING_ENTITY_PRODUCT',
  TRADING_RELATIONSHIP = 'TRADING_RELATIONSHIP',
  FUND_ASSIGNMENT = 'FUND_ASSIGNMENT',
  AXE = 'AXE',
}

export enum SpreadsheetSettlementVenue {
  Bilateral = 'Bilateral',
  Direct = 'Direct',
  Sponsored = 'Sponsored',
}

export enum StartTimeZone {
  AMSTERDAM = 'AMSTERDAM',
  LONDON = 'LONDON',
  NEW_YORK = 'NEW_YORK',
  TOKYO = 'TOKYO',
}

export enum Status {
  REQUESTED = 'REQUESTED',
  APPROVED = 'APPROVED',
}

export enum StockBorrowingLendingType {
  NONE = 'NONE',
  BASKET = 'BASKET',
  SINGLE = 'SINGLE',
}

export enum SubscribeType {
  FILTERED = 'FILTERED',
  PAGE = 'PAGE',
  LIVE_ONLY = 'LIVE_ONLY',
}

export enum TradeClassification {
  STANDARD = 'STANDARD',
  EXTENDABLE = 'EXTENDABLE',
  EVER_GREEN = 'EVER_GREEN',
}

export enum TradeConditionFlag {
  NONE = 'NONE',
  ROLL = 'ROLL',
  UNWIND = 'UNWIND',
  SUB = 'SUB',
}

export enum TradingEntityAliasType {
  FICC = 'FICC',
  ALL = 'ALL',
}

export enum TradingEntityClearingType {
  DIRECT = 'DIRECT',
  SPONSORED = 'SPONSORED',
}

export enum TradingEntityProductPermission {
  VIEW = 'VIEW',
  TRADE = 'TRADE',
}

export enum TradingEntityType {
  STANDARD = 'STANDARD',
  BLOCK = 'BLOCK',
}

export enum TradingLimitType {
  NONE = 'NONE',
  GROSS = 'GROSS',
  NET = 'NET',
}

export enum TradingStatus {
  ENABLED = 'ENABLED',
  DISABLED = 'DISABLED',
}

export enum TradingType {
  CASH = 'CASH',
  NOTIONAL = 'NOTIONAL',
  EITHER = 'EITHER',
}

export enum Trigger {
  NEW_TRADE = 'NEW_TRADE',
  ALLOCATE_ROLL_BACK = 'ALLOCATE_ROLL_BACK',
  ALLOCATION = 'ALLOCATION',
  CANCEL_TRADE = 'CANCEL_TRADE',
  AMEND_TRADE_ROLL_BACK = 'AMEND_TRADE_ROLL_BACK',
  AMEND_TRADE = 'AMEND_TRADE',
}

export enum Type {
  MESSAGE_SEND_PROCESSING_FAILED = 'MESSAGE_SEND_PROCESSING_FAILED',
  RESPONSE_PROCESSING_FAILED = 'RESPONSE_PROCESSING_FAILED',
  CANNOT_SEND_TO_MQ = 'CANNOT_SEND_TO_MQ',
  TIMEOUT = 'TIMEOUT',
  UNKNOWN = 'UNKNOWN',
}

export enum UserStatus {
  ACTIVE = 'ACTIVE',
  SUSPENDED = 'SUSPENDED',
  DELETED = 'DELETED',
}

export enum ValidationType {
  MISSING_MANDATORY = 'MISSING_MANDATORY',
  INVALID_VALUE = 'INVALID_VALUE',
}
